import { useEffect, useRef, useState } from 'react'
import NavBar from '../components/NavBar'
import CatanBoard, { BuildMode } from '../components/CatanBoard'
import ChatPanel from '../components/ChatPanel'
import SettingsModal from '../components/SettingsModal'
// SEQ=10124 P8-CK-H — 9 UX 모달/오버레이 컴포넌트
import BishopVictimModal           from '../components/progressCardUx/BishopVictimModal'
import DeserterPlacementOverlay    from '../components/progressCardUx/DeserterPlacementOverlay'
import DiplomatRoadOverlay         from '../components/progressCardUx/DiplomatRoadOverlay'
import IntrigueMoveOverlay         from '../components/progressCardUx/IntrigueMoveOverlay'
import SpyHandModal                from '../components/progressCardUx/SpyHandModal'
import InventorSwapModal           from '../components/progressCardUx/InventorSwapModal'
import PioneeringRoadOverlay       from '../components/progressCardUx/PioneeringRoadOverlay'
import CommercialHarborTradeModal  from '../components/progressCardUx/CommercialHarborTradeModal'
import MerchantPlacementOverlay    from '../components/progressCardUx/MerchantPlacementOverlay'
import { sound } from '../util/sound'
import { AuthState } from '../store/auth'
import KnightPanel from '../components/KnightPanel'
import ProgressCardPanel from '../components/ProgressCardPanel'
import OpponentCollection from '../components/OpponentCollection'
import {
  catanStore, CatanGame, CatanPlayer, CatanHex, CatanPort,
  CatanVertex, CatanEdge,
  // SEQ=10113 P8-CK-B
  CatanKnight,
  // SEQ=10115 P8-CK-D
  CatanProgressCard, ProgressCardDrawnPayload, ProgressCardUsedPayload,
  PermanentCardAcquiredPayload,
  BuildingPlacedPayload, RoadPlacedPayload, TurnAdvancedPayload,
  DiceRolledPayload, ResourceGrantedPayload, ResourceCountUpdatedPayload,
  RobberPhaseStartedPayload, RobberMovedPayload, CardsDiscardedPayload,
  CardsStolenPayload, MyResourcesSyncPayload, PlayerResourcesSyncPayload,
  // Phase 6 발전 카드
  MyDevCard, DevCardType,
  MyDevCardsSyncPayload, DevCardBoughtPayload, DevCardCountChangedPayload,
  DevCardDeckChangedPayload, DevCardPlayedPayload, LargestArmyChangedPayload,
  // 단계 5 (Phase 7)
  LongestRoadChangedPayload, AllScoresSyncPayload, MyVictoryPointsSyncPayload,
  GameEndedPayload, AllScoresPlayer,
  // Phase 5 거래
  TradeProposedPayload, TradeRespondedPayload, TradeCounterProposedPayload,
  TradeCompletedPayload, TradeCancelledPayload, ProposerResourcesSyncPayload,
  OpenTradeView,
  subscribeCatan,
} from '../store/catan'
import { roomStore, ChatEntry, subscribeRoom } from '../store/room'
import { apiCatanGameState } from '../server/ServiceCall'
import * as catanHub from '../hub/catanHub'
import * as roomHub from '../hub/roomHub'
import { getHexVertices, getEdgeVertices } from '../util/catanCoord'
import '../css/App.css'
import '../css/Play.css'

// ── 플레이어 색상 (서버 COLOR 문자열 → CSS hex) ─────────────
const PLAYER_COLOR: Record<string, string> = {
  RED:    '#D32F2F',
  BLUE:   '#1976D2',
  GREEN:  '#388E3C',
  ORANGE: '#F57C00',
  BROWN:  '#6D4C41',
  WHITE:  '#FAFAFA',
}

// ── 반응형 스테이지 크기 계산 ────────────────────────────────
// Stage 크기 산정 근거 (2026-05-06 v5 — 항구 잘림 fix + offset 적정값 채택):
//   ·외곽 vertex 최대 반경       ≈ hexSize × 2.6 (q,r ∈ {-2..2} pointy-top)
//   ·midpoint(외곽 vertex 페어 평균) ≈ hexSize × 2.5
//   ·port offset (midpoint 외향) = hexSize × 0.8  (v4 1.5 너무 멈 → v5 0.8 헥스 변 직외측)
//   ·port asset 절반            = hexSize × 0.55 (portAssetSize/2 = 1.1/2, v4 0.6 → v5 0.55)
//   ·port 중심                  ≈ hexSize × 3.3 (midpoint 2.5 + offset 0.8)
//   ·port 안쪽 끝               ≈ hexSize × 2.75 (헥스 변 직외측, 자원 헥스 가림 미미)
//   ·port 외측 끝               ≈ hexSize × 3.85
//   ·비율 텍스트 overlay        + hexSize × 0.34 (자산 위 0.32 + 텍스트 height)
//   ·port hasPrivilege 글로우    + hexSize × 0.4  (shadowBlur)
//   → 외곽 안전 반경 ≈ hexSize × 4.6 (가장 멀리 있는 항구 + 글로우)
//   여유 0.9 hexSize, 정사각 한 변 = hexSize × 11 (반경 5.5) — STAGE_RATIO 11 유지.
const STAGE_RATIO = 11

function calcStageSize(): { w: number; h: number; hexSize: number } {
  const isMobile = window.innerWidth < 768
  // 데스크톱 4컬럼: 사이드 패널 220+260+300 + gap/border = ~800 예약 (2026-05-06 v3 확장).
  // 모바일: 좌우 8px padding 합쳐 viewport 전체 사용.
  const maxW     = isMobile
    ? Math.min(window.innerWidth - 16, 420)
    : Math.min(window.innerWidth - 800, 880)
  const maxH     = isMobile
    ? Math.min(window.innerHeight * 0.55, 420)
    : Math.min(window.innerHeight - 140, 760)

  const fit      = Math.min(maxW, maxH)
  // hexSize = fit / 11. 최소 24(모바일)/36(데스크톱) — 11배 환산 후도 가독성 유지.
  const hexSize  = isMobile ? Math.max(fit / STAGE_RATIO, 24) : Math.max(fit / STAGE_RATIO, 36)
  const w        = hexSize * STAGE_RATIO
  const h        = hexSize * STAGE_RATIO
  return { w, h, hexSize }
}

// ── 모바일 감지 훅 ────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    function onResize() {
      clearTimeout(timer)
      timer = setTimeout(() => setIsMobile(window.innerWidth < 768), 100)
    }
    window.addEventListener('resize', onResize)
    return () => { clearTimeout(timer); window.removeEventListener('resize', onResize) }
  }, [])
  return isMobile
}

type PlayTab = 'board' | 'players' | 'resource' | 'chat'

interface Props {
  authState: AuthState
  roomCode:  string
  matchId?:  number
  onLeave:   () => void
  onLogout:  () => void
  // "새 게임" — RoomRestarted 수신 시 대기실(Room) 화면으로 전환 (SEQ=10076 흐름 변경)
  onRoomRestarted: (roomCode: string) => void
  // SEQ=10110 STEP=4 — 닉네임 변경 후 AuthState 갱신용 (Play 화면에서는 [👤] 비활성이므로 미사용이지만 시그니처 일치)
  onNicknameChanged: (newNickname: string) => void
}

// 자원 종류 한글 + 이모지(토스트 메시지) + png 경로(2026-05-01 vector 자원 5종)
// SEQ=10112 P8-CK-A — EXT_CK 상품 3종(PAPER/CLOTH/COIN) 추가, 총 8종
const RESOURCE_LABEL: Record<string, { emoji: string; label: string; img: string }> = {
  WOOL:  { emoji: '🐑', label: '양', img: '/assets/catan/resource/sheep.png' },
  WHEAT: { emoji: '🌾', label: '곡식', img: '/assets/catan/resource/wheat.png' },
  BRICK: { emoji: '🧱', label: '벽돌', img: '/assets/catan/resource/brick.png' },
  ORE:   { emoji: '⛰️', label: '광물', img: '/assets/catan/resource/ore.png' },
  WOOD:  { emoji: '🌲', label: '나무', img: '/assets/catan/resource/wood.png' },
  // EXT_CK 상품 (Cities & Knights): 산출 = 도시 + 원천 헥스 인접 시
  PAPER: { emoji: '📜', label: '종이', img: '/assets/catan/resource/paper.png' },
  CLOTH: { emoji: '🧶', label: '옷감', img: '/assets/catan/resource/cloth.png' },
  COIN:  { emoji: '🪙', label: '동전', img: '/assets/catan/resource/coin.png' },
}

const RESOURCE_TYPES = ['WOOL', 'WHEAT', 'BRICK', 'ORE', 'WOOD'] as const
// EXT_CK 상품 3종 (EXT_CK ON 룸에서만 렌더)
const COMMODITY_TYPES = ['PAPER', 'CLOTH', 'COIN'] as const

// 도시 개선 분야 매핑
const CK_FIELD_LABEL: Record<'POL' | 'SCI' | 'TRD', { label: string; commodity: 'PAPER' | 'CLOTH' | 'COIN'; commodityLabel: string }> = {
  POL: { label: '정치', commodity: 'COIN',  commodityLabel: '동전' },
  SCI: { label: '과학', commodity: 'PAPER', commodityLabel: '종이' },
  TRD: { label: '상업', commodity: 'CLOTH', commodityLabel: '옷감' },
}

// ── Phase 4-3 건설 비용 상수 (카탄.md §2-3) ────────────────────
const BUILD_COST: Record<'BUILD_ROAD' | 'BUILD_SETTLEMENT' | 'BUILD_CITY', Partial<Record<string, number>>> = {
  BUILD_ROAD:       { WOOD: 1, BRICK: 1 },
  BUILD_SETTLEMENT: { WOOD: 1, BRICK: 1, WOOL: 1, WHEAT: 1 },
  BUILD_CITY:       { ORE: 3, WHEAT: 2 },
}

// ── 비용 표시 자원 순서 (UI 일관성) ────────────────────
const COST_DISPLAY_ORDER = ['WOOD', 'BRICK', 'WOOL', 'WHEAT', 'ORE'] as const

function canAfford(my: Record<string, number>, cost: Partial<Record<string, number>>): boolean {
  for (const k of Object.keys(cost)) {
    if ((my[k] ?? 0) < (cost[k] ?? 0)) return false
  }
  return true
}

// ── 비용 아이콘 인라인 렌더 (자원 png + 수량) ─────────────
function CostIcons({ cost, my }: { cost: Partial<Record<string, number>>; my: Record<string, number> }) {
  return (
    <span className="build-cost">
      {COST_DISPLAY_ORDER
        .filter(rt => (cost[rt] ?? 0) > 0)
        .map(rt => {
          const need = cost[rt] ?? 0
          const have = my[rt] ?? 0
          const lack = have < need
          const lbl  = RESOURCE_LABEL[rt]
          return (
            <span
              key={rt}
              className={`build-cost-item${lack ? ' lack' : ''}`}
              title={`${lbl.label} ${need}개 필요 (보유 ${have})`}
            >
              <img src={lbl.img} alt={lbl.label} className="build-cost-icon" />
              <span className="build-cost-num">×{need}</span>
            </span>
          )
        })}
    </span>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function Play({ authState, roomCode, matchId, onLeave, onLogout, onRoomRestarted, onNicknameChanged }: Props) {
  const isMobile = useIsMobile()

  const [game,        setGame]        = useState<CatanGame | null>(catanStore.getGame())
  const [players,     setPlayers]     = useState<CatanPlayer[]>(catanStore.getPlayers())
  const [hexes,       setHexes]       = useState<CatanHex[]>(catanStore.getHexes())
  const [ports,       setPorts]       = useState<CatanPort[]>(catanStore.getPorts())
  const [vertices,    setVertices]    = useState<CatanVertex[]>(catanStore.getVertices())
  const [edges,       setEdges]       = useState<CatanEdge[]>(catanStore.getEdges())
  const [myResources, setMyResources] = useState<Record<string, number>>(catanStore.getMyResources())
  const [resCounts,   setResCounts]   = useState<Record<number, number>>(catanStore.getResourceCounts())
  // SEQ=10113 P8-CK-B — 기사 (Knight) state
  const [knights, setKnights] = useState<CatanKnight[]>(catanStore.getKnights())
  // SEQ=10114 P8-CK-C — 도시 성벽 + 야만인 트랙 + 최근 침공 결과 state
  const [cityWalls, setCityWalls] = useState<ReturnType<typeof catanStore.getCityWalls>>(catanStore.getCityWalls())
  const [barbarianPos, setBarbarianPos] = useState<number>(catanStore.getBarbarianPosition())
  const [lastInvasion, setLastInvasion] = useState<ReturnType<typeof catanStore.getLastInvasion>>(catanStore.getLastInvasion())
  // SEQ=10115 P8-CK-D — 본인 진보 카드 + 영구 카드 컬렉션
  const [myProgressCards, setMyProgressCards] = useState<CatanProgressCard[]>(catanStore.getMyProgressCards())
  const [permanentCards, setPermanentCards] = useState<Record<number, string[]>>(catanStore.getAllPermanentCards())

  const [loading,    setLoading]   = useState(false)
  const [errMsg,     setErrMsg]    = useState('')
  const [actionMsg,  setActionMsg] = useState('')  // 액션 안내 메시지
  const [errorBanner,setErrorBanner] = useState('') // ActionError 토스트
  const [restartPending, setRestartPending] = useState(false) // 새 게임 시작 진행 중 (중복 클릭 차단) — SEQ=10072 STEP A
  const [activeTab, setActiveTab]  = useState<PlayTab>('board')
  const [lastDice,   setLastDice]  = useState<DiceRolledPayload | null>(catanStore.getLastDice())
  const [diceRolling, setDiceRolling] = useState(false)
  const [showSettings, setShowSettings] = useState(false) // SEQ=10110 STEP=4

  const [stageSize, setStageSize] = useState(() => calcStageSize())

  // ── 채팅 상태 (룸 스토어 미러) ─────────────────────────
  const [chatLog,    setChatLog]    = useState<ChatEntry[]>(roomStore.getChatLog())
  const [unreadChat, setUnreadChat] = useState<number>(0)
  const prevChatLen = useRef<number>(roomStore.getChatLog().length)

  // ── Phase 5 거래 상태 ─────────────────────────────────
  const [openTrades, setOpenTrades] = useState<OpenTradeView[]>(catanStore.getOpenTrades())
  // 제안자 모달 (본인 거래 제안 시)
  const [proposeOpen,        setProposeOpen]        = useState(false)
  const [proposeGiveCounts,  setProposeGiveCounts]  = useState<Record<string, number>>({})
  const [proposeGetCounts,   setProposeGetCounts]   = useState<Record<string, number>>({})
  const [proposeTarget,      setProposeTarget]      = useState<number | null>(null) // null=전체
  // 응답자 모달 (본인이 응답 가능한 trade가 들어왔을 때 자동 표시)
  const [respondTradeId,     setRespondTradeId]     = useState<number | null>(null)
  const respondTradeIdRef = useRef<number | null>(null)
  useEffect(() => { respondTradeIdRef.current = respondTradeId }, [respondTradeId])
  const [counterMode,        setCounterMode]        = useState(false)  // 응답자 모달에서 카운터 모드 전환
  const [counterGiveCounts,  setCounterGiveCounts]  = useState<Record<string, number>>({})
  const [counterGetCounts,   setCounterGetCounts]   = useState<Record<string, number>>({})
  // 은행/항구 모달
  const [bankOpen,           setBankOpen]           = useState(false)
  const [bankGiveType,       setBankGiveType]       = useState<string>('')
  const [bankGetType,        setBankGetType]        = useState<string>('')
  const [portModalPortId,    setPortModalPortId]    = useState<number | null>(null)
  const [portGiveType,       setPortGiveType]       = useState<string>('')
  const [portGetType,        setPortGetType]        = useState<string>('')

  // ── Track C-1 턴 안내 임팩트 상태 ────────────────────────
  // 본인/상대 턴 진입 시 슬라이드 배너 1.5초 + 좌석 펄스
  const [showTurnBanner,    setShowTurnBanner]    = useState(false)
  const [turnBannerText,    setTurnBannerText]    = useState('')
  const [turnBannerVariant, setTurnBannerVariant] = useState<'my-turn' | 'discard' | null>(null)
  const [seatPulseUserCode, setSeatPulseUserCode] = useState<number | null>(null)
  const turnBannerTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null)
  const seatPulseTimerRef   = useRef<ReturnType<typeof setTimeout> | null>(null)
  const prevTurnUserRef     = useRef<number | null>(null)
  const prevPhaseRef        = useRef<string | null>(null)

  // ── Track C-2 도둑 위치 펄스 상태 ────────────────────────
  const [robberPulse, setRobberPulse] = useState(false)
  const robberPulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Phase 4-2 도적/폐기 상태
  const [myDiscardRequired, setMyDiscardRequired] = useState<number>(0)
  const [discardOpen, setDiscardOpen] = useState(false)
  const [discardCounts, setDiscardCounts] = useState<Record<string, number>>({})
  const [victimChoice, setVictimChoice] = useState<{ hexId: number; victims: number[] } | null>(null)

  // Phase 4-3 본 턴 건설 모드 (MAIN_TURN_POSTROLL 전용, 비활성=null)
  const [buildMode, setBuildMode] = useState<BuildMode>(null)

  // ── SEQ=10124 P8-CK-H — 9 진보카드 UX ActionMode ─────────────
  //   진보카드 사용 후 보드 클릭/모달 입력이 필요한 9 카드의 단일 상태 머신.
  //   기존 buildMode / useCardModal / knightStep / roadBuildEdges 와 격리.
  //   진입 시 다른 액션 잠금 (buildMode + useCardModal 모두 null 강제).
  type ProgressActionMode =
    | null
    | { kind: 'POL_BISHOP_PICK_HEX';     cardId: number }
    | { kind: 'POL_BISHOP_CONFIRM';      cardId: number; hexId: number; victims: number[] }
    | { kind: 'POL_DESERTER';            cardId: number; step: 'pick_knight' | 'pick_vertex'; pickedKnightId?: number; candidateVertexIds: number[] }
    | { kind: 'POL_DIPLOMAT';            cardId: number; candidateEdgeIds: number[] }
    | { kind: 'POL_INTRIGUE';            cardId: number; step: 'pick_knight' | 'pick_to_vertex'; pickedKnightId?: number; candidateVertexIds: number[] }
    | { kind: 'POL_SPY';                 cardId: number; step: 'pick_player' | 'pick_card'; targetUserCode?: number; targetHand?: { cardId: number; cardType: string }[] }
    | { kind: 'SCI_INVENTOR';            cardId: number; step: 'pick_a' | 'pick_b' | 'confirm'; hexA?: number; hexB?: number }
    | { kind: 'SCI_PIONEERING';          cardId: number; candidateEdgeIds: number[] }
    | { kind: 'TRD_COMMERCIAL_HARBOR';   cardId: number; myCommodity: '' | 'WHEAT' | 'WOOL' | 'ORE' | 'BRICK' | 'WOOD'; theirResource: '' | 'WHEAT' | 'WOOL' | 'ORE' | 'BRICK' | 'WOOD' }
    | { kind: 'TRD_MERCHANT';            cardId: number; candidateHexIds: number[] }
  const [progressMode, setProgressMode] = useState<ProgressActionMode>(null)
  const [progressBusy, setProgressBusy] = useState<boolean>(false)

  // Phase 6 발전 카드 상태
  const [myDevCards, setMyDevCards] = useState<MyDevCard[]>(catanStore.getMyDevCards())
  const [devDeckRemaining, setDevDeckRemaining] = useState<number>(catanStore.getDevDeckRemaining())
  const [largestArmyUserCode, setLargestArmyUserCode] = useState<number | null>(catanStore.getLargestArmyUserCode())
  // 단계 5 (Phase 7) 상태
  const [longestRoadUserCode, setLongestRoadUserCode] = useState<number | null>(catanStore.getLongestRoadUserCode())
  const [allScores, setAllScores] = useState<AllScoresPlayer[]>(catanStore.getAllScores())
  const [myVictoryPoints, setMyVictoryPoints] = useState<MyVictoryPointsSyncPayload | null>(catanStore.getMyVictoryPoints())
  const [gameEnded, setGameEnded] = useState<GameEndedPayload | null>(catanStore.getGameEnded())
  // 사용 모달 상태 (5종 별도)
  const [useCardModal, setUseCardModal] = useState<{
    cardId: number
    cardType: DevCardType
  } | null>(null)
  // 발전카드 hover 확대 (Track O 2026-05-07) — 화면 중앙 overlay
  const [hoveredDevCard, setHoveredDevCard] = useState<{
    cardId: number
    cardType: DevCardType
  } | null>(null)
  // KNIGHT 사용 시 도적 hex 선택 + 약탈 victim 선택
  const [knightStep, setKnightStep] = useState<'pick_hex' | 'pick_victim'>('pick_hex')
  const [knightHexId, setKnightHexId] = useState<number | null>(null)
  // ROAD_BUILDING 사용 시 두 edge 선택 진행 상태
  const [roadBuildEdges, setRoadBuildEdges] = useState<number[]>([])
  // MONOPOLY 사용 시 자원 종류 선택
  const [monopolyResource, setMonopolyResource] = useState<string>('')
  // YEAR_OF_PLENTY 사용 시 자원 2개 선택
  const [yopResources, setYopResources] = useState<string[]>([])

  // 방금 놓은 마을 vertex_id (PLACE_ROAD 페이즈 인접 강조용)
  const [lastSettlementVertexId, setLastSettlementVertexId] = useState<number | null>(null)

  // ActionError 타이머 ref
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Stage 크기 동적 재계산
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    function onResize() {
      clearTimeout(timer)
      timer = setTimeout(() => setStageSize(calcStageSize()), 150)
    }
    window.addEventListener('resize', onResize)
    return () => { clearTimeout(timer); window.removeEventListener('resize', onResize) }
  }, [])

  // ESC 키로 빌드 모드 + 진보카드 ActionMode 취소
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (progressMode != null) {
          setProgressMode(null)
          setActionMsg('')
          return
        }
        if (buildMode != null) setBuildMode(null)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [buildMode, progressMode])

  // Track B (D-3, 2026-05-06) — KNIGHT 인접 victim 1명 자동 약탈
  // pick_victim 진입 직후 victim 후보 1명이면 자동 confirmUseKnight 호출 (모달 미표시)
  useEffect(() => {
    if (useCardModal?.cardType !== 'KNIGHT' || knightStep !== 'pick_victim' || knightHexId == null) return
    const hexVids = new Set<number>(getHexVertices(knightHexId))
    const cands = vertices
      .filter(v => hexVids.has(v.vertexId))
      .filter(v => v.ownerUserCode != null && v.ownerUserCode !== authState.userCode)
      .map(v => v.ownerUserCode!) as number[]
    const victims = Array.from(new Set(cands)).filter(uc => (resCounts[uc] ?? 0) > 0)
    if (victims.length === 0) {
      // 인접 0명 — 도적만 이동 (victimUc=null) 자동 호출
      const mid = game?.matchId ?? matchId ?? 0
      if (mid && useCardModal) {
        catanHub.useKnight(mid, useCardModal.cardId, knightHexId, null).catch(() => {})
        setUseCardModal(null)
        setKnightHexId(null)
        setKnightStep('pick_hex')
      }
    } else if (victims.length === 1) {
      // 인접 1명 — 자동 약탈
      const mid = game?.matchId ?? matchId ?? 0
      if (mid && useCardModal) {
        catanHub.useKnight(mid, useCardModal.cardId, knightHexId, victims[0]).catch(() => {})
        setUseCardModal(null)
        setKnightHexId(null)
        setKnightStep('pick_hex')
      }
    }
    // 2+ → 모달 그대로 (사용자 선택)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useCardModal, knightStep, knightHexId])

  // ── store 구독 ─────────────────────────────────────────
  useEffect(() => {
    const unsub = subscribeCatan(() => {
      setGame(catanStore.getGame())
      setPlayers(catanStore.getPlayers())
      setHexes(catanStore.getHexes())
      setPorts(catanStore.getPorts())
      setVertices(catanStore.getVertices())
      setEdges(catanStore.getEdges())
      setMyResources(catanStore.getMyResources())
      setResCounts(catanStore.getResourceCounts())
      // SEQ=10113 P8-CK-B — 기사 (Knight)
      setKnights(catanStore.getKnights())
      // SEQ=10114 P8-CK-C — 도시 성벽 + 야만인 트랙
      setCityWalls(catanStore.getCityWalls())
      setBarbarianPos(catanStore.getBarbarianPosition())
      setLastInvasion(catanStore.getLastInvasion())
      // SEQ=10115 P8-CK-D — 진보 카드 + 영구 카드 컬렉션
      setMyProgressCards(catanStore.getMyProgressCards())
      setPermanentCards(catanStore.getAllPermanentCards())
      setLastDice(catanStore.getLastDice())
      setMyDevCards(catanStore.getMyDevCards())
      setDevDeckRemaining(catanStore.getDevDeckRemaining())
      setLargestArmyUserCode(catanStore.getLargestArmyUserCode())
      // 단계 5
      setLongestRoadUserCode(catanStore.getLongestRoadUserCode())
      setAllScores(catanStore.getAllScores())
      setMyVictoryPoints(catanStore.getMyVictoryPoints())
      setGameEnded(catanStore.getGameEnded())
      // Phase 5 거래
      setOpenTrades(catanStore.getOpenTrades())
      // Phase 4-2 폐기 대기 동기화 (새로고침 복구 — store notify 시 React state 갱신)
      setMyDiscardRequired(catanStore.getMyDiscardRequired())
    })
    return () => { unsub() }
  }, [])

  // Phase 4-2 새로고침 복구 — myDiscardRequired 변동 감지 시 discardModal 자동 표시 (D-6 자동)
  // onRobberPhaseStarted 핸들러는 그대로 유지(실시간 routing), 이 useEffect는 새로고침 복구 전용
  useEffect(() => {
    if (myDiscardRequired > 0 && game?.currentPhase === 'MAIN_TURN_DISCARD' && !discardOpen) {
      setDiscardOpen(true)
      setDiscardCounts({ WOOL: 0, WHEAT: 0, BRICK: 0, ORE: 0, WOOD: 0 })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myDiscardRequired, game?.currentPhase])

  // ── 채팅(roomStore) 구독 + 미열람 배지 카운트 ─────────────
  useEffect(() => {
    const unsub = subscribeRoom(() => {
      setChatLog(roomStore.getChatLog())
    })
    return () => { unsub() }
  }, [])

  // 채팅 탭 활성 시 unread 0, 그 외 탭에서 새 메시지가 오면 미열람 ++
  useEffect(() => {
    if (isMobile && activeTab === 'chat') {
      setUnreadChat(0)
      prevChatLen.current = chatLog.length
      return
    }
    // 데스크톱은 채팅이 항상 보이므로 unread 0 유지
    if (!isMobile) {
      setUnreadChat(0)
      prevChatLen.current = chatLog.length
      return
    }
    const newCount = chatLog.length - prevChatLen.current
    if (newCount > 0) {
      setUnreadChat(prev => prev + newCount)
      prevChatLen.current = chatLog.length
    }
  }, [chatLog, activeTab, isMobile])

  // ── 진입 시 보드 데이터 fetch + CatanHub 연결 ─────────────
  useEffect(() => {
    const token = authState.token

    // 이미 store에 데이터 있으면 fetch 스킵
    const shouldFetch = catanStore.getHexes().length === 0

    // SEQ=10081 fix — PLACE_ROAD 가이드 도로 복원을 catanStore 기준으로 단일화.
    //   기존 버그: lastSettlementVertexId 복원이 shouldFetch 분기 안에만 있어,
    //   App.tsx 진행중게임 복귀 모달([예])이 미리 catanStore.applyGameState 로
    //   store(hexes 포함)를 채운 경우 shouldFetch=false → 복원 블록 미실행 →
    //   새로고침 후 PLACE_ROAD 가이드 도로 사라짐.
    //   catanStore.applyGameState 는 양쪽 경로 모두 players[].lastSettlementVertexId 를
    //   파싱하므로(catan.ts), store 에서 읽으면 fetch 여부와 무관하게 복원된다.
    const restoreLastSettlementFromStore = () => {
      const me = catanStore.getPlayers().find(p => p.userCode === authState.userCode)
      const lsv = me?.lastSettlementVertexId
      if (lsv != null && lsv > 0) {
        setLastSettlementVertexId(lsv)
      }
    }

    if (shouldFetch) {
      setLoading(true)
      apiCatanGameState(token, roomCode, matchId ?? 0)
        .then(data => {
          if (data) {
            catanStore.applyGameState(data as Record<string, unknown>)
            // PLACE_ROAD 페이즈 재진입 시 lastSettlementVertexId 복원 (store 기준)
            restoreLastSettlementFromStore()
          } else {
            setErrMsg('보드 데이터를 불러올 수 없습니다.')
          }
        })
        .catch(() => setErrMsg('네트워크 오류로 보드 데이터 로드 실패'))
        .finally(() => setLoading(false))
    } else {
      // store 가 이미 채워진 경우(App.tsx 복귀 모달 경유) — fetch 없이 store 에서 복원
      restoreLastSettlementFromStore()
    }

    // CatanHub 연결 시작 + 연결 후 발전카드/점수 동기화 보강
    // 2026-05-06 v7: 새로고침 시 발전카드 복원 race fix —
    //   기존: useEffect 후미 if(matchId) getMyDevCards() 호출(line 845)이 ensureConnected race로 swallow 가능.
    //   추가: startConnection resolve 후 명시적으로 getMyDevCards + getAllScores 재호출(idempotent).
    catanHub.startConnection(token)
      .then(() => {
        if (matchId) {
          catanHub.getMyDevCards(matchId).catch(() => {})
          catanHub.getAllScores(matchId).catch(() => {})
        }
      })
      .catch(() => {
        // 연결 실패는 치명적 아님 (REST로 이미 보드 있음)
      })

    // RoomHub 연결 시작 (채팅 + 입퇴장 시스템 메시지용)
    roomHub.startConnection(token).catch(() => {
      // 연결 실패해도 게임 진행은 가능 (채팅만 비활성)
    })

    // ── SignalR 이벤트 핸들러 등록 ─────────────────────────
    const onBuildingPlaced = (payload: BuildingPlacedPayload) => {
      const prevPhase = catanStore.getGame()?.currentPhase
      catanStore.applyBuildingPlaced(payload)
      // 본인이 마을을 놓은 경우 lastSettlementVertexId 갱신
      if (payload.userCode === authState.userCode && payload.buildingType === 'SETTLEMENT') {
        setLastSettlementVertexId(payload.vertexId)
      }
      // 시스템 메시지 — MAIN_TURN_POSTROLL 본 턴 건설만 (SETUP 단계는 시스템 메시지 노이즈 회피)
      if (prevPhase === 'MAIN_TURN_POSTROLL') {
        const nick = catanStore.getPlayers().find(p => p.userCode === payload.userCode)?.nickname ?? `#${payload.userCode}`
        const lbl = payload.buildingType === 'CITY' ? '도시(성)' : '마을'
        roomStore.appendSystemChat(`🏗️ ${nick} ${lbl} 건설`, 'BUILD_GAME')
      }
    }

    const onRoadPlaced = (payload: RoadPlacedPayload) => {
      const prevPhase = catanStore.getGame()?.currentPhase
      catanStore.applyRoadPlaced(payload)
      // 시스템 메시지 — MAIN_TURN_POSTROLL 본 턴 도로만
      if (prevPhase === 'MAIN_TURN_POSTROLL') {
        const nick = catanStore.getPlayers().find(p => p.userCode === payload.userCode)?.nickname ?? `#${payload.userCode}`
        roomStore.appendSystemChat(`🛣️ ${nick} 도로 건설`, 'BUILD_GAME')
      }
    }

    const onTurnAdvanced = (payload: TurnAdvancedPayload) => {
      const prev = catanStore.getGame()
      const prevTurnUser = prev?.currentTurnUserCode
      const prevPhase    = prev?.currentPhase
      catanStore.applyTurnAdvanced(payload)
      // 마을 배치 단계 전환 시 lastSettlementVertexId 초기화
      if (payload.currentPhase === 'PLACE_SETTLEMENT') {
        setLastSettlementVertexId(null)
      }
      // POSTROLL 이탈 시 빌드 모드 자동 해제
      if (payload.currentPhase !== 'MAIN_TURN_POSTROLL') {
        setBuildMode(null)
      }
      // 시스템 메시지 — 턴 전환 시점 (currentTurnUserCode 변동 + MAIN_TURN 진입 후)
      if (
        payload.status === 'MAIN_TURN'
        && payload.currentTurnUserCode != null
        && payload.currentTurnUserCode !== prevTurnUser
      ) {
        const nextNick = catanStore.getPlayers().find(p => p.userCode === payload.currentTurnUserCode)?.nickname
        if (nextNick) roomStore.appendSystemChat(`${nextNick} 님의 차례입니다.`, 'TURN_CHANGE')
      }

      // ── Track C-1 턴 안내 임팩트 트리거 (D-3 매 턴마다 / D-8 SETUP 첫 턴 포함) ──
      const myUc = authState.userCode
      const turnUc = payload.currentTurnUserCode ?? null
      // 페이즈 변동 확인 (DISCARD 진입 분기, D-9)
      const enteredDiscard = payload.currentPhase === 'MAIN_TURN_DISCARD' && prevPhase !== 'MAIN_TURN_DISCARD'

      // 턴 진입 효과 (currentTurnUserCode 변동 시)
      if (turnUc != null && turnUc !== prevTurnUserRef.current) {
        // 기존 timer 정리
        if (turnBannerTimerRef.current) clearTimeout(turnBannerTimerRef.current)
        if (seatPulseTimerRef.current)  clearTimeout(seatPulseTimerRef.current)

        if (turnUc === myUc) {
          // 본인 턴 진입 — D-7 배너 텍스트 + D-1 슬라이드 배너 + D-2 1.5초 자동 dismiss
          setTurnBannerText('당신의 차례입니다')
          setTurnBannerVariant('my-turn')
          setShowTurnBanner(true)
          turnBannerTimerRef.current = setTimeout(() => setShowTurnBanner(false), 1500)
          // 본인 좌석 펄스 (D-1) — 3회 ≈ 1.5초
          setSeatPulseUserCode(myUc)
          seatPulseTimerRef.current = setTimeout(() => setSeatPulseUserCode(null), 1500)
          // SEQ=10110 STEP=4 — 내 차례 진입 효과음
          sound.play('my_turn')
        } else {
          // 상대 턴 진입 (D-4 짧은 1초 펄스)
          setSeatPulseUserCode(turnUc)
          seatPulseTimerRef.current = setTimeout(() => setSeatPulseUserCode(null), 1000)
        }
        prevTurnUserRef.current = turnUc
      }

      // DISCARD 본인 대상 추가 배너 (D-9)
      if (enteredDiscard) {
        const myReq = catanStore.getMyDiscardRequired()
        if (myReq > 0) {
          if (turnBannerTimerRef.current) clearTimeout(turnBannerTimerRef.current)
          setTurnBannerText(`카드 ${myReq}장 폐기 필요`)
          setTurnBannerVariant('discard')
          setShowTurnBanner(true)
          turnBannerTimerRef.current = setTimeout(() => setShowTurnBanner(false), 1500)
        }
      }
      prevPhaseRef.current = payload.currentPhase ?? null
    }

    const onScoreUpdated = ({ userCode, score }: { userCode: number; score: number }) => {
      catanStore.applyScoreUpdated(userCode, score)
    }

    // SEQ=10112 P8-CK-A — EXT_CK 도시 개선 격상 이벤트 (CK_CITY_IMPROVE 시스템 채팅 PASS)
    const onCityImproved = (payload: {
      matchId: number; userCode: number; category: 'POL' | 'SCI' | 'TRD'; newLevel: number; metropolisTaken: boolean
    }) => {
      // store 측 플레이어 단계 업데이트 (memo: store에 별도 메서드 없으면 setter 직접 갱신)
      const updatedPlayers = catanStore.getPlayers().map(p => {
        if (p.userCode !== payload.userCode) return p
        if (payload.category === 'POL') return { ...p, cityImprovePol: payload.newLevel }
        if (payload.category === 'SCI') return { ...p, cityImproveSci: payload.newLevel }
        return { ...p, cityImproveTrd: payload.newLevel }
      })
      setPlayers(updatedPlayers)
      // 시스템 채팅 (CK_CITY_IMPROVE) — errorBanner 패턴 차용, 3초 후 사라짐
      const fieldLabel = CK_FIELD_LABEL[payload.category].label
      const playerNickname = updatedPlayers.find(p => p.userCode === payload.userCode)?.nickname ?? `유저${payload.userCode}`
      const metroSuffix = payload.metropolisTaken ? ` — 메트로폴리스 획득!` : ''
      setErrorBanner(`${playerNickname} 님이 ${fieldLabel} ${payload.newLevel}단계로 격상${metroSuffix}`)
      setTimeout(() => setErrorBanner(''), 3000)
    }

    // SEQ=10113 P8-CK-B — 기사 5종 + 1 hook (KnightsDeactivated) 이벤트 핸들러
    const onKnightBuilt = (payload: {
      matchId: number; userCode: number; knightId: number; vertexId: number; level: number; isActive: boolean
    }) => {
      catanStore.upsertKnight({
        knightId: payload.knightId, matchId: payload.matchId,
        userCode: payload.userCode, level:    payload.level,
        isActive: payload.isActive, vertexId: payload.vertexId,
      })
      const nick = catanStore.getPlayers().find(p => p.userCode === payload.userCode)?.nickname ?? `유저${payload.userCode}`
      setErrorBanner(`${nick} 님이 기사 건설 (vertex ${payload.vertexId})`)
      setTimeout(() => setErrorBanner(''), 2500)
    }
    const onKnightUpgraded = (payload: { matchId: number; userCode: number; knightId: number; newLevel: number }) => {
      catanStore.setKnightLevel(payload.knightId, payload.newLevel)
      const nick = catanStore.getPlayers().find(p => p.userCode === payload.userCode)?.nickname ?? `유저${payload.userCode}`
      setErrorBanner(`${nick} 님 기사 LV${payload.newLevel} 격상`)
      setTimeout(() => setErrorBanner(''), 2500)
    }
    const onKnightActivated = (payload: { matchId: number; userCode: number; knightId: number }) => {
      catanStore.setKnightActive(payload.knightId, true)
    }
    const onKnightMoved = (payload: { matchId: number; userCode: number; knightId: number; newVertexId: number }) => {
      catanStore.setKnightVertex(payload.knightId, payload.newVertexId, false)
    }
    const onKnightDisplaced = (payload: { matchId: number; knightId: number; userCode: number }) => {
      catanStore.removeKnight(payload.knightId)
      const nick = catanStore.getPlayers().find(p => p.userCode === payload.userCode)?.nickname ?? `유저${payload.userCode}`
      setErrorBanner(`${nick} 님 기사 추방됨!`)
      setTimeout(() => setErrorBanner(''), 2500)
    }
    const onRobberChased = (payload: {
      matchId: number; userCode: number; knightId: number; newRobberHexId: number
    }) => {
      catanStore.setKnightActive(payload.knightId, false)
      // 도적 위치는 보드 상태 갱신 (다른 RobberMoved 이벤트 정합 위해 vertices/hex 추후 GameLoad 호출 권장)
      // 시스템 알림
      const nick = catanStore.getPlayers().find(p => p.userCode === payload.userCode)?.nickname ?? `유저${payload.userCode}`
      setErrorBanner(`${nick} 님 기사로 도적 추방 → hex ${payload.newRobberHexId}`)
      setTimeout(() => setErrorBanner(''), 2500)
    }
    const onKnightsDeactivated = (payload: { matchId: number; userCode: number; affected: number }) => {
      catanStore.deactivateAllKnightsOf(payload.userCode)
      // 본인 차례 시작이면 알림 (Play.tsx 자기 턴 시작 시 모든 기사 자동 비활성화 알림)
      if (payload.userCode === authState.userCode) {
        setErrorBanner(`내 차례 시작 — 기사 ${payload.affected}기 자동 비활성`)
        setTimeout(() => setErrorBanner(''), 2500)
      }
    }

    // ── SEQ=10114 P8-CK-C — 야만인 트랙 + 침공 + 도시 성벽 이벤트 핸들러 ──
    const onBarbarianAdvanced = (payload: { newPosition: number; invasionTriggered: boolean }) => {
      catanStore.setBarbarianPosition(payload.newPosition)
      if (!payload.invasionTriggered) {
        const txt = `야만인 전진! 위치 ${payload.newPosition}/7`
        roomStore.appendSystemChat(txt, 'CK_BARBARIAN_MOVE')
      }
    }
    const onBarbarianInvasion = (payload: { resultJson: string }) => {
      try {
        const obj = JSON.parse(payload.resultJson) as {
          log_id:               number
          invasion_no:          number
          outcome:              'WIN' | 'LOSE'
          strength_sum:         number
          barbarians:           number
          defender_user_codes:  number[]
          loser_user_codes:     number[]
          loser_vertex_ids:     number[]
        }
        catanStore.setLastInvasion({
          logId:             obj.log_id,
          invasionNo:        obj.invasion_no,
          outcome:           obj.outcome,
          strengthSum:       obj.strength_sum,
          barbarians:        obj.barbarians,
          defenderUserCodes: obj.defender_user_codes ?? [],
          loserUserCodes:    obj.loser_user_codes    ?? [],
          loserVertexIds:    obj.loser_vertex_ids    ?? [],
        })
        // 침공 후 모든 Active 기사 비활성화 (서버 SP가 이미 처리, 클라 동기화)
        for (const k of catanStore.getKnights()) {
          if (k.isActive) catanStore.setKnightActive(k.knightId, false)
        }
        // BARBARIAN_POSITION 0 리셋
        catanStore.setBarbarianPosition(0)
        // 패배 시 도시→마을 + 성벽 제거 동기화
        if (obj.outcome === 'LOSE' && obj.loser_vertex_ids.length > 0) {
          catanStore.applyInvasionLoss(obj.loser_vertex_ids)
        }
        // SEQ=10115 P8-CK-D — WIN 시 DEFENDER_OF_CATAN +1 (defender_user_codes 전원)
        if (obj.outcome === 'WIN' && obj.defender_user_codes.length > 0) {
          for (const uc of obj.defender_user_codes) {
            catanStore.incrementDefenderOfCatan(uc, +1)
            catanStore.addPermanentCard(uc, 'POL_DEFENDER_OF_CATAN')
          }
        }
        // 사용자 컨펌 #7 핵심 5종 시스템 채팅
        const cat = obj.outcome === 'WIN' ? 'CK_BARBARIAN_INVADE_WIN' : 'CK_BARBARIAN_INVADE_LOSE'
        const head = obj.outcome === 'WIN'
          ? `[침공 승리] 방어 ${obj.strength_sum} vs 야만인 ${obj.barbarians}`
          : `[침공 패배] 방어 ${obj.strength_sum} vs 야만인 ${obj.barbarians}`
        roomStore.appendSystemChat(head, cat)
      } catch (e) {
        // JSON 파싱 실패 시 raw 표시
        setErrorBanner(`야만인 침공 발생 (응답 파싱 실패): ${payload.resultJson}`)
        setTimeout(() => setErrorBanner(''), 4000)
      }
    }
    const onCityWallBuilt = (payload: { matchId: number; userCode: number; vertexId: number }) => {
      catanStore.upsertCityWall({
        matchId:  payload.matchId,
        vertexId: payload.vertexId,
        userCode: payload.userCode,
      })
      const nick = catanStore.getPlayers().find(p => p.userCode === payload.userCode)?.nickname ?? `유저${payload.userCode}`
      setErrorBanner(`${nick} 님 도시 성벽 건설 → vertex ${payload.vertexId}`)
      setTimeout(() => setErrorBanner(''), 2500)
    }

    // SEQ=10115 P8-CK-D — 진보 카드 이벤트 핸들러
    const onProgressCardDrawn = (payload: ProgressCardDrawnPayload) => {
      // Caller 본인은 cardType 정확히, 다른 사람은 ProgressCardDrawnCountOnly에서 +1만
      const myUc = authState.userCode
      if (payload.userCode === myUc) {
        catanStore.addMyProgressCard({
          cardId:   payload.cardId,
          category: payload.category,
          cardType: payload.cardType,
        })
        setErrorBanner(`진보 카드 추첨: ${payload.cardType}`)
        setTimeout(() => setErrorBanner(''), 2500)
      }
    }
    const onProgressCardDrawnCountOnly = (payload: { matchId: number; userCode: number; category: string }) => {
      catanStore.incrementOtherProgressCardCount(payload.userCode, +1)
    }
    const onProgressCardUsed = (payload: ProgressCardUsedPayload) => {
      const myUc = authState.userCode
      const nick = catanStore.getPlayers().find(p => p.userCode === payload.userCode)?.nickname ?? `유저${payload.userCode}`
      // 핸드에서 제거 (본인) 또는 카운트 감소 (다른 사람)
      if (payload.userCode === myUc) {
        catanStore.removeMyProgressCard(payload.cardId)
      } else {
        catanStore.incrementOtherProgressCardCount(payload.userCode, -1)
      }
      // 시스템 채팅 CK_PROGRESS_USE (사용자 컨펌 #7 핵심 5종)
      const lbl = payload.cardType
      roomStore.appendSystemChat(`${nick} 님이 ${lbl} 카드를 사용했습니다`, 'CK_PROGRESS_USE')
      // 효과 적용은 후속 SP/이벤트가 별도로 자원/기사 갱신 broadcast (TODO P8-CK-E: 효과 처리 통합)
    }
    const onPermanentCardAcquired = (payload: PermanentCardAcquiredPayload) => {
      catanStore.addPermanentCard(payload.userCode, payload.cardType)
      // POL_DEFENDER_OF_CATAN 은 카운터 +1 (BarbarianInvasion 결과에서도 처리되지만 안전망)
      if (payload.cardType === 'POL_DEFENDER_OF_CATAN') {
        catanStore.incrementDefenderOfCatan(payload.userCode, +1)
      }
    }

    // SEQ=10125 P8-CK-H2 (c) — POL_SPY second-stage 결과 수신
    //   서버는 viewer 한정 SpyHandRevealed 송출 (정보 비대칭 보존). 본인이 진행 중인
    //   ProgressActionMode=POL_SPY step=pick_player → step=pick_card 로 전이 + targetHand 주입.
    //   매칭 실패 (다른 카드 진행 중 또는 SEQ 불일치) 시 무시.
    const onSpyHandRevealed = (payload: { matchId: number; targetUserCode: number; cards: { cardId: number; cardType: string }[] }) => {
      setProgressMode((prev) => {
        if (!prev || prev.kind !== 'POL_SPY') return prev
        if (prev.targetUserCode != null && prev.targetUserCode !== payload.targetUserCode) return prev
        return {
          ...prev,
          step:           'pick_card',
          targetUserCode: payload.targetUserCode,
          targetHand:     payload.cards,
        }
      })
    }

    // ─────────────────────────────────────────────────────────
    // SEQ=10119 P8-CK-F2 — 진보 카드 효과 broadcast 핸들러
    // CatanHub.BroadcastProgressEffectSideEffects 가 9 kind 로 송출:
    //   ROBBER_MOVED / KNIGHTS_ACTIVATED / KNIGHTS_UPGRADED / KNIGHT_CHANGED
    //   CITY_WALL_BUILT / BOARD_CHANGED / TOKEN_SWAPPED / MERCHANT_PLACED
    //   FLAG_SET  (SEQ=10119 신규)
    // 각 kind 별 토스트 + 시각 갱신 hook.
    // 실제 보드 상태(자원/기사/도시벽)는 ResourceCountUpdated / MyResourcesSync /
    // KnightUpgraded / CityWallBuilt 등 기존 broadcast 가 권위적으로 갱신함.
    // 본 핸들러는 보조 UX (시스템 메시지 + 토스트) 만 담당.
    // ─────────────────────────────────────────────────────────
    const onProgressEffectApplied = (payload: {
      matchId: number
      userCode: number
      cardType: string
      effectJson: string
      kind: string
      flag?: string
      flagValue?: string
    }) => {
      const nick = catanStore.getPlayers().find(p => p.userCode === payload.userCode)?.nickname ?? `유저${payload.userCode}`
      switch (payload.kind) {
        case 'ROBBER_MOVED':
          roomStore.appendSystemChat(`🦹 ${nick} ${payload.cardType} — 도적 이동`, 'CK_PROGRESS_USE')
          // 도적 시각 갱신은 별도 RobberMoved broadcast 가 전담
          break
        case 'KNIGHTS_ACTIVATED':
          roomStore.appendSystemChat(`⚔️ ${nick} 본인 기사 일괄 활성화`, 'CK_PROGRESS_USE')
          break
        case 'KNIGHTS_UPGRADED':
          roomStore.appendSystemChat(`🛡️ ${nick} 기사 격상 (${payload.cardType})`, 'CK_PROGRESS_USE')
          break
        case 'KNIGHT_CHANGED':
          roomStore.appendSystemChat(`🛡️ ${nick} ${payload.cardType} — 기사 변경`, 'CK_PROGRESS_USE')
          break
        case 'CITY_WALL_BUILT':
          roomStore.appendSystemChat(`🧱 ${nick} 도시 성벽 무료 건설 (Engineer)`, 'CK_PROGRESS_USE')
          break
        case 'BOARD_CHANGED':
          roomStore.appendSystemChat(`🗺️ ${nick} 보드 변경 (${payload.cardType})`, 'CK_PROGRESS_USE')
          break
        case 'TOKEN_SWAPPED':
          roomStore.appendSystemChat(`🔢 ${nick} 숫자 토큰 교환 (Inventor)`, 'CK_PROGRESS_USE')
          break
        case 'MERCHANT_PLACED':
          roomStore.appendSystemChat(`🛒 ${nick} 상인 마커 이동`, 'CK_PROGRESS_USE')
          break
        case 'FLAG_SET': {
          const flagLabel: Record<string, string> = {
            NEXT_DICE_FORCE: '다음 굴림 강제',
            NEXT_CITY_IMPROVE_DISCOUNT: '다음 도시격상 -1상품',
            NEXT_CITY_BUILD_COST_OVERRIDE: '도시 비용 ORE2+WHEAT1',
            NEXT_BUILD_DISCOUNT: '다음 건설 -1자원',
            NEXT_DISTRIBUTE_BONUS: '다음 분배 +1',
            TRADE_2_TO_1_RES: '자원 2:1 (이번 차례)',
            TRADE_BONUS_PLUS_1: '거래당 +1 (이번 턴)',
          }
          const lbl = payload.flag ? (flagLabel[payload.flag] ?? payload.flag) : payload.cardType
          const v = payload.flagValue ? ` = ${payload.flagValue}` : ''
          roomStore.appendSystemChat(`✨ ${nick} 효과 활성: ${lbl}${v}`, 'CK_PROGRESS_USE')
          if (payload.userCode === authState.userCode) {
            setActionMsg(`효과 활성: ${lbl}${v}`)
            setTimeout(() => setActionMsg(''), 4000)
          }
          break
        }
        default:
          roomStore.appendSystemChat(`${nick} 진보카드 효과 (${payload.kind})`, 'CK_PROGRESS_USE')
      }
    }

    const onResourceCountUpdated = (payload: ResourceCountUpdatedPayload) => {
      catanStore.applyResourceCountUpdated(payload.userCode, payload.totalCount)
    }

    // ── Phase 4-1 이벤트 핸들러 ─────────────────────────
    const onDiceRolled = (payload: DiceRolledPayload) => {
      catanStore.applyDiceRolled(payload)
      setDiceRolling(false)
      // SEQ=10110 STEP=4 — 주사위 굴림 효과음 (모두에게)
      sound.play('dice_roll')
      // 시스템 메시지
      const nick = catanStore.getPlayers().find(p => p.userCode === payload.userCode)?.nickname ?? `#${payload.userCode}`
      const sevenTag = payload.total === 7 ? ' (도적!)' : ''
      roomStore.appendSystemChat(`🎲 ${nick} 주사위 ${payload.dice1}+${payload.dice2}=${payload.total}${sevenTag}`, 'DICE_ROLL')
    }

    const onResourceGrantedV2 = (payload: ResourceGrantedPayload) => {
      catanStore.applyResourceGrantedV2(payload, authState.userCode)
      if (payload.userCode === authState.userCode) {
        // 신고 #7 UX 개선: 자원 종류 상세 표시 (이전 "자원 N장 획득!" 한 줄)
        const detail = payload.grants
          .map(g => {
            const rt = g.resourceType ?? g.type ?? ''
            const lbl = RESOURCE_LABEL[rt]
            return lbl ? `${lbl.emoji}${lbl.label} ${g.count}` : `${rt} ${g.count}`
          })
          .join(', ')
        const total = payload.grants.reduce((s, g) => s + g.count, 0)
        setActionMsg(detail ? `자원 획득: ${detail} (총 ${total}장)` : `자원 ${total}장 획득!`)
        setTimeout(() => setActionMsg(''), 4500)
      }
    }

    // ── Phase 4-2 이벤트 핸들러 ─────────────────────────
    const onRobberPhaseStarted = (payload: RobberPhaseStartedPayload) => {
      catanStore.applyRobberPhaseStarted(payload, authState.userCode)
      const myReq = payload.pendingDiscards.find(p => p.userCode === authState.userCode)?.required ?? 0
      setMyDiscardRequired(myReq)
      if (myReq > 0) {
        setDiscardOpen(true)
        setDiscardCounts({ WOOL: 0, WHEAT: 0, BRICK: 0, ORE: 0, WOOD: 0 })
      }
    }

    const onRobberMoved = (payload: RobberMovedPayload) => {
      catanStore.applyRobberMoved(payload)
      setVictimChoice(null)
      // 시스템 메시지
      const nick = catanStore.getPlayers().find(p => p.userCode === payload.userCode)?.nickname ?? `#${payload.userCode}`
      roomStore.appendSystemChat(`🦹 ${nick} 도적을 이동시켰습니다 (hex #${payload.hexId})`, 'ROBBER_MOVE')
      // Track C-2 도둑 위치 펄스 — D-4 1초 짧은 강조
      if (robberPulseTimerRef.current) clearTimeout(robberPulseTimerRef.current)
      setRobberPulse(true)
      robberPulseTimerRef.current = setTimeout(() => setRobberPulse(false), 1000)
    }

    const onCardsDiscarded = (payload: CardsDiscardedPayload) => {
      catanStore.applyCardsDiscarded(payload, authState.userCode)
      if (payload.userCode === authState.userCode) {
        setMyDiscardRequired(0)
        setDiscardOpen(false)
      }
    }

    const onCardsStolen = (payload: CardsStolenPayload) => {
      // Track D-4 (2026-05-06): 그룹 broadcast에는 자원 종류 미포함
      catanStore.applyCardsStolen(payload, authState.userCode)
      const isThief  = payload.thiefUserCode === authState.userCode
      const isVictim = payload.victimUserCode === authState.userCode
      if (isThief)       setActionMsg('약탈 성공! (자원 종류는 별도 알림)')   // 곧 CardsStolenDetail 도착
      else if (isVictim) setActionMsg('자원 1장을 약탈당했습니다.')
      else               setActionMsg('도적 약탈이 발생했습니다 (종류 비공개)')
      setTimeout(() => setActionMsg(''), 4000)
      // 시스템 메시지 — 정보 비대칭 보존 (자원 종류 비공개, 1장만 표시)
      const thiefNick  = catanStore.getPlayers().find(p => p.userCode === payload.thiefUserCode)?.nickname  ?? `#${payload.thiefUserCode}`
      const victimNick = catanStore.getPlayers().find(p => p.userCode === payload.victimUserCode)?.nickname ?? `#${payload.victimUserCode}`
      roomStore.appendSystemChat(`💰 ${thiefNick} 이 ${victimNick} 에게서 자원 1장 약탈`, 'ROBBER_STEAL')
    }

    // Track D-4 (2026-05-06) — 가해자 전용 자원 종류 인지 (Caller broadcast)
    const onCardsStolenDetail = (payload: { thiefUserCode: number; victimUserCode: number; resourceType: string }) => {
      // 본인이 가해자(thief)인 경우만 도착
      const lbl = RESOURCE_LABEL[payload.resourceType]?.label ?? payload.resourceType
      setActionMsg(`자원 ${lbl} 1장을 약탈했습니다!`)
      setTimeout(() => setActionMsg(''), 4000)
    }

    const onPlayerResourcesSync = (payload: PlayerResourcesSyncPayload) => {
      // 2026-05-07 fix: MONOPOLY 등 영향받은 다른 플레이어 본인 자원 5행 갱신
      catanStore.applyPlayerResourcesSync(payload, authState.userCode)
    }

    const onMyResourcesSync = (payload: MyResourcesSyncPayload) => {
      catanStore.applyMyResourcesSync(payload)
      // Track 2 (2026-05-06 D-1·D-3) — 본인 자원 변동 시 PROPOSER OPEN 자동 cancel
      // 카운터(parent_trade_id != null)도 본인 PROPOSER면 동일 적용 (D-3)
      const myUc = authState.userCode
      const myOpenTrades = catanStore.getOpenTrades()
        .filter(t => t.proposerUserCode === myUc)
      if (myOpenTrades.length === 0) return
      const myRes = catanStore.getMyResources()
      const mid = game?.matchId ?? matchId ?? 0
      if (!mid) return
      let cancelledAny = false
      for (const t of myOpenTrades) {
        const giveCounts = (() => {
          try { return JSON.parse(t.giveJson) as Record<string, number> } catch { return {} }
        })()
        const insufficient = Object.entries(giveCounts).some(
          ([rt, n]) => (myRes[rt] ?? 0) < (n ?? 0)
        )
        if (insufficient) {
          catanHub.cancelTrade(mid, t.tradeId).catch(() => {})
          cancelledAny = true
        }
      }
      if (cancelledAny) {
        // D-2 — 토스트 3.5초
        setActionMsg('자원 부족으로 거래 제안 자동 취소')
        setTimeout(() => setActionMsg(''), 3500)
      }
    }

    const onActionError = ({ message }: { code: string; message: string }) => {
      setErrorBanner(message)
      setDiceRolling(false)
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current)
      errorTimerRef.current = setTimeout(() => setErrorBanner(''), 4000)
    }

    // ── Phase 6 발전 카드 이벤트 핸들러 ─────────────────────
    const onMyDevCardsSync = (payload: MyDevCardsSyncPayload) => {
      catanStore.applyMyDevCardsSync(payload)
    }

    const onDevCardBought = (payload: DevCardBoughtPayload) => {
      // 현재 턴 번호 조회 (boughtTurnNo 기록용 — 같은 턴 사용 금지 검증)
      // 서버 SP에서 BOUGHT_TURN_NO=TURN_NO로 자동 기록됨. 클라는 표시 목적.
      // 정확한 값은 GetMyDevCards 재조회로 받지만, 즉시 추가도 OK.
      // turn_no는 클라 store에 별도 보관하지 않으므로 -1 sentinel 사용 후 다음 sync에서 정정.
      catanStore.applyDevCardBought(payload, -1)
      // 정확 동기화 위해 즉시 재조회 (덱 잔량·BOUGHT_TURN_NO)
      catanHub.getMyDevCards(matchId ?? 0).catch(() => {})
      const lblMap: Record<DevCardType, string> = {
        KNIGHT: '기사', ROAD_BUILDING: '도로 건설', MONOPOLY: '독점',
        YEAR_OF_PLENTY: '풍년', VICTORY_POINT: '승점',
      }
      const lbl = lblMap[payload.cardType] ?? payload.cardType
      setActionMsg(`발전 카드 획득: ${lbl}`)
      setTimeout(() => setActionMsg(''), 4000)
    }

    const onDevCardCountChanged = (payload: DevCardCountChangedPayload) => {
      catanStore.applyDevCardCountChanged(payload, authState.userCode)
    }

    const onDevCardDeckChanged = (payload: DevCardDeckChangedPayload) => {
      catanStore.applyDevCardDeckChanged(payload)
    }

    const onDevCardPlayed = (payload: DevCardPlayedPayload) => {
      catanStore.applyDevCardPlayed(payload, authState.userCode)
      const player = catanStore.getPlayers().find(p => p.userCode === payload.userCode)
      const name = player?.nickname ?? `사용자 #${payload.userCode}`
      const lblMap: Record<DevCardType, string> = {
        KNIGHT: '기사', ROAD_BUILDING: '도로 건설', MONOPOLY: '독점',
        YEAR_OF_PLENTY: '풍년', VICTORY_POINT: '승점',
      }
      const lbl = lblMap[payload.cardType] ?? payload.cardType
      let msg = `${name}: ${lbl} 카드 사용`
      if (payload.cardType === 'MONOPOLY' && payload.totalTaken != null) {
        msg += ` (${payload.resourceType} ${payload.totalTaken}장 약탈)`
      } else if (payload.cardType === 'YEAR_OF_PLENTY' && payload.resource1 && payload.resource2) {
        msg += ` (${payload.resource1} + ${payload.resource2})`
      }
      setActionMsg(msg)
      setTimeout(() => setActionMsg(''), 4500)
      // 시스템 채팅 (DEVCARD_PLAYED 화이트리스트 — VICTORY_POINT 단순 공개는 차단, 효과 카드만 노출)
      if (payload.cardType !== 'VICTORY_POINT') {
        roomStore.appendSystemChat(`🃏 ${msg}`, 'DEVCARD_PLAYED')
      }
    }

    const onLargestArmyChanged = (payload: LargestArmyChangedPayload) => {
      catanStore.applyLargestArmyChanged(payload)
      const toName = catanStore.getPlayers().find(p => p.userCode === payload.toUserCode)?.nickname ?? ''
      const cnt   = payload.knightCount ? ` (기사 ${payload.knightCount}장)` : ''
      const msg = `기사단 승점 → ${toName}${cnt} (+2점)`
      setActionMsg(msg)
      setTimeout(() => setActionMsg(''), 4500)
      roomStore.appendSystemChat(`🛡️ ${msg}`, 'LARGEST_ARMY')
    }

    // 단계 5 (Phase 7) 이벤트 핸들러
    const onLongestRoadChanged = (payload: LongestRoadChangedPayload) => {
      catanStore.applyLongestRoadChanged(payload)
      let msg: string
      if (payload.toUserCode == null) {
        msg = `가장 긴 도로 박탈 (5미만)`
      } else {
        const name = catanStore.getPlayers().find(p => p.userCode === payload.toUserCode)?.nickname ?? ''
        msg = `가장 긴 도로 → ${name} (도로 ${payload.roadLength}개, +2점)`
      }
      setActionMsg(msg)
      setTimeout(() => setActionMsg(''), 4500)
      roomStore.appendSystemChat(`🛣️ ${msg}`, 'LONGEST_ROAD')
    }
    const onAllScoresSync = (payload: AllScoresSyncPayload) => {
      catanStore.applyAllScoresSync(payload)
    }
    const onMyVictoryPointsSync = (payload: MyVictoryPointsSyncPayload) => {
      catanStore.applyMyVictoryPointsSync(payload)
    }
    const onGameEnded = (payload: GameEndedPayload) => {
      catanStore.applyGameEnded(payload)
      const winName = catanStore.getPlayers().find(p => p.userCode === payload.winnerUserCode)?.nickname ?? '?'
      setActionMsg(`게임 종료 — 우승: ${winName} (${payload.winnerPoints}점)`)
      // 자동 종료 알림 모달은 store.gameEnded로 분기 (UI 렌더에서 표시)
      // 시스템 메시지 — 게임 종료
      roomStore.appendSystemChat(`🏆 ${winName} 우승! (${payload.winnerPoints}점)`, 'GAME_END')
    }

    // ── RoomHub 이벤트 (채팅 + 입퇴장 시스템 메시지) ─────────
    const onRoomChatReceived = (data: unknown) => {
      const d = data as { userCode: number; nickname: string; message: string; timestamp: string }
      roomStore.appendChat({
        userCode:  d.userCode,
        nickname:  d.nickname,
        message:   d.message,
        timestamp: (d.timestamp ?? '').slice(11, 16),
        isSystem:  false,
      })
    }
    const onRoomMemberJoined = (data: unknown) => {
      const d = data as { nickname: string }
      if (d.nickname) roomStore.appendSystemChat(`${d.nickname} 님이 입장했습니다.`, 'MEMBER_ENTER')
    }
    const onRoomMemberLeft = (data: unknown) => {
      const d = data as { userCode: number }
      const leftNick = catanStore.getPlayers().find(p => p.userCode === d.userCode)?.nickname
      if (leftNick) roomStore.appendSystemChat(`${leftNick} 님이 퇴장했습니다.`, 'MEMBER_LEAVE')
    }
    // "새 게임" — 방장이 RestartGame 호출 시 그룹 전체가 RoomRestarted 수신.
    // 방이 WAITING 으로 전환됐으므로 대기실(Room) 화면으로 이동 (SEQ=10076 흐름 변경).
    const onRoomRestartedEvent = (data: unknown) => {
      const d = data as Parameters<typeof roomStore.applyDetail>[0] & { roomCode: string }
      // 갱신된 RoomDetail 을 store 에 반영 (멤버/좌석/STATUS=WAITING 동기화)
      if (d && (d as { room?: unknown }).room) {
        roomStore.applyDetail(d)
      }
      roomStore.appendSystemChat('🔄 새 게임 대기실로 이동합니다.', 'GAME_START')
      onRoomRestarted(d.roomCode)
    }
    // RoomHub Error — RestartGame 등 실패 시 진행 상태 해제 + 안내
    const onRoomError = (data: unknown) => {
      const d = data as { code?: string; msg?: string; action?: string }
      if (d.action === 'RestartGame') {
        setRestartPending(false)
        setActionMsg(d.msg || '새 게임 준비에 실패했습니다.')
        setTimeout(() => setActionMsg(''), 4000)
      }
    }
    roomHub.on('ChatReceived',  onRoomChatReceived  as (...a: unknown[]) => void)
    roomHub.on('MemberJoined',  onRoomMemberJoined  as (...a: unknown[]) => void)
    roomHub.on('MemberLeft',    onRoomMemberLeft    as (...a: unknown[]) => void)
    roomHub.on('RoomRestarted', onRoomRestartedEvent as (...a: unknown[]) => void)
    roomHub.on('Error',         onRoomError         as (...a: unknown[]) => void)

    // ── Phase 5 거래 핸들러 ───────────────────────────────
    const onTradeProposed = (payload: TradeProposedPayload) => {
      catanStore.applyTradeProposed(payload)
      // 응답자 자동 모달: 제안자 본인 아니면서 (TARGET NULL 또는 본인 지정) — D-12 클라 측 보강
      const myUc = authState.userCode
      const isProposer = payload.proposerUserCode === myUc
      const isAddressed = payload.targetUserCode == null || payload.targetUserCode === myUc
      if (!isProposer && isAddressed) {
        // 이미 다른 응답 모달이 떠있는 경우 새 trade는 무시 (사용자가 닫을 때까지)
        setRespondTradeId(prev => prev ?? payload.tradeId)
        // SEQ=10110 STEP=4 — 본인이 응답 가능한 거래 제안일 때만 알림음
        sound.play('trade_request')
      }
      // 시스템 메시지
      const proposerNick = catanStore.getPlayers().find(p => p.userCode === payload.proposerUserCode)?.nickname ?? `#${payload.proposerUserCode}`
      const targetNick = payload.targetUserCode != null
        ? (catanStore.getPlayers().find(p => p.userCode === payload.targetUserCode)?.nickname ?? `#${payload.targetUserCode}`)
        : '전체'
      roomStore.appendSystemChat(`💱 ${proposerNick} 거래 제안 → ${targetNick}`, 'TRADE_PROPOSE')
    }

    const onTradeResponded = (payload: TradeRespondedPayload) => {
      catanStore.applyTradeResponded(payload, authState.userCode)
      const responderNick = catanStore.getPlayers().find(p => p.userCode === payload.responderUserCode)?.nickname ?? `#${payload.responderUserCode}`
      const actLabel = payload.action === 'REJECT' ? '거절' : '카운터 제안'
      roomStore.appendSystemChat(`💱 ${responderNick} ${actLabel}`, 'TRADE_RESPOND')
    }

    const onTradeCounterProposed = (payload: TradeCounterProposedPayload) => {
      catanStore.applyTradeCounterProposed(payload)
      const myUc = authState.userCode
      // 신규 카운터 trade의 target이 본인이면 응답 모달 자동 표시
      if (payload.targetUserCode === myUc) {
        setRespondTradeId(prev => prev ?? payload.counterTradeId)
      }
    }

    // 거래 결과 요약 1줄 생성 헬퍼 — 수락/거부/카운터/대기 응답자 나열
    const buildTradeResultSummary = (tradeId: number, proposerUserCode: number): string => {
      const allPlayers = catanStore.getPlayers()
      const responses  = catanStore.getTradeResponses(tradeId)
      const parts: string[] = []
      // 제안자 본인 제외한 모든 플레이어 응답 수집
      for (const p of allPlayers) {
        if (p.userCode === proposerUserCode) continue
        const act = responses[p.userCode]
        if (act === 'ACCEPT')  parts.push(`${p.nickname}=수락`)
        else if (act === 'REJECT')  parts.push(`${p.nickname}=거부`)
        else if (act === 'COUNTER') parts.push(`${p.nickname}=카운터`)
      }
      return parts.length > 0 ? parts.join(', ') : ''
    }

    const onTradeCompleted = (payload: TradeCompletedPayload) => {
      catanStore.applyTradeCompleted(payload)
      // 본인이 제안자/수락자였던 모달 닫기
      if (payload.tradeId != null && respondTradeIdRef.current === payload.tradeId) {
        setRespondTradeId(null)
        setCounterMode(false)
      }
      // 시스템 메시지 (2026-05-09 화이트리스트 정책 — 카테고리 통과분만 chatLog 누적)
      if (payload.kind === 'PLAYER') {
        const pNick = catanStore.getPlayers().find(p => p.userCode === payload.proposerUserCode)?.nickname ?? `#${payload.proposerUserCode}`
        const aNick = catanStore.getPlayers().find(p => p.userCode === payload.accepterUserCode)?.nickname ?? `#${payload.accepterUserCode}`
        roomStore.appendSystemChat(`✅ 거래 성사: ${pNick} ↔ ${aNick}`, 'TRADE_PLAYER_COMPLETE')
        // 거래 결과 요약 1줄 (전체 응답자 액션 — 채팅 로그에 누적, 토스트와 이중 노출 의도)
        if (payload.tradeId != null && payload.proposerUserCode != null) {
          const summary = buildTradeResultSummary(payload.tradeId, payload.proposerUserCode)
          if (summary) {
            roomStore.appendSystemChat(`  └ 응답 결과: ${summary}`, 'TRADE_PLAYER_COMPLETE')
          }
          // 토스트는 제안자에게만 (즉시 시인성)
          if (payload.proposerUserCode === authState.userCode) {
            const summaryText = summary ? ` — ${summary}` : ''
            setActionMsg(`거래 성사: ${aNick} 수락${summaryText}`)
            setTimeout(() => setActionMsg(''), 5000)
          }
        }
      } else if (payload.kind === 'BANK') {
        const nick = catanStore.getPlayers().find(p => p.userCode === payload.userCode)?.nickname ?? `#${payload.userCode}`
        roomStore.appendSystemChat(`🏦 ${nick} 은행 4:1 (${payload.giveType} → ${payload.getType})`, 'TRADE_BANK')
      } else if (payload.kind === 'PORT') {
        const nick = catanStore.getPlayers().find(p => p.userCode === payload.userCode)?.nickname ?? `#${payload.userCode}`
        roomStore.appendSystemChat(`⚓ ${nick} 항구 ${payload.ratio}:1 (${payload.giveType} → ${payload.getType})`, 'TRADE_PORT')
      }
    }

    const onTradeCancelled = (payload: TradeCancelledPayload) => {
      // 거래 결과 요약 — applyTradeCancelled 전에 수집 (제거 전 responses 아직 유효)
      const summary = buildTradeResultSummary(payload.tradeId, payload.proposerUserCode)
      catanStore.applyTradeCancelled(payload)
      // 본인이 응답 모달 열고 있던 trade가 취소되면 모달 닫기
      if (respondTradeIdRef.current === payload.tradeId) {
        setRespondTradeId(null)
        setCounterMode(false)
      }
      const reasonLabel = payload.reason === 'END_TURN' ? '턴 종료 자동 취소'
        : payload.reason === 'ALL_REJECTED' ? '전원 거부로 자동 취소'
        : '취소'
      const pNick = catanStore.getPlayers().find(p => p.userCode === payload.proposerUserCode)?.nickname ?? `#${payload.proposerUserCode}`
      roomStore.appendSystemChat(`💱 ${pNick} 거래 ${reasonLabel}`, 'TRADE_CANCEL')
      if (summary) roomStore.appendSystemChat(`  └ 응답 결과: ${summary}`, 'TRADE_CANCEL')
      // 거래 결과 토스트 (제안자에게만, USER_CANCEL은 본인 액션이므로 토스트 생략)
      if (payload.proposerUserCode === authState.userCode && payload.reason !== 'USER_CANCEL') {
        const summaryText = summary ? ` — ${summary}` : ''
        setActionMsg(`거래 ${reasonLabel}${summaryText}`)
        setTimeout(() => setActionMsg(''), 5000)
      }
    }

    const onProposerResourcesSync = (payload: ProposerResourcesSyncPayload) => {
      catanStore.applyProposerResourcesSync(payload, authState.userCode)
    }

    catanHub.on('TradeProposed',         onTradeProposed         as (...a: unknown[]) => void)
    catanHub.on('TradeResponded',        onTradeResponded        as (...a: unknown[]) => void)
    catanHub.on('TradeCounterProposed',  onTradeCounterProposed  as (...a: unknown[]) => void)
    catanHub.on('TradeCompleted',        onTradeCompleted        as (...a: unknown[]) => void)
    catanHub.on('TradeCancelled',        onTradeCancelled        as (...a: unknown[]) => void)
    catanHub.on('ProposerResourcesSync', onProposerResourcesSync as (...a: unknown[]) => void)

    catanHub.on('BuildingPlaced',        onBuildingPlaced as (...args: unknown[]) => void)
    catanHub.on('RoadPlaced',            onRoadPlaced     as (...args: unknown[]) => void)
    catanHub.on('TurnAdvanced',          onTurnAdvanced   as (...args: unknown[]) => void)
    catanHub.on('ScoreUpdated',          onScoreUpdated   as (...args: unknown[]) => void)
    // SEQ=10112 P8-CK-A — EXT_CK 도시 개선 격상 이벤트
    catanHub.on('CityImproved',          onCityImproved   as (...args: unknown[]) => void)
    // SEQ=10113 P8-CK-B — 기사 이벤트 6종
    catanHub.on('KnightBuilt',           onKnightBuilt    as (...args: unknown[]) => void)
    catanHub.on('KnightUpgraded',        onKnightUpgraded as (...args: unknown[]) => void)
    catanHub.on('KnightActivated',       onKnightActivated as (...args: unknown[]) => void)
    catanHub.on('KnightMoved',           onKnightMoved    as (...args: unknown[]) => void)
    catanHub.on('KnightDisplaced',       onKnightDisplaced as (...args: unknown[]) => void)
    catanHub.on('RobberChased',          onRobberChased   as (...args: unknown[]) => void)
    catanHub.on('KnightsDeactivated',    onKnightsDeactivated as (...args: unknown[]) => void)
    // SEQ=10114 P8-CK-C — 야만인 트랙 + 침공 + 도시 성벽
    catanHub.on('BarbarianAdvanced',     onBarbarianAdvanced as (...args: unknown[]) => void)
    catanHub.on('BarbarianInvasion',     onBarbarianInvasion as (...args: unknown[]) => void)
    catanHub.on('CityWallBuilt',         onCityWallBuilt     as (...args: unknown[]) => void)
    // SEQ=10115 P8-CK-D — 진보 카드 4종 이벤트
    catanHub.on('ProgressCardDrawn',          onProgressCardDrawn          as (...args: unknown[]) => void)
    catanHub.on('ProgressCardDrawnCountOnly', onProgressCardDrawnCountOnly as (...args: unknown[]) => void)
    catanHub.on('ProgressCardUsed',           onProgressCardUsed           as (...args: unknown[]) => void)
    catanHub.on('PermanentCardAcquired',      onPermanentCardAcquired      as (...args: unknown[]) => void)
    // SEQ=10125 P8-CK-H2 (c) — POL_SPY second-stage RPC 결과
    catanHub.on('SpyHandRevealed',            onSpyHandRevealed            as (...args: unknown[]) => void)
    // SEQ=10119 P8-CK-F2 — 진보카드 효과 broadcast 9 kind
    catanHub.on('ProgressEffectApplied',      onProgressEffectApplied      as (...args: unknown[]) => void)
    catanHub.on('ResourceGranted',       onResourceGrantedV2 as (...args: unknown[]) => void)
    catanHub.on('ResourceCountUpdated',  onResourceCountUpdated as (...args: unknown[]) => void)
    catanHub.on('DiceRolled',            onDiceRolled     as (...args: unknown[]) => void)
    catanHub.on('RobberPhaseStarted',    onRobberPhaseStarted as (...args: unknown[]) => void)
    catanHub.on('RobberMoved',           onRobberMoved    as (...args: unknown[]) => void)
    catanHub.on('CardsDiscarded',        onCardsDiscarded as (...args: unknown[]) => void)
    catanHub.on('CardsStolen',           onCardsStolen    as (...args: unknown[]) => void)
    catanHub.on('CardsStolenDetail',     onCardsStolenDetail as (...args: unknown[]) => void)
    catanHub.on('MyResourcesSync',       onMyResourcesSync as (...args: unknown[]) => void)
    catanHub.on('PlayerResourcesSync',   onPlayerResourcesSync as (...args: unknown[]) => void)
    catanHub.on('ActionError',           onActionError    as (...args: unknown[]) => void)
    // Phase 6 발전 카드 이벤트
    catanHub.on('MyDevCardsSync',        onMyDevCardsSync as (...args: unknown[]) => void)
    catanHub.on('DevCardBought',         onDevCardBought  as (...args: unknown[]) => void)
    catanHub.on('DevCardCountChanged',   onDevCardCountChanged as (...args: unknown[]) => void)
    catanHub.on('DevCardDeckChanged',    onDevCardDeckChanged as (...args: unknown[]) => void)
    catanHub.on('DevCardPlayed',         onDevCardPlayed  as (...args: unknown[]) => void)
    catanHub.on('LargestArmyChanged',    onLargestArmyChanged as (...args: unknown[]) => void)
    // 단계 5 (Phase 7) 이벤트
    catanHub.on('LongestRoadChanged',    onLongestRoadChanged as (...args: unknown[]) => void)
    catanHub.on('AllScoresSync',         onAllScoresSync      as (...args: unknown[]) => void)
    catanHub.on('MyVictoryPointsSync',   onMyVictoryPointsSync as (...args: unknown[]) => void)
    catanHub.on('GameEnded',             onGameEnded          as (...args: unknown[]) => void)

    // 진입 시 본인 보유 카드 + 덱 잔량 1회 동기화 (재연결 포함)
    if (matchId) {
      catanHub.getMyDevCards(matchId).catch(() => {})
      // 단계 5: 진입 시 모든 점수 동기화
      catanHub.getAllScores(matchId).catch(() => {})
    }

    return () => {
      roomHub.off('ChatReceived', onRoomChatReceived as (...a: unknown[]) => void)
      roomHub.off('MemberJoined', onRoomMemberJoined as (...a: unknown[]) => void)
      roomHub.off('MemberLeft',   onRoomMemberLeft   as (...a: unknown[]) => void)
      roomHub.off('RoomRestarted', onRoomRestartedEvent as (...a: unknown[]) => void)
      roomHub.off('Error',        onRoomError        as (...a: unknown[]) => void)
      // Phase 5 거래
      catanHub.off('TradeProposed',         onTradeProposed         as (...a: unknown[]) => void)
      catanHub.off('TradeResponded',        onTradeResponded        as (...a: unknown[]) => void)
      catanHub.off('TradeCounterProposed',  onTradeCounterProposed  as (...a: unknown[]) => void)
      catanHub.off('TradeCompleted',        onTradeCompleted        as (...a: unknown[]) => void)
      catanHub.off('TradeCancelled',        onTradeCancelled        as (...a: unknown[]) => void)
      catanHub.off('ProposerResourcesSync', onProposerResourcesSync as (...a: unknown[]) => void)
      catanHub.off('BuildingPlaced',       onBuildingPlaced as (...args: unknown[]) => void)
      catanHub.off('RoadPlaced',           onRoadPlaced     as (...args: unknown[]) => void)
      catanHub.off('TurnAdvanced',         onTurnAdvanced   as (...args: unknown[]) => void)
      catanHub.off('ScoreUpdated',         onScoreUpdated   as (...args: unknown[]) => void)
      catanHub.off('CityImproved',         onCityImproved   as (...args: unknown[]) => void)
      // SEQ=10113 P8-CK-B — 기사 이벤트 7개
      catanHub.off('KnightBuilt',          onKnightBuilt    as (...args: unknown[]) => void)
      catanHub.off('KnightUpgraded',       onKnightUpgraded as (...args: unknown[]) => void)
      catanHub.off('KnightActivated',      onKnightActivated as (...args: unknown[]) => void)
      catanHub.off('KnightMoved',          onKnightMoved    as (...args: unknown[]) => void)
      catanHub.off('KnightDisplaced',      onKnightDisplaced as (...args: unknown[]) => void)
      catanHub.off('RobberChased',         onRobberChased   as (...args: unknown[]) => void)
      catanHub.off('KnightsDeactivated',   onKnightsDeactivated as (...args: unknown[]) => void)
      catanHub.off('BarbarianAdvanced',    onBarbarianAdvanced as (...args: unknown[]) => void)
      catanHub.off('BarbarianInvasion',    onBarbarianInvasion as (...args: unknown[]) => void)
      catanHub.off('CityWallBuilt',        onCityWallBuilt     as (...args: unknown[]) => void)
      // SEQ=10115 P8-CK-D — 진보 카드 off
      catanHub.off('ProgressCardDrawn',          onProgressCardDrawn          as (...args: unknown[]) => void)
      catanHub.off('ProgressCardDrawnCountOnly', onProgressCardDrawnCountOnly as (...args: unknown[]) => void)
      catanHub.off('ProgressCardUsed',           onProgressCardUsed           as (...args: unknown[]) => void)
      catanHub.off('PermanentCardAcquired',      onPermanentCardAcquired      as (...args: unknown[]) => void)
      catanHub.off('SpyHandRevealed',            onSpyHandRevealed            as (...args: unknown[]) => void)
      catanHub.off('ProgressEffectApplied',      onProgressEffectApplied      as (...args: unknown[]) => void)
      catanHub.off('ResourceGranted',      onResourceGrantedV2 as (...args: unknown[]) => void)
      catanHub.off('ResourceCountUpdated', onResourceCountUpdated as (...args: unknown[]) => void)
      catanHub.off('DiceRolled',           onDiceRolled     as (...args: unknown[]) => void)
      catanHub.off('RobberPhaseStarted',   onRobberPhaseStarted as (...args: unknown[]) => void)
      catanHub.off('RobberMoved',          onRobberMoved    as (...args: unknown[]) => void)
      catanHub.off('CardsDiscarded',       onCardsDiscarded as (...args: unknown[]) => void)
      catanHub.off('CardsStolen',          onCardsStolen    as (...args: unknown[]) => void)
      catanHub.off('CardsStolenDetail',    onCardsStolenDetail as (...args: unknown[]) => void)
      catanHub.off('MyResourcesSync',      onMyResourcesSync as (...args: unknown[]) => void)
      catanHub.off('PlayerResourcesSync',  onPlayerResourcesSync as (...args: unknown[]) => void)
      catanHub.off('ActionError',          onActionError    as (...args: unknown[]) => void)
      catanHub.off('MyDevCardsSync',       onMyDevCardsSync as (...args: unknown[]) => void)
      catanHub.off('DevCardBought',        onDevCardBought  as (...args: unknown[]) => void)
      catanHub.off('DevCardCountChanged',  onDevCardCountChanged as (...args: unknown[]) => void)
      catanHub.off('DevCardDeckChanged',   onDevCardDeckChanged as (...args: unknown[]) => void)
      catanHub.off('DevCardPlayed',        onDevCardPlayed  as (...args: unknown[]) => void)
      catanHub.off('LargestArmyChanged',   onLargestArmyChanged as (...args: unknown[]) => void)
      // 단계 5
      catanHub.off('LongestRoadChanged',   onLongestRoadChanged as (...args: unknown[]) => void)
      catanHub.off('AllScoresSync',        onAllScoresSync      as (...args: unknown[]) => void)
      catanHub.off('MyVictoryPointsSync',  onMyVictoryPointsSync as (...args: unknown[]) => void)
      catanHub.off('GameEnded',            onGameEnded          as (...args: unknown[]) => void)
      // Track C-1·C-2 timer cleanup
      if (turnBannerTimerRef.current)  clearTimeout(turnBannerTimerRef.current)
      if (seatPulseTimerRef.current)   clearTimeout(seatPulseTimerRef.current)
      if (robberPulseTimerRef.current) clearTimeout(robberPulseTimerRef.current)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomCode, matchId])

  // ── Phase 5 거래 핸들러 ──────────────────────────────────

  /** 자원 카운터 → JSON 직렬화 */
  function countsToJson(counts: Record<string, number>): string {
    const obj: Record<string, number> = {}
    for (const k of RESOURCE_TYPES) {
      const n = counts[k] ?? 0
      if (n > 0) obj[k] = n
    }
    return JSON.stringify(obj)
  }

  /** JSON → 자원 카운트 (시각 표시용) */
  function jsonToCounts(json: string): Record<string, number> {
    try {
      const parsed = JSON.parse(json) as Record<string, number>
      return parsed
    } catch { return {} }
  }

  /** 거래 진입 가능 여부 (본인 POSTROLL) — myUserCode/isMyTurn은 아래에서 const 선언이므로 derived 직접 */
  const _myUc       = authState.userCode
  const _isMyTurn   = game?.currentTurnUserCode === _myUc
  const canTrade    = _isMyTurn && game?.currentPhase === 'MAIN_TURN_POSTROLL'

  /** 본인이 권한 보유한 항구 ID 목록 (vertex 점유 기반) */
  const myPortIds: number[] = ports
    .filter(p => {
      const v1 = vertices.find(v => v.vertexId === p.vertexId1)
      const v2 = vertices.find(v => v.vertexId === p.vertexId2)
      return (v1?.ownerUserCode === _myUc) || (v2?.ownerUserCode === _myUc)
    })
    .map(p => p.portId)

  function openProposeModal() {
    if (!canTrade) {
      setErrorBanner('주사위를 굴린 후 본인 턴에만 거래 제안 가능')
      setTimeout(() => setErrorBanner(''), 2500)
      return
    }
    // 본인 OPEN 1건 이미 있으면 모달 차단 (D-4)
    const myOpen = catanStore.getMyOpenProposal(myUserCode)
    if (myOpen) {
      setErrorBanner('이미 진행 중인 거래 제안이 있습니다. 응답을 받거나 취소 후 다시 시도하세요.')
      setTimeout(() => setErrorBanner(''), 3000)
      return
    }
    setProposeGiveCounts({})
    setProposeGetCounts({})
    setProposeTarget(null)
    setProposeOpen(true)
  }

  async function handleSubmitPropose() {
    const giveJson = countsToJson(proposeGiveCounts)
    const getJson  = countsToJson(proposeGetCounts)
    const giveTotal = Object.values(proposeGiveCounts).reduce((s, v) => s + (v ?? 0), 0)
    const getTotal  = Object.values(proposeGetCounts).reduce((s, v) => s + (v ?? 0), 0)
    if (giveTotal < 1 || getTotal < 1) {
      setErrorBanner('주는 자원과 받는 자원 각각 1장 이상이어야 합니다.')
      setTimeout(() => setErrorBanner(''), 2500)
      return
    }
    const mid = game?.matchId ?? matchId ?? 0
    if (!mid) return
    try {
      await catanHub.proposeTrade(mid, giveJson, getJson, proposeTarget)
      setProposeOpen(false)
    } catch {
      setErrorBanner('서버 연결 오류')
      setTimeout(() => setErrorBanner(''), 3000)
    }
  }

  async function handleRespondAccept(tradeId: number) {
    const mid = game?.matchId ?? matchId ?? 0
    if (!mid) return
    try {
      await catanHub.respondTrade(mid, tradeId, 'ACCEPT', null, null)
      setRespondTradeId(null)
      setCounterMode(false)
    } catch {
      setErrorBanner('서버 연결 오류')
      setTimeout(() => setErrorBanner(''), 3000)
    }
  }
  async function handleRespondReject(tradeId: number) {
    const mid = game?.matchId ?? matchId ?? 0
    if (!mid) return
    try {
      await catanHub.respondTrade(mid, tradeId, 'REJECT', null, null)
      setRespondTradeId(null)
      setCounterMode(false)
    } catch {
      setErrorBanner('서버 연결 오류')
      setTimeout(() => setErrorBanner(''), 3000)
    }
  }
  async function handleRespondCounter(tradeId: number) {
    const giveJson = countsToJson(counterGiveCounts)
    const getJson  = countsToJson(counterGetCounts)
    const giveTotal = Object.values(counterGiveCounts).reduce((s, v) => s + (v ?? 0), 0)
    const getTotal  = Object.values(counterGetCounts).reduce((s, v) => s + (v ?? 0), 0)
    if (giveTotal < 1 || getTotal < 1) {
      setErrorBanner('카운터도 양방 자원 1장 이상 필요')
      setTimeout(() => setErrorBanner(''), 2500)
      return
    }
    const mid = game?.matchId ?? matchId ?? 0
    if (!mid) return
    try {
      await catanHub.respondTrade(mid, tradeId, 'COUNTER', giveJson, getJson)
      setRespondTradeId(null)
      setCounterMode(false)
      setCounterGiveCounts({})
      setCounterGetCounts({})
    } catch {
      setErrorBanner('서버 연결 오류')
      setTimeout(() => setErrorBanner(''), 3000)
    }
  }
  async function handleCancelMyOpen() {
    const myOpen = catanStore.getMyOpenProposal(myUserCode)
    if (!myOpen) return
    const mid = game?.matchId ?? matchId ?? 0
    if (!mid) return
    try {
      await catanHub.cancelTrade(mid, myOpen.tradeId)
    } catch {
      setErrorBanner('서버 연결 오류')
      setTimeout(() => setErrorBanner(''), 3000)
    }
  }

  function openBankModal() {
    if (!canTrade) {
      setErrorBanner('주사위를 굴린 후 본인 턴에만 은행 거래 가능')
      setTimeout(() => setErrorBanner(''), 2500)
      return
    }
    setBankGiveType('')
    setBankGetType('')
    setBankOpen(true)
  }
  async function handleSubmitBank() {
    if (!bankGiveType || !bankGetType) {
      setErrorBanner('주는 자원과 받는 자원을 모두 선택하세요')
      setTimeout(() => setErrorBanner(''), 2500)
      return
    }
    if (bankGiveType === bankGetType) {
      setErrorBanner('같은 자원으로 거래할 수 없습니다.')
      setTimeout(() => setErrorBanner(''), 2500)
      return
    }
    if ((myResources[bankGiveType] ?? 0) < 4) {
      setErrorBanner(`${RESOURCE_LABEL[bankGiveType]?.label ?? bankGiveType} 4장 필요 (현재 ${myResources[bankGiveType] ?? 0}장)`)
      setTimeout(() => setErrorBanner(''), 3000)
      return
    }
    const mid = game?.matchId ?? matchId ?? 0
    if (!mid) return
    try {
      await catanHub.bankTrade(mid, bankGiveType, bankGetType)
      setBankOpen(false)
    } catch {
      setErrorBanner('서버 연결 오류')
      setTimeout(() => setErrorBanner(''), 3000)
    }
  }

  function openPortModal(portId: number) {
    if (!canTrade) {
      setErrorBanner('주사위를 굴린 후 본인 턴에만 항구 거래 가능')
      setTimeout(() => setErrorBanner(''), 2500)
      return
    }
    if (!myPortIds.includes(portId)) {
      setErrorBanner('해당 항구의 권한이 없습니다 (마을 또는 도시(성) 점유 필요)')
      setTimeout(() => setErrorBanner(''), 3000)
      return
    }
    const port = ports.find(p => p.portId === portId)
    setPortGiveType(port?.resourceType ?? '')
    setPortGetType('')
    setPortModalPortId(portId)
  }
  async function handleSubmitPort() {
    if (portModalPortId == null) return
    const port = ports.find(p => p.portId === portModalPortId)
    if (!port) return
    if (!portGiveType || !portGetType) {
      setErrorBanner('주는 자원과 받는 자원을 모두 선택하세요')
      setTimeout(() => setErrorBanner(''), 2500)
      return
    }
    if (portGiveType === portGetType) {
      setErrorBanner('같은 자원으로 거래할 수 없습니다.')
      setTimeout(() => setErrorBanner(''), 2500)
      return
    }
    if (port.ratio === 2 && port.resourceType && portGiveType !== port.resourceType) {
      setErrorBanner(`이 항구는 ${RESOURCE_LABEL[port.resourceType]?.label ?? port.resourceType} 2:1 항구입니다.`)
      setTimeout(() => setErrorBanner(''), 3000)
      return
    }
    if ((myResources[portGiveType] ?? 0) < port.ratio) {
      setErrorBanner(`${RESOURCE_LABEL[portGiveType]?.label ?? portGiveType} ${port.ratio}장 필요 (현재 ${myResources[portGiveType] ?? 0}장)`)
      setTimeout(() => setErrorBanner(''), 3000)
      return
    }
    const mid = game?.matchId ?? matchId ?? 0
    if (!mid) return
    try {
      await catanHub.portTrade(mid, portModalPortId, portGiveType, portGetType)
      setPortModalPortId(null)
    } catch {
      setErrorBanner('서버 연결 오류')
      setTimeout(() => setErrorBanner(''), 3000)
    }
  }

  // ── 채팅 보내기 ────────────────────────────────────────
  async function handleSendChat(message: string) {
    try {
      await roomHub.sendChat(roomCode, message)
    } catch {
      setErrorBanner('채팅 전송 실패')
      setTimeout(() => setErrorBanner(''), 3000)
    }
  }

  // ── 방 나가기 — 두 분기 (D-1·D-3, 2026-05-06 사용자 결정) ─────
  /** 잠시 나가기 — 자리 보존 (Phase 1.5 grace), BG_ROOM_MEMBER 그대로, 새로고침 시 자동 재진입 */
  async function handleLeaveTemp() {
    if (!window.confirm(
      '잠시 나가시겠습니까?\n' +
      '\n자리가 보존되어 재진입 가능합니다.\n' +
      '(새로고침하거나 다시 로그인하면 자동으로 게임에 복귀합니다.)'
    )) return
    catanStore.clearGame()
    await catanHub.stopConnection().catch(() => {})
    onLeave()
  }

  /** 게임 포기 — 자리 영구 정리 (D-2 그대로 진행: 다른 멤버 게임 계속, 빈 자리) */
  async function handleForfeitGame() {
    if (!window.confirm(
      '게임을 포기하시겠습니까?\n' +
      '\n자리가 영구 정리되어 재진입 불가합니다.\n' +
      '다른 플레이어는 빈 자리 그대로 게임을 계속합니다.'
    )) return
    try {
      // BG_ROOM_MEMBER row 삭제 — RoomHub.LeaveRoom invoke
      await roomHub.roomLeave(roomCode)
    } catch {
      // invoke 실패해도 클라 측 정리는 진행 (서버 측 grace 30분 후 자동 정리)
    }
    catanStore.clearGame()
    await catanHub.stopConnection().catch(() => {})
    onLeave()
  }

  /** 기존 호출자 호환용 — 게임 포기 모달 (보다 명시적인 쪽으로 위임) */
  async function handleLeave() {
    await handleForfeitGame()
  }

  // ── 로그아웃 ──────────────────────────────────────────
  async function handleLogout() {
    catanStore.clearGame()
    await catanHub.stopConnection().catch(() => {})
    onLogout()
  }

  // ── "새 게임" — 대기실로 전환 (방장만, 게임 종료 모달에서) — SEQ=10076 흐름 변경 ─────
  async function handleRestartGame() {
    if (restartPending) return
    setRestartPending(true)
    try {
      // RestartGame invoke — 성공 시 그룹 전체가 RoomRestarted 수신 → onRoomRestartedEvent → onRoomRestarted
      // 방이 WAITING 으로 전환되고 클라는 대기실(Room) 화면으로 이동. 게임은 방장이 별도 "게임 시작".
      await roomHub.restartGame(roomCode)
      // 본인도 RoomRestarted 브로드캐스트로 대기실 전환되므로 여기서 별도 페이지 전환 없음.
    } catch (e) {
      setRestartPending(false)
      setActionMsg('새 게임 준비에 실패했습니다. 잠시 후 다시 시도해 주세요.')
    }
  }

  // ── 클릭 핸들러 ───────────────────────────────────────
  const myUserCode = authState.userCode
  // 본인이 관전자인지 — roomStore 멤버 기준 (SEQ=10072 STEP B)
  //   관전자는 게임 액션 UI 숨김 + "관전 중" 배지. 서버는 RejectIfObserver 로 액션 강제 차단.
  const isObserver = roomStore.getMembers().find(m => m.userCode === myUserCode)?.seatType === 'OBSERVER'
  // 관전자는 어떤 경우에도 "내 턴"이 아님 (CTN_GAME_PLAYER 에 없음)
  const isMyTurn   = !isObserver && game?.currentTurnUserCode === myUserCode
  // SEQ=10228 B1 — 게임 종료 후 재시작 버튼은 플레이어 누구나 노출(방장 한정 해제).
  //   기존 isRoomHost(방장 판정)는 더 이상 노출 조건으로 쓰지 않아 제거.

  async function handleVertexClick(vertexId: number) {
    if (!isMyTurn) return
    const mid = game?.matchId ?? matchId ?? 0
    if (!mid) return
    // SEQ=10124 P8-CK-H — ActionMode 우선 분기
    if (progressMode) {
      const dispatched = await dispatchProgressVertexClick(vertexId)
      if (dispatched) return
    }
    const phase = game?.currentPhase
    try {
      if (phase === 'PLACE_SETTLEMENT') {
        await catanHub.placeInitialSettlement(mid, vertexId)
        return
      }
      if (phase === 'MAIN_TURN_POSTROLL') {
        if (buildMode === 'BUILD_SETTLEMENT') {
          await catanHub.buildSettlement(mid, vertexId)
          setBuildMode(null)  // 빌드 후 모드 해제
          return
        }
        if (buildMode === 'BUILD_CITY') {
          await catanHub.buildCity(mid, vertexId)
          setBuildMode(null)
          return
        }
      }
    } catch {
      setErrorBanner('서버 연결 오류')
      setTimeout(() => setErrorBanner(''), 3000)
    }
  }

  async function handleRollDice() {
    if (!isMyTurn || game?.currentPhase !== 'MAIN_TURN_PREROLL') {
      setActionMsg('지금은 주사위를 굴릴 수 없습니다.')
      setTimeout(() => setActionMsg(''), 2000)
      return
    }
    const mid = game?.matchId ?? matchId ?? 0
    if (!mid) return
    setDiceRolling(true)
    try {
      await catanHub.rollDice(mid)
    } catch {
      setErrorBanner('서버 연결 오류')
      setDiceRolling(false)
      setTimeout(() => setErrorBanner(''), 3000)
    }
  }

  // ── Phase 4-4 턴 종료 ────────────────────────────────────
  async function handleEndTurn() {
    if (!isMyTurn || game?.currentPhase !== 'MAIN_TURN_POSTROLL') {
      setActionMsg('지금은 턴을 종료할 수 없습니다.')
      setTimeout(() => setActionMsg(''), 2000)
      return
    }
    const mid = game?.matchId ?? matchId ?? 0
    if (!mid) return
    try {
      await catanHub.endTurn(mid)
    } catch {
      setErrorBanner('서버 연결 오류')
      setTimeout(() => setErrorBanner(''), 3000)
    }
  }

  // ── Phase 4-2 카드 폐기 처리 ─────────────────────────────
  async function handleConfirmDiscard() {
    const total = (discardCounts.WOOL ?? 0) + (discardCounts.WHEAT ?? 0)
                + (discardCounts.BRICK ?? 0) + (discardCounts.ORE ?? 0) + (discardCounts.WOOD ?? 0)
    if (total !== myDiscardRequired) {
      setErrorBanner(`정확히 ${myDiscardRequired}장을 폐기해야 합니다 (현재 ${total}장)`)
      setTimeout(() => setErrorBanner(''), 3000)
      return
    }
    const mid = game?.matchId ?? matchId ?? 0
    if (!mid) return
    try {
      await catanHub.discardCards(mid,
        discardCounts.WOOL ?? 0, discardCounts.WHEAT ?? 0,
        discardCounts.BRICK ?? 0, discardCounts.ORE ?? 0, discardCounts.WOOD ?? 0)
    } catch {
      setErrorBanner('서버 연결 오류')
      setTimeout(() => setErrorBanner(''), 3000)
    }
  }

  // ── Phase 4-2 도적 이동 헥스 클릭 ────────────────────────
  async function handleHexClick(hexId: number) {
    const mid = game?.matchId ?? matchId ?? 0
    if (!mid) return

    // SEQ=10124 P8-CK-H — ActionMode 우선 분기
    if (progressMode) {
      const dispatched = await dispatchProgressHexClick(hexId)
      if (dispatched) return
    }

    // KNIGHT 카드 사용 중 도적 hex 선택 모드 우선
    if (useCardModal?.cardType === 'KNIGHT' && knightStep === 'pick_hex' && isMyTurn) {
      // 현재 도적이 있는 hex는 제외
      if (hexes.find(h => h.hasRobber)?.hexId === hexId) return
      setKnightHexId(hexId)
      setKnightStep('pick_victim')
      return
    }

    if (!isMyTurn || game?.currentPhase !== 'MAIN_TURN_MOVE_ROBBER') return

    // 도둑 hex의 6 vertex 점유자 중 본인 제외 + 자원 ≥1 보유자만 victim 후보 (2026-05-06 fix)
    // 카탄 표준 룰: "도둑이 위치한 타일 인접 플레이어"
    const hexVertexIds = new Set<number>(getHexVertices(hexId))
    const candidateUsers = vertices
      .filter(v => hexVertexIds.has(v.vertexId))
      .filter(v => v.ownerUserCode != null && v.ownerUserCode !== authState.userCode)
      .map(v => v.ownerUserCode!) as number[]
    const uniqueCandidates = Array.from(new Set(candidateUsers))
    // 자원 ≥1 보유자 필터 (resCounts 합계 기준 — Hub ResourceCountUpdated 동기화됨)
    const uniqueVictims = uniqueCandidates.filter(uc => (resCounts[uc] ?? 0) > 0)

    if (uniqueVictims.length === 0) {
      // 약탈 대상 없음 → 즉시 도적만 이동
      try {
        await catanHub.moveRobber(mid, hexId, null)
      } catch {
        setErrorBanner('서버 연결 오류')
        setTimeout(() => setErrorBanner(''), 3000)
      }
    } else if (uniqueVictims.length === 1) {
      // Track B (D-3, 2026-05-06) — 인접 victim 1명이면 자동 약탈 (모달 미표시)
      try {
        await catanHub.moveRobber(mid, hexId, uniqueVictims[0])
      } catch {
        setErrorBanner('서버 연결 오류')
        setTimeout(() => setErrorBanner(''), 3000)
      }
    } else {
      setVictimChoice({ hexId, victims: uniqueVictims })
    }
  }

  async function handleConfirmVictim(victimUc: number | null) {
    if (!victimChoice) return
    const mid = game?.matchId ?? matchId ?? 0
    if (!mid) return
    try {
      await catanHub.moveRobber(mid, victimChoice.hexId, victimUc)
    } catch {
      setErrorBanner('서버 연결 오류')
      setTimeout(() => setErrorBanner(''), 3000)
    }
  }

  // ── SEQ=10124 P8-CK-H — 진보카드 ActionMode dispatch 헬퍼 ─────────
  //   각 dispatch 함수는 boolean 반환 (true=처리, false=fallback).
  //   commitProgressInvoke 는 progressMode 닫기 + invoke + 실패 시 ActionMode rollback.
  async function commitProgressInvoke(cardId: number, payload: object | null) {
    const mid = game?.matchId ?? matchId ?? 0
    if (!mid) return
    setProgressBusy(true)
    try {
      await catanHub.useProgressCard(mid, cardId, payload ? JSON.stringify(payload) : null)
      setProgressMode(null)
      setActionMsg('')
    } catch (err) {
      // 서버 검증 실패 → ActionMode rollback (사용자가 재시도 또는 취소)
      setErrorBanner(`진보카드 사용 오류: ${(err as Error).message ?? ''}`)
      setTimeout(() => setErrorBanner(''), 3000)
      // progressMode 유지 — 사용자가 ESC 또는 모달 취소로 종료
    } finally {
      setProgressBusy(false)
    }
  }

  async function dispatchProgressHexClick(hexId: number): Promise<boolean> {
    if (!progressMode) return false
    if (progressMode.kind === 'POL_BISHOP_PICK_HEX') {
      // 인접 vertex 점유자(자원 ≥1 보유) 사전 집계
      const hexVids = new Set<number>(getHexVertices(hexId))
      const cands = vertices
        .filter(v => hexVids.has(v.vertexId))
        .filter(v => v.ownerUserCode != null && v.ownerUserCode !== authState.userCode)
        .map(v => v.ownerUserCode!) as number[]
      const victims = Array.from(new Set(cands)).filter(uc => (resCounts[uc] ?? 0) > 0)
      setProgressMode({ kind: 'POL_BISHOP_CONFIRM', cardId: progressMode.cardId, hexId, victims })
      return true
    }
    if (progressMode.kind === 'SCI_INVENTOR') {
      const hex = hexes.find(h => h.hexId === hexId)
      if (!hex) return true
      if (!hex.numberToken || hex.numberToken === 2 || hex.numberToken === 12) {
        setErrorBanner('2/12 hex는 교환할 수 없습니다.')
        setTimeout(() => setErrorBanner(''), 2500)
        return true
      }
      if (progressMode.step === 'pick_a') {
        setProgressMode({ ...progressMode, step: 'pick_b', hexA: hexId })
        setActionMsg('발명가: 교환할 hex B를 클릭하세요.')
        return true
      }
      if (progressMode.step === 'pick_b') {
        if (hexId === progressMode.hexA) {
          setErrorBanner('같은 hex는 선택할 수 없습니다.')
          setTimeout(() => setErrorBanner(''), 2500)
          return true
        }
        setProgressMode({ ...progressMode, step: 'confirm', hexB: hexId })
        return true
      }
    }
    if (progressMode.kind === 'TRD_MERCHANT') {
      await commitProgressInvoke(progressMode.cardId, { target_hex_id: hexId })
      return true
    }
    return false
  }

  async function dispatchProgressVertexClick(vertexId: number): Promise<boolean> {
    if (!progressMode) return false
    if (progressMode.kind === 'POL_DESERTER' && progressMode.step === 'pick_vertex') {
      if (!progressMode.candidateVertexIds.includes(vertexId)) {
        setErrorBanner('본인 도로 인접 빈 vertex만 선택 가능합니다.')
        setTimeout(() => setErrorBanner(''), 2500)
        return true
      }
      const kid = progressMode.pickedKnightId
      if (!kid) return true
      await commitProgressInvoke(progressMode.cardId, {
        target_knight_id: kid, target_vertex_id: vertexId,
      })
      return true
    }
    if (progressMode.kind === 'POL_INTRIGUE' && progressMode.step === 'pick_to_vertex') {
      if (!progressMode.candidateVertexIds.includes(vertexId)) {
        setErrorBanner('본인 도로 인접 vertex만 선택 가능합니다.')
        setTimeout(() => setErrorBanner(''), 2500)
        return true
      }
      const kid = progressMode.pickedKnightId
      if (!kid) return true
      await commitProgressInvoke(progressMode.cardId, {
        target_knight_id: kid, to_vertex_id: vertexId,
      })
      return true
    }
    return false
  }

  async function dispatchProgressEdgeClick(edgeId: number): Promise<boolean> {
    if (!progressMode) return false
    if (progressMode.kind === 'POL_DIPLOMAT') {
      if (!progressMode.candidateEdgeIds.includes(edgeId)) {
        setErrorBanner('미연결 도로(상대)만 제거 가능합니다.')
        setTimeout(() => setErrorBanner(''), 2500)
        return true
      }
      await commitProgressInvoke(progressMode.cardId, { target_edge_id: edgeId })
      return true
    }
    if (progressMode.kind === 'SCI_PIONEERING') {
      if (!progressMode.candidateEdgeIds.includes(edgeId)) {
        setErrorBanner('빈 edge만 선택 가능합니다.')
        setTimeout(() => setErrorBanner(''), 2500)
        return true
      }
      await commitProgressInvoke(progressMode.cardId, { edge_id: edgeId })
      return true
    }
    return false
  }

  // ── SEQ=10125 P8-CK-H2 (a) — 기사 클릭 통합 (DESERTER / INTRIGUE 단계 1) ─────
  //   CatanBoard.tsx 의 onKnightClick 콜백이 본 함수를 호출.
  //   DESERTER  pick_knight  → 본인 도로 인접 빈 vertex 후보 집계 → step=pick_vertex
  //   INTRIGUE  pick_knight  → 본인 도로 인접 vertex(점유 무관) 후보 집계 → step=pick_to_vertex
  //   양 분기 모두 클라 사전 검증: 후보가 0개면 ActionError + ActionMode 유지(사용자 선택 가능).
  function handleKnightClick(knightId: number, ownerUserCode: number) {
    if (!progressMode) return
    // 본인 기사는 CatanBoard 측에서 listening=false 처리되지만 안전망
    if (ownerUserCode === myUserCode) {
      setErrorBanner('본인 기사는 선택할 수 없습니다.')
      setTimeout(() => setErrorBanner(''), 2500)
      return
    }
    if (progressMode.kind === 'POL_DESERTER' && progressMode.step === 'pick_knight') {
      // 후보 vertex = 본인 도로 인접 vertex 중 OWNER=NULL & knight 미점유
      const myRoadEdges = edges.filter(e => e.ownerUserCode === myUserCode && e.buildingType === 'ROAD')
      const candVertexSet = new Set<number>()
      for (const e of myRoadEdges) {
        const vs = getEdgeVertices(e.edgeId)
        for (const v of vs) candVertexSet.add(v)
      }
      const knightVertices = new Set<number>((knights ?? []).map(k => k.vertexId))
      const candidate = Array.from(candVertexSet).filter(v => {
        const vert = vertices.find(x => x.vertexId === v)
        return vert && vert.ownerUserCode == null && !knightVertices.has(v)
      })
      if (candidate.length === 0) {
        setErrorBanner('신설 가능한 본인 도로 인접 빈 vertex가 없습니다.')
        setTimeout(() => setErrorBanner(''), 3000)
        return
      }
      setProgressMode({
        kind: 'POL_DESERTER',
        cardId: progressMode.cardId,
        step: 'pick_vertex',
        pickedKnightId: knightId,
        candidateVertexIds: candidate,
      })
      setActionMsg('탈주병: 신설할 본인 도로 인접 빈 vertex를 클릭하세요.')
      return
    }
    if (progressMode.kind === 'POL_INTRIGUE' && progressMode.step === 'pick_knight') {
      // 후보 vertex = 본인 도로 인접 vertex(점유 무관) — 강제 이동 대상 (knight 미점유)
      const myRoadEdges = edges.filter(e => e.ownerUserCode === myUserCode && e.buildingType === 'ROAD')
      const candVertexSet = new Set<number>()
      for (const e of myRoadEdges) {
        const vs = getEdgeVertices(e.edgeId)
        for (const v of vs) candVertexSet.add(v)
      }
      const knightVertices = new Set<number>(
        (knights ?? []).filter(k => k.knightId !== knightId).map(k => k.vertexId)
      )
      const candidate = Array.from(candVertexSet).filter(v => !knightVertices.has(v))
      if (candidate.length === 0) {
        setErrorBanner('강제 이동 가능한 본인 도로 인접 vertex가 없습니다.')
        setTimeout(() => setErrorBanner(''), 3000)
        return
      }
      setProgressMode({
        kind: 'POL_INTRIGUE',
        cardId: progressMode.cardId,
        step: 'pick_to_vertex',
        pickedKnightId: knightId,
        candidateVertexIds: candidate,
      })
      setActionMsg('음모: 강제 이동할 본인 도로 인접 vertex를 클릭하세요.')
      return
    }
  }

  async function handleEdgeClick(edgeId: number) {
    const mid = game?.matchId ?? matchId ?? 0
    if (!mid) return

    // SEQ=10124 P8-CK-H — ActionMode 우선 분기
    if (progressMode) {
      const dispatched = await dispatchProgressEdgeClick(edgeId)
      if (dispatched) return
    }

    // ROAD_BUILDING 카드 사용 중: edge 2개 선택
    if (useCardModal?.cardType === 'ROAD_BUILDING' && isMyTurn) {
      if (roadBuildEdges.includes(edgeId)) {
        setErrorBanner('같은 edge는 선택할 수 없습니다.')
        setTimeout(() => setErrorBanner(''), 2500)
        return
      }
      const next = [...roadBuildEdges, edgeId]
      setRoadBuildEdges(next)
      if (next.length === 2) {
        try {
          await catanHub.useRoadBuilding(mid, useCardModal.cardId, next[0], next[1])
          setUseCardModal(null)
          setRoadBuildEdges([])
        } catch {
          setErrorBanner('서버 연결 오류')
          setTimeout(() => setErrorBanner(''), 3000)
          setRoadBuildEdges([])
          setUseCardModal(null)
        }
      }
      return
    }

    if (!isMyTurn) return
    const phase = game?.currentPhase
    try {
      if (phase === 'PLACE_ROAD') {
        await catanHub.placeInitialRoad(mid, edgeId)
        return
      }
      if (phase === 'MAIN_TURN_POSTROLL' && buildMode === 'BUILD_ROAD') {
        await catanHub.buildRoad(mid, edgeId)
        setBuildMode(null)
        return
      }
    } catch {
      setErrorBanner('서버 연결 오류')
      setTimeout(() => setErrorBanner(''), 3000)
    }
  }

  // ── 턴 안내 헤더 텍스트 ───────────────────────────────
  function turnGuideText(): string {
    if (!game) return ''
    const phase = game.currentPhase
    if (isMyTurn) {
      if (phase === 'PLACE_SETTLEMENT')    return '내 차례: 마을을 놓을 곳을 클릭하세요'
      if (phase === 'PLACE_ROAD')          return '내 차례: 도로를 놓을 곳을 클릭하세요'
      if (phase === 'MAIN_TURN_PREROLL')   return '내 차례: 주사위를 굴리세요'
      if (phase === 'MAIN_TURN_POSTROLL') {
        if (buildMode === 'BUILD_ROAD')       return '도로 건설: edge를 클릭하세요'
        if (buildMode === 'BUILD_SETTLEMENT') return '마을 건설: vertex를 클릭하세요'
        if (buildMode === 'BUILD_CITY')       return '도시(성) 업그레이드: 본인 마을 vertex를 클릭하세요'
        return '내 차례: 건설/거래/턴 종료'
      }
      if (phase === 'MAIN_TURN_DISCARD')   return myDiscardRequired > 0 ? `카드 ${myDiscardRequired}장 폐기 필요` : '다른 플레이어 폐기 대기'
      if (phase === 'MAIN_TURN_MOVE_ROBBER') return '내 차례: 도적을 이동시킬 헥스를 클릭하세요'
      if (game.status === 'MAIN_TURN')     return '내 차례 (본 턴)'
      return '내 차례'
    }
    const turnPlayer = players.find(p => p.userCode === game.currentTurnUserCode)
    const name = turnPlayer?.nickname ?? '상대방'
    if (phase === 'PLACE_SETTLEMENT')    return `${name} 차례 — 마을 놓는 중`
    if (phase === 'PLACE_ROAD')          return `${name} 차례 — 도로 놓는 중`
    if (phase === 'MAIN_TURN_PREROLL')   return `${name} 차례 — 주사위 굴리는 중`
    if (phase === 'MAIN_TURN_POSTROLL')  return `${name} 차례 — 행동 선택 중`
    if (game.status === 'MAIN_TURN')     return `${name} 차례 (본 턴)`
    return `${name} 차례`
  }

  // ── 플레이어 패널 ──────────────────────────────────────
  const playerPanel = (
    <div className="play-player-panel">
      <h4 className="panel-title">플레이어</h4>
      {players.length === 0 ? (
        <p className="muted" style={{ fontSize: 13 }}>로딩 중…</p>
      ) : (
        <ul className="play-player-list">
          {players
            .slice()
            .sort((a, b) => a.seatIndex - b.seatIndex)
            .map(p => {
              const color = PLAYER_COLOR[p.color] ?? '#ccc'
              const isMe  = p.userCode === myUserCode
              const isCurrent = game?.currentTurnUserCode === p.userCode
              // Track C-1 좌석 펄스 — 본인이면 my, 상대면 other (color CSS variable로 구분)
              const isPulsing = seatPulseUserCode === p.userCode
              const pulseClass = isPulsing ? (isMe ? ' pulse-me' : ' pulse-other') : ''
              return (
                <li
                  key={p.userCode}
                  className={`play-player-item${isMe ? ' play-player-me' : ''}${isCurrent ? ' play-player-turn' : ''}${pulseClass}`}
                  style={isPulsing && !isMe ? { ['--pulse-color' as string]: color } : undefined}
                >
                  <span
                    className="play-player-color"
                    style={{ background: color, border: p.color === 'WHITE' ? '1px solid #666' : 'none' }}
                  />
                  <span className="play-player-name">
                    {p.nickname}
                    {isMe && <span className="play-you-badge"> (나)</span>}
                  </span>
                  {!isMe && (
                    <span className="play-player-res-count">
                      {resCounts[p.userCode] ?? 0}장
                    </span>
                  )}
                </li>
              )
            })}
        </ul>
      )}
    </div>
  )

  // ── 주사위/턴 패널 (Phase 4-1, 4-2, 4-3, 4-4) ─────────────
  // Phase 4-3 건설 후보 사전 산출 (도시 가능 여부 판단)
  const hasMySettlement = vertices.some(v => v.ownerUserCode === myUserCode && v.buildingType === 'SETTLEMENT')

  function selectBuildMode(m: Exclude<BuildMode, null>) {
    if (!isMyTurn || game?.currentPhase !== 'MAIN_TURN_POSTROLL') return
    if (!canAfford(myResources, BUILD_COST[m])) {
      setErrorBanner(buildErrorMsg(m))
      setTimeout(() => setErrorBanner(''), 2500)
      return
    }
    if (m === 'BUILD_CITY' && !hasMySettlement) {
      setErrorBanner('도시(성) 업그레이드할 본인 마을이 없습니다.')
      setTimeout(() => setErrorBanner(''), 2500)
      return
    }
    setBuildMode(prev => prev === m ? null : m)
  }

  function buildErrorMsg(m: 'BUILD_ROAD' | 'BUILD_SETTLEMENT' | 'BUILD_CITY'): string {
    if (m === 'BUILD_ROAD')       return '자원 부족 (도로: 나무 1 + 벽돌 1)'
    if (m === 'BUILD_SETTLEMENT') return '자원 부족 (마을: 나무 1 + 벽돌 1 + 양 1 + 곡식 1)'
    return '자원 부족 (도시(성): 광물 3 + 곡식 2)'
  }

  // ── 건설 항목별 메타 (라벨·비용·활성 조건) ─────────────────
  const BUILD_ITEMS: Array<{
    mode: 'BUILD_ROAD' | 'BUILD_SETTLEMENT' | 'BUILD_CITY'
    label: string
    cost: Partial<Record<string, number>>
    extraDisabled: boolean
    extraDisabledMsg?: string
    activeColor: string
  }> = [
    {
      mode: 'BUILD_ROAD',
      label: '도로',
      cost: BUILD_COST.BUILD_ROAD,
      extraDisabled: false,
      activeColor: '#1976D2',
    },
    {
      mode: 'BUILD_SETTLEMENT',
      label: '마을',
      cost: BUILD_COST.BUILD_SETTLEMENT,
      extraDisabled: false,
      activeColor: '#1976D2',
    },
    {
      mode: 'BUILD_CITY',
      label: '도시(성)',
      cost: BUILD_COST.BUILD_CITY,
      extraDisabled: !hasMySettlement,
      extraDisabledMsg: '도시(성) 업그레이드할 본인 마을이 없습니다.',
      activeColor: '#FFB300',
    },
  ]

  // 한글 비용 보조 라벨 (2026-05-06: 라벨 + emoji 이중 표기, 1줄 row 구조)
  function buildCostKoreanText(cost: Partial<Record<string, number>>): string {
    return COST_DISPLAY_ORDER
      .filter(rt => (cost[rt] ?? 0) > 0)
      .map(rt => {
        const n = cost[rt] ?? 0
        const lbl = RESOURCE_LABEL[rt].label
        return n === 1 ? lbl : `${lbl}×${n}`
      })
      .join('+')
  }

  const dicePanel = game?.status === 'MAIN_TURN' ? (
    <div className="play-dice-panel">
      {/* 주사위 버튼은 턴 종료 직상으로 이동 (Track D, 2026-05-06) */}
      {/* Phase 4-3 건설 액션 패널 — 상시 표시 (본인 턴/상대 턴 무관 열람 가능) ─────
          클릭 가능 여부는 isMyTurn + MAIN_TURN_POSTROLL + canAfford 로 결정.
          상대 턴이거나 PostRoll 외 페이즈에서는 비용·라벨만 보이고 disabled 처리. */}
      <div className="play-build-panel">
        <div className="play-build-panel-title">
          건설
          {!(isMyTurn && game.currentPhase === 'MAIN_TURN_POSTROLL') && (
            <span className="play-build-panel-hint">
              {' '}— {isMyTurn ? '주사위 굴린 후 가능' : '상대 차례 (열람만)'}
            </span>
          )}
        </div>
        <div className="play-build-buttons">
          {BUILD_ITEMS.map(item => {
            const afford       = canAfford(myResources, item.cost)
            const canBuildNow  = isMyTurn && game.currentPhase === 'MAIN_TURN_POSTROLL'
                                 && afford && !item.extraDisabled
            const isActive     = buildMode === item.mode
            return (
              <button
                key={item.mode}
                type="button"
                className={`btn-build${isActive ? ' active' : ''}${!afford ? ' lack' : ''}`}
                onClick={() => {
                  if (!isMyTurn || game.currentPhase !== 'MAIN_TURN_POSTROLL') return
                  if (item.extraDisabled) {
                    setErrorBanner(item.extraDisabledMsg ?? '')
                    setTimeout(() => setErrorBanner(''), 2500)
                    return
                  }
                  selectBuildMode(item.mode)
                }}
                disabled={!canBuildNow}
                title={`${item.label} 비용: ${
                  COST_DISPLAY_ORDER.filter(rt => (item.cost[rt] ?? 0) > 0)
                    .map(rt => `${RESOURCE_LABEL[rt].label} ${item.cost[rt]}`)
                    .join(' + ')
                }`}
                style={isActive ? { background: item.activeColor, color: '#fff' } : undefined}
              >
                <span className="btn-build-label">
                  {item.label}
                  <span className="btn-build-cost-text">({buildCostKoreanText(item.cost)})</span>
                </span>
                <CostIcons cost={item.cost} my={myResources} />
              </button>
            )
          })}
          {/* 발전 카드 구매 — 짓기 메뉴 4번째 (2026-05-06: 거래 박스 → 건설 박스 이동, 카탄 룰 정합) */}
          {(() => {
            const buyCost: Partial<Record<string, number>> = { WOOL: 1, WHEAT: 1, ORE: 1 }
            const afford = canAfford(myResources, buyCost)
            const canBuy = isMyTurn && game.currentPhase === 'MAIN_TURN_POSTROLL'
                          && devDeckRemaining > 0 && afford
            const titleSuffix = devDeckRemaining > 0 ? ` (덱 ${devDeckRemaining}장)` : ' (덱 소진)'
            return (
              <button
                type="button"
                className={`btn-build dev-card${!afford ? ' lack' : ''}`}
                onClick={handleBuyDevCard}
                disabled={!canBuy}
                title={`발전 카드 구매: 양 1 + 곡식 1 + 광물 1${titleSuffix}`}
              >
                <span className="btn-build-label">
                  발전카드
                  <span className="btn-build-cost-text">({buildCostKoreanText(buyCost)})</span>
                </span>
                <CostIcons cost={buyCost} my={myResources} />
              </button>
            )
          })()}
          {buildMode != null && (
            <button
              type="button"
              className="btn-build-cancel"
              onClick={() => setBuildMode(null)}
            >
              취소 (ESC)
            </button>
          )}
        </div>
        {/* 빌드 모드 안내 */}
        {isMyTurn && game.currentPhase === 'MAIN_TURN_POSTROLL' && buildMode === 'BUILD_ROAD' && (
          <div className="play-build-hint">본인색 점선 edge를 클릭해 도로 건설</div>
        )}
        {isMyTurn && game.currentPhase === 'MAIN_TURN_POSTROLL' && buildMode === 'BUILD_SETTLEMENT' && (
          <div className="play-build-hint">파란 원 vertex를 클릭해 마을 건설</div>
        )}
        {isMyTurn && game.currentPhase === 'MAIN_TURN_POSTROLL' && buildMode === 'BUILD_CITY' && (
          <div className="play-build-hint city">금색 원 vertex(본인 마을)를 클릭해 도시(성) 업그레이드</div>
        )}
      </div>

      {/* SEQ=10112 P8-CK-A — EXT_CK 도시 개선 패널 (EXT_CK ON 룸에서만 노출) */}
      {game.isExtCk && (() => {
        const me = players.find(p => p.userCode === authState.userCode)
        if (!me) return null
        const fields: Array<{ key: 'POL' | 'SCI' | 'TRD'; cur: number }> = [
          { key: 'POL', cur: me.cityImprovePol ?? 0 },
          { key: 'SCI', cur: me.cityImproveSci ?? 0 },
          { key: 'TRD', cur: me.cityImproveTrd ?? 0 },
        ]
        const canImproveNow = isMyTurn && game.currentPhase === 'MAIN_TURN_POSTROLL'
        return (
          <div className="play-build-panel" style={{ marginTop: '8px', borderTop: '1px solid #ddd', paddingTop: '8px' }}>
            <div className="play-build-panel-title">
              도시 개선 (EXT_CK)
              {!canImproveNow && (
                <span className="play-build-panel-hint">
                  {' '}— {isMyTurn ? '주사위 굴린 후 가능' : '상대 차례 (열람만)'}
                </span>
              )}
            </div>
            <div className="play-build-buttons">
              {fields.map(f => {
                const meta = CK_FIELD_LABEL[f.key]
                const nextLevel = f.cur + 1
                const cost = nextLevel
                const have = myResources[meta.commodity] ?? 0
                const maxedOut = f.cur >= 5
                const afford = have >= cost
                const enabled = canImproveNow && !maxedOut && afford
                const onClick = async () => {
                  const mid = game?.matchId ?? matchId ?? 0
                  if (!mid) return
                  try {
                    await catanHub.improveCity(mid, f.key)
                  } catch (err) {
                    setErrorBanner(`서버 연결 오류: ${(err as Error).message ?? ''}`)
                    setTimeout(() => setErrorBanner(''), 3000)
                  }
                }
                const titleSuffix = maxedOut
                  ? ' (최대 단계 도달)'
                  : (afford ? '' : ` — ${meta.commodityLabel} ${cost - have}개 부족`)
                const labelText = maxedOut
                  ? `${meta.label} ★5 (메트로폴리스)`
                  : `${meta.label} ${f.cur}→${nextLevel} (${meta.commodityLabel}×${cost})`
                return (
                  <button
                    key={f.key}
                    type="button"
                    className={`btn-build${!enabled ? ' lack' : ''}`}
                    onClick={enabled ? onClick : undefined}
                    disabled={!enabled}
                    title={`도시 개선 ${meta.label}${titleSuffix}`}
                  >
                    <span className="btn-build-label">
                      {labelText}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        )
      })()}

      {/* SEQ=10113 P8-CK-B — EXT_CK 기사 패널 (EXT_CK ON 룸 전용) */}
      {game.isExtCk && (() => {
        const myUC = authState.userCode
        const myKnights = knights.filter(k => k.userCode === myUC)
        const myWool  = myResources['WOOL']  ?? 0
        const myOre   = myResources['ORE']   ?? 0
        const myWheat = myResources['WHEAT'] ?? 0
        const onBuildKnightClick = () => {
          const mid = game?.matchId ?? matchId ?? 0
          if (!mid) return
          const v = window.prompt('기사를 건설할 vertex 번호 (1~54, 본인 도로 인접 + 미점유):')
          if (!v) return
          const vertexId = parseInt(v, 10)
          if (Number.isNaN(vertexId)) return
          void catanHub.buildKnight(mid, vertexId).catch(err => {
            setErrorBanner(`서버 연결 오류: ${(err as Error).message ?? ''}`)
            setTimeout(() => setErrorBanner(''), 3000)
          })
        }
        const onUpgradeClick = (kid: number) => {
          const mid = game?.matchId ?? matchId ?? 0
          if (!mid) return
          void catanHub.upgradeKnight(mid, kid).catch(err => {
            setErrorBanner(`서버 연결 오류: ${(err as Error).message ?? ''}`)
            setTimeout(() => setErrorBanner(''), 3000)
          })
        }
        const onActivateClick = (kid: number) => {
          const mid = game?.matchId ?? matchId ?? 0
          if (!mid) return
          void catanHub.activateKnight(mid, kid).catch(err => {
            setErrorBanner(`서버 연결 오류: ${(err as Error).message ?? ''}`)
            setTimeout(() => setErrorBanner(''), 3000)
          })
        }
        const onMoveClick = (kid: number) => {
          const mid = game?.matchId ?? matchId ?? 0
          if (!mid) return
          const v = window.prompt('이동할 vertex 번호 (1~54, 도로 1칸 인접):')
          if (!v) return
          const newV = parseInt(v, 10)
          const e = window.prompt('경로 edge_id (1~72, 본인 도로):')
          if (!e) return
          const pathE = parseInt(e, 10)
          if (Number.isNaN(newV) || Number.isNaN(pathE)) return
          void catanHub.moveKnight(mid, kid, newV, pathE).catch(err => {
            setErrorBanner(`서버 연결 오류: ${(err as Error).message ?? ''}`)
            setTimeout(() => setErrorBanner(''), 3000)
          })
        }
        const onChaseRobberClick = (kid: number) => {
          const mid = game?.matchId ?? matchId ?? 0
          if (!mid) return
          const k = knights.find(x => x.knightId === kid)
          if (!k) return
          const h = window.prompt('도적을 옮길 hex_id (현재 도적 위치와 다른 hex, 1~19):')
          if (!h) return
          const newH = parseInt(h, 10)
          if (Number.isNaN(newH)) return
          void catanHub.chaseRobber(mid, kid, k.vertexId, newH).catch(err => {
            setErrorBanner(`서버 연결 오류: ${(err as Error).message ?? ''}`)
            setTimeout(() => setErrorBanner(''), 3000)
          })
        }
        return (
          <KnightPanel
            myKnights={myKnights}
            isMyTurn={isMyTurn && game.currentPhase === 'MAIN_TURN_POSTROLL'}
            myWool={myWool} myOre={myOre} myWheat={myWheat}
            onBuildKnightClick={onBuildKnightClick}
            onUpgradeClick={onUpgradeClick}
            onActivateClick={onActivateClick}
            onMoveClick={onMoveClick}
            onChaseRobberClick={onChaseRobberClick}
          />
        )
      })()}

      {/* SEQ=10115 P8-CK-D — 진보 카드 패널 (EXT_CK 룸만) */}
      {game.isExtCk && (() => {
        const mid = game.matchId ?? matchId ?? 0
        const myUc = authState.userCode
        const onUseCardClick = (cardId: number, cardType: string) => {
          if (!mid) return
          // SEQ=10124 P8-CK-H — 9 UX 카드는 ActionMode 진입 (보드 클릭/모달 dispatch)
          //   기존 buildMode + useCardModal 잠금 (다른 액션 차단).
          if (cardType === 'POL_BISHOP') {
            setBuildMode(null); setUseCardModal(null)
            setProgressMode({ kind: 'POL_BISHOP_PICK_HEX', cardId })
            setActionMsg('주교: 도적을 이동시킬 hex를 클릭하세요.')
            return
          }
          if (cardType === 'POL_DESERTER') {
            setBuildMode(null); setUseCardModal(null)
            // 후보 vertex: 본인 도로 인접 + 빈 vertex (단순 사전 집계)
            const myEdges = edges.filter(e => e.ownerUserCode === myUc)
            const candidate = new Set<number>()
            for (const e of myEdges) {
              for (const vid of getEdgeVertices(e.edgeId)) {
                const v = vertices.find(vv => vv.vertexId === vid)
                if (v && v.ownerUserCode == null) candidate.add(vid)
              }
            }
            setProgressMode({
              kind: 'POL_DESERTER', cardId, step: 'pick_knight',
              candidateVertexIds: Array.from(candidate),
            })
            return
          }
          if (cardType === 'POL_DIPLOMAT') {
            setBuildMode(null); setUseCardModal(null)
            // 후보: OWNER!=null && OWNER!=본인 && 양끝 vertex 어디에도 점유 없음
            const occupiedVids = new Set(vertices.filter(v => v.ownerUserCode != null).map(v => v.vertexId))
            const candidate = edges
              .filter(e => e.ownerUserCode != null && e.ownerUserCode !== myUc)
              .filter(e => {
                const ends = getEdgeVertices(e.edgeId)
                for (const vid of ends) if (occupiedVids.has(vid)) return false
                return true
              })
              .map(e => e.edgeId)
            setProgressMode({ kind: 'POL_DIPLOMAT', cardId, candidateEdgeIds: candidate })
            return
          }
          if (cardType === 'POL_INTRIGUE') {
            setBuildMode(null); setUseCardModal(null)
            const myEdges = edges.filter(e => e.ownerUserCode === myUc)
            const candidate = new Set<number>()
            for (const e of myEdges) {
              for (const vid of getEdgeVertices(e.edgeId)) {
                candidate.add(vid)
              }
            }
            setProgressMode({
              kind: 'POL_INTRIGUE', cardId, step: 'pick_knight',
              candidateVertexIds: Array.from(candidate),
            })
            return
          }
          if (cardType === 'POL_SPY') {
            setBuildMode(null); setUseCardModal(null)
            setProgressMode({ kind: 'POL_SPY', cardId, step: 'pick_player' })
            return
          }
          if (cardType === 'SCI_INVENTOR') {
            setBuildMode(null); setUseCardModal(null)
            setProgressMode({ kind: 'SCI_INVENTOR', cardId, step: 'pick_a' })
            setActionMsg('발명가: 교환할 hex A를 클릭하세요 (2/12 제외).')
            return
          }
          if (cardType === 'SCI_PIONEERING') {
            setBuildMode(null); setUseCardModal(null)
            // 후보: OWNER NULL edge (도로 인접 면제, 본 SEQ STEP=2 단순화 — 서버 SP 재검증 follow-up)
            const candidate = edges.filter(e => e.ownerUserCode == null).map(e => e.edgeId)
            setProgressMode({ kind: 'SCI_PIONEERING', cardId, candidateEdgeIds: candidate })
            return
          }
          if (cardType === 'TRD_COMMERCIAL_HARBOR') {
            setBuildMode(null); setUseCardModal(null)
            setProgressMode({
              kind: 'TRD_COMMERCIAL_HARBOR', cardId,
              myCommodity: '', theirResource: '',
            })
            return
          }
          if (cardType === 'TRD_MERCHANT') {
            setBuildMode(null); setUseCardModal(null)
            // 후보 hex: 본인 마을/도시 인접 hex (vertex의 hexAdj 매핑 단순화 → 서버 재검증)
            // STEP=2 단순화: 모든 hex 후보로 두고 클라 highlight는 제한 없이, 서버 SP 재검증.
            const candidate = hexes.map(h => h.hexId)
            setProgressMode({ kind: 'TRD_MERCHANT', cardId, candidateHexIds: candidate })
            return
          }
          // 기존 분기 (TODO P8-CK-E 효과 모달 — 9 UX 카드 외)
          let payload: string | null = null
          if (['POL_WEDDING','TRD_MASTER_MERCHANT','TRD_TRADE_MONOPOLY','TRD_RESOURCE_MONOPOLY','TRD_CUSTOMS_HOUSE'].includes(cardType)) {
            const t = window.prompt(`${cardType} 사용 — 대상 user_code 또는 자원 종류 (예: 99102 또는 WOOL). 빈값=서버 기본:`)
            if (t) payload = JSON.stringify({ target: t })
          }
          void catanHub.useProgressCard(mid, cardId, payload).catch(err => {
            setErrorBanner(`진보 카드 사용 오류: ${(err as Error).message ?? ''}`)
            setTimeout(() => setErrorBanner(''), 3000)
          })
        }
        const isMyCardTurn = isMyTurn  // 카드 사용은 자기 차례 전체 시점 허용 (공식 EXT_CK)
        return (
          <>
            <ProgressCardPanel
              myCards={myProgressCards}
              isMyTurn={isMyCardTurn}
              onUseClick={onUseCardClick}
            />
            <OpponentCollection
              myUserCode={myUc}
              players={players}
              permanentCards={permanentCards}
              getProgressCardCount={(uc) => catanStore.getOtherProgressCardCount(uc)}
            />
            {/* 진보 카드 추첨 버튼 (디버그용 — 자기 차례 + 도시개선 1+ 보유 분야만) */}
            {(() => {
              const me = players.find(p => p.userCode === myUc)
              if (!me) return null
              const mid2 = game.matchId ?? matchId ?? 0
              const onDrawClick = (cat: 'POL' | 'SCI' | 'TRD') => {
                if (!mid2) return
                void catanHub.drawProgressCard(mid2, cat).catch(err => {
                  setErrorBanner(`진보 카드 추첨 오류: ${(err as Error).message ?? ''}`)
                  setTimeout(() => setErrorBanner(''), 3000)
                })
              }
              return (
                <div style={{ border: '1px solid #888', padding: 8, marginTop: 8 }}>
                  <h4 style={{ margin: '0 0 6px 0' }}>진보 카드 추첨 (디버그)</h4>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                    실제 추첨은 빨간 다이 이벤트로 자동 발생 (TODO P8-CK-E). 현재는 수동 버튼.
                  </div>
                  <button disabled={!isMyTurn || (me.cityImprovePol ?? 0) < 1}
                          onClick={() => onDrawClick('POL')} style={{ marginRight: 4 }}>POL 추첨</button>
                  <button disabled={!isMyTurn || (me.cityImproveSci ?? 0) < 1}
                          onClick={() => onDrawClick('SCI')} style={{ marginRight: 4 }}>SCI 추첨</button>
                  <button disabled={!isMyTurn || (me.cityImproveTrd ?? 0) < 1}
                          onClick={() => onDrawClick('TRD')}>TRD 추첨</button>
                </div>
              )
            })()}
          </>
        )
      })()}

      {/* SEQ=10114 P8-CK-C — 야만인 트랙 + 도시 성벽 패널 (EXT_CK 룸만) */}
      {game.isExtCk && (() => {
        const pos = catanStore.getBarbarianPosition()
        const imminent = pos >= 6
        const myBrick = catanStore.getMyResources()['BRICK'] ?? 0
        const myCities = catanStore.getVertices().filter(v => v.ownerUserCode === authState.userCode && v.buildingType === 'CITY')
        const myWalls  = catanStore.getCityWalls().filter(w => w.userCode === authState.userCode)
        const wallVids = new Set(myWalls.map(w => w.vertexId))
        const wallEligibleCities = myCities.filter(c => !wallVids.has(c.vertexId))
        const canBuildWall = isMyTurn && game.currentPhase === 'MAIN_TURN_POSTROLL' && myBrick >= 2 && wallEligibleCities.length > 0
        const lastInv = catanStore.getLastInvasion()
        const onBuildWallClick = () => {
          if (wallEligibleCities.length === 0) {
            setErrorBanner('성벽을 지을 수 있는 도시가 없습니다 (도시 + 성벽 미보유 필요)')
            setTimeout(() => setErrorBanner(''), 3000)
            return
          }
          const list = wallEligibleCities.map(c => c.vertexId).join(', ')
          const v = window.prompt(`성벽을 건설할 도시 vertex_id (BRICK 2 차감):\n${list}`)
          if (!v) return
          const vid = parseInt(v, 10)
          if (Number.isNaN(vid)) return
          const mid = catanStore.getGame()?.matchId
          if (!mid) return
          void catanHub.buildCityWall(mid, vid).catch(err => {
            setErrorBanner(`서버 연결 오류: ${(err as Error).message ?? ''}`)
            setTimeout(() => setErrorBanner(''), 3000)
          })
        }
        return (
          <div style={{ marginTop: 8, padding: 8, border: '1px solid #b00', borderRadius: 4, background: imminent ? '#ffd0d0' : '#fafafa' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 'bold', color: imminent ? '#900' : '#444' }}>
                야만인 트랙 {pos}/7 {imminent && '⚠️ 임박'}
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>
                내 활성 기사 합산: {catanStore.getMyKnightStrengthSum(authState.userCode)}
              </div>
            </div>
            <div style={{ marginTop: 4, height: 8, background: '#eee', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${(pos / 7) * 100}%`, height: '100%', background: imminent ? '#c00' : '#888' }} />
            </div>
            <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                disabled={!canBuildWall}
                onClick={onBuildWallClick}
                style={{
                  padding: '4px 10px',
                  background: canBuildWall ? '#fff' : '#eee',
                  cursor: canBuildWall ? 'pointer' : 'not-allowed',
                  border: '1px solid #999',
                  borderRadius: 3,
                }}
              >
                🧱 도시 성벽 건설 (BRICK 2)
              </button>
              <span style={{ fontSize: 12, color: '#666' }}>
                내 성벽 {myWalls.length}개 · 도시 {myCities.length}개 · BRICK {myBrick}
              </span>
            </div>
            {lastInv && (
              <div style={{ marginTop: 6, padding: 6, background: lastInv.outcome === 'WIN' ? '#e0ffe0' : '#ffe0e0', borderRadius: 3, fontSize: 12 }}>
                <div style={{ fontWeight: 'bold' }}>
                  최근 침공 #{lastInv.invasionNo} — {lastInv.outcome === 'WIN' ? '승리' : '패배'}
                </div>
                <div>방어 합산 {lastInv.strengthSum} vs 야만인 {lastInv.barbarians}</div>
                {lastInv.outcome === 'WIN' && lastInv.defenderUserCodes.length > 0 && (
                  <div>
                    최강 방어자:{' '}
                    {lastInv.defenderUserCodes
                      .map(uc => catanStore.getPlayers().find(p => p.userCode === uc)?.nickname ?? `#${uc}`)
                      .join(', ')}{' '}
                    (TODO: Defender 카드 발급 P8-CK-D)
                  </div>
                )}
                {lastInv.outcome === 'LOSE' && lastInv.loserUserCodes.length > 0 && (
                  <div>
                    최약 방어자:{' '}
                    {lastInv.loserUserCodes
                      .map(uc => catanStore.getPlayers().find(p => p.userCode === uc)?.nickname ?? `#${uc}`)
                      .join(', ')}{' '}
                    — 도시 {lastInv.loserVertexIds.join(', ')} → 마을
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })()}

      {/* 주사위 굴리기 — 건설 다음 / 거래 앞 (2026-05-06 v7 사용자 요청 재배치) */}
      {(() => {
        const canRoll = isMyTurn && game.currentPhase === 'MAIN_TURN_PREROLL' && !diceRolling
        const hint = (() => {
          if (!isMyTurn) return '상대 차례'
          if (game.currentPhase === 'MAIN_TURN_PREROLL') return ''
          if (game.currentPhase === 'MAIN_TURN_POSTROLL') return '주사위 이미 굴림'
          if (game.currentPhase === 'MAIN_TURN_DISCARD') return '카드 폐기 단계'
          if (game.currentPhase === 'MAIN_TURN_MOVE_ROBBER') return '도적 이동 단계'
          return '주사위 불가'
        })()
        return (
          <button
            className="btn-roll-dice"
            onClick={handleRollDice}
            disabled={!canRoll}
            title={canRoll ? '주사위를 굴립니다' : `주사위 굴림 불가 — ${hint}`}
            style={{ marginTop: 8 }}
          >
            {diceRolling
              ? '굴리는 중…'
              : (canRoll ? '🎲 주사위 굴리기' : `🎲 주사위 굴리기${hint ? ` (${hint})` : ''}`)}
          </button>
        )
      })()}
      {/* 마지막 주사위 결과 (주사위 버튼 직하단) */}
      {lastDice && (
        <div className="play-dice-result" style={{ marginTop: 6 }}>
          <span className="dice-face">{diceFace(lastDice.dice1)}</span>
          <span className="dice-face">{diceFace(lastDice.dice2)}</span>
          <span className="dice-total"> = {lastDice.total}</span>
          {lastDice.total === 7 && (
            <span className="dice-seven-label"> 도적!</span>
          )}
        </div>
      )}
      {/* 턴 종료 버튼 — 주사위 결과 직하단 (2026-05-06 v9 사용자 요청 재배치) */}
      {isMyTurn && game.currentPhase === 'MAIN_TURN_POSTROLL' && (
        <button
          className="btn-end-turn"
          onClick={handleEndTurn}
          style={{ marginTop: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer' }}
        >
          턴 종료 ▶
        </button>
      )}
      {/* Phase 5 거래 액션 — 상시 표시 (POSTROLL 외 disable) */}
      <div className="play-trade-panel">
        <div className="play-trade-title">
          거래
          {!canTrade && (
            <span className="play-trade-hint">
              {' '}— {isMyTurn ? '주사위 굴린 후 가능' : '상대 차례'}
            </span>
          )}
        </div>
        <div className="play-trade-buttons">
          {/* 1줄 1기능 (2026-05-06: inline-wrap → column, 220px 컬럼 안정화) */}
          <button
            type="button"
            className="btn-trade"
            onClick={openProposeModal}
            disabled={!canTrade}
            title="다른 플레이어와 자원 거래 제안"
          >
            <span className="btn-trade-label">플레이어 거래</span>
            <span className="btn-trade-sub">자원 ↔ 자원 (제안)</span>
          </button>
          <button
            type="button"
            className="btn-trade"
            onClick={openBankModal}
            disabled={!canTrade}
            title="은행 4:1 (자기 자원 4장 → 다른 자원 1장)"
          >
            <span className="btn-trade-label">은행 거래</span>
            <span className="btn-trade-sub">4 : 1</span>
          </button>
          <button
            type="button"
            className="btn-trade"
            disabled={!canTrade || myPortIds.length === 0}
            title={myPortIds.length === 0 ? '본인 권한 항구 없음' : '항구 거래 (본인 권한 항구 선택)'}
            onClick={() => {
              if (myPortIds.length === 0) {
                setErrorBanner('본인 권한 항구가 없습니다 (항구 vertex에 마을/도시(성) 필요)')
                setTimeout(() => setErrorBanner(''), 3000)
                return
              }
              if (myPortIds.length === 1) {
                openPortModal(myPortIds[0])
              } else {
                // 다수 항구 — 첫 항구 자동 진입 (모달에서 항구 변경 가능)
                openPortModal(myPortIds[0])
              }
            }}
          >
            <span className="btn-trade-label">항구 거래</span>
            <span className="btn-trade-sub">
              {myPortIds.length === 0 ? '본인 권한 항구 없음' : `보유 항구 ${myPortIds.length}곳 (2:1 또는 3:1)`}
            </span>
          </button>
        </div>
        {/* 본인 OPEN 진행 중 거래 배너 */}
        {(() => {
          const myOpen = catanStore.getMyOpenProposal(myUserCode)
          if (!myOpen) return null
          const giveCounts = jsonToCounts(myOpen.giveJson)
          const getCounts = jsonToCounts(myOpen.getJson)
          // 응답 대상 플레이어 목록 (본인 제외, 특정 대상 지정 시 해당 1명만)
          const targetPlayers = players.filter(p =>
            p.userCode !== myUserCode &&
            (myOpen.targetUserCode == null || p.userCode === myOpen.targetUserCode)
          )
          const responses = catanStore.getTradeResponses(myOpen.tradeId)
          return (
            <div className="play-trade-my-banner">
              <div className="trade-banner-top">
                <span>내 제안 진행 중:</span>
                <span className="trade-give">
                  주: {Object.entries(giveCounts).map(([k,v]) => `${RESOURCE_LABEL[k]?.label ?? k} ${v}`).join('·') || '-'}
                </span>
                <span className="trade-arrow">↔</span>
                <span className="trade-get">
                  받: {Object.entries(getCounts).map(([k,v]) => `${RESOURCE_LABEL[k]?.label ?? k} ${v}`).join('·') || '-'}
                </span>
                <button type="button" className="btn-trade-cancel" onClick={handleCancelMyOpen}>취소</button>
              </div>
              {targetPlayers.length > 0 && (
                <div className="trade-response-status-row">
                  {targetPlayers.map(p => {
                    const action = responses[p.userCode] ?? null
                    const statusCls = action === 'ACCEPT' ? 'trs-accept'
                      : action === 'REJECT' ? 'trs-reject'
                      : action === 'COUNTER' ? 'trs-counter'
                      : 'trs-pending'
                    const statusIcon = action === 'ACCEPT' ? '✓'
                      : action === 'REJECT' ? '✗'
                      : action === 'COUNTER' ? '↩'
                      : '⏳'
                    const statusLabel = action === 'ACCEPT' ? '수락'
                      : action === 'REJECT' ? '거부'
                      : action === 'COUNTER' ? '카운터'
                      : '대기'
                    return (
                      <span key={p.userCode} className={`trade-response-chip ${statusCls}`}>
                        <span className="trc-dot" style={{ background: p.color }}></span>
                        <span className="trc-nick">{p.nickname}</span>
                        <span className="trc-icon">{statusIcon}</span>
                        <span className="trc-label">{statusLabel}</span>
                      </span>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })()}
      </div>
      {/* 도적 이동 안내 (Phase 4-2) — 2026-05-06 v9: 가독성 강화 (B안)
            어두운 부모 패널(play-resource-panel #16213e) 안에서 #ccd6f6 흰빛 색 상속 차단.
            밝은 #fff8e1 배경 + 진한 #1a1a2e 검은 텍스트 + fontWeight 700, 승점 패널과 톤 일관. */}
      {isMyTurn && game.currentPhase === 'MAIN_TURN_MOVE_ROBBER' && (
        <div className="play-robber-prompt" style={{
          marginTop: 8, padding: '10px 12px',
          background: '#fff8e1', border: '1px solid #FB8C00', borderRadius: 6,
          fontSize: 13, fontWeight: 700, color: '#1a1a2e',
        }}>
          🦹 도적을 이동시킬 헥스를 클릭하세요 (현재 위치 외)
        </div>
      )}
    </div>
  ) : null

  // ── 폐기 모달 (Phase 4-2) ───────────────────────────────
  const discardModal = discardOpen && myDiscardRequired > 0 ? (
    <div className="play-modal-overlay" style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div className="play-modal" style={{ background: '#fff', padding: 24, borderRadius: 8, minWidth: 320, maxWidth: '90vw' }}>
        <h3 style={{ margin: 0, marginBottom: 12 }}>카드 폐기 (도적)</h3>
        <p style={{ marginBottom: 16, fontSize: 14, color: '#555' }}>
          정확히 <strong>{myDiscardRequired}장</strong>을 폐기해야 합니다.
        </p>
        {RESOURCE_TYPES.map(rt => {
          const have   = myResources[rt] ?? 0
          const picked = discardCounts[rt] ?? 0
          const { label, img } = RESOURCE_LABEL[rt]
          return (
            <div key={rt} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ width: 100, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <img src={img} alt={label} style={{ width: 22, height: 22, objectFit: 'contain' }} />
                {label} ({have})
              </span>
              <button onClick={() => setDiscardCounts(p => ({ ...p, [rt]: Math.max(0, (p[rt] ?? 0) - 1) }))} disabled={picked <= 0}>−</button>
              <span style={{ width: 24, textAlign: 'center' }}>{picked}</span>
              <button onClick={() => setDiscardCounts(p => ({ ...p, [rt]: Math.min(have, (p[rt] ?? 0) + 1) }))} disabled={picked >= have}>+</button>
            </div>
          )
        })}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, fontSize: 14 }}>
          <span>합계: <strong>{Object.values(discardCounts).reduce((s, v) => s + v, 0)}</strong> / {myDiscardRequired}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button
            onClick={handleConfirmDiscard}
            disabled={Object.values(discardCounts).reduce((s, v) => s + v, 0) !== myDiscardRequired}
            style={{ padding: '8px 16px', fontWeight: 600 }}
          >
            폐기 확정
          </button>
        </div>
      </div>
    </div>
  ) : null

  // ── 약탈 대상 선택 모달 (Phase 4-2) ───────────────────────
  const victimModal = victimChoice ? (
    <div className="play-modal-overlay" style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div className="play-modal" style={{ background: '#fff', padding: 24, borderRadius: 8, minWidth: 280 }}>
        <h3 style={{ margin: 0, marginBottom: 12 }}>약탈 대상 선택</h3>
        <p style={{ marginBottom: 16, fontSize: 14, color: '#555' }}>
          누구에게서 자원 1장을 약탈하시겠습니까?
        </p>
        {victimChoice.victims.map(uc => {
          const p = players.find(pl => pl.userCode === uc)
          return (
            <button
              key={uc}
              onClick={() => handleConfirmVictim(uc)}
              style={{ display: 'block', width: '100%', padding: 8, marginBottom: 8, textAlign: 'left' }}
            >
              {p?.nickname ?? `사용자 #${uc}`} ({resCounts[uc] ?? 0}장 보유)
            </button>
          )
        })}
        <button
          onClick={() => handleConfirmVictim(null)}
          style={{ display: 'block', width: '100%', padding: 8, marginTop: 8, color: '#888' }}
        >
          약탈 안 함 (도적만 이동)
        </button>
      </div>
    </div>
  ) : null

  // ── Phase 6 발전 카드 액션 ────────────────────────────────

  /** 발전 카드 구매 */
  async function handleBuyDevCard() {
    if (!isMyTurn || game?.currentPhase !== 'MAIN_TURN_POSTROLL') {
      setErrorBanner('주사위를 굴린 후 본인 턴에만 구매 가능')
      setTimeout(() => setErrorBanner(''), 2500)
      return
    }
    const cost = { WOOL: 1, WHEAT: 1, ORE: 1 }
    if (!canAfford(myResources, cost)) {
      setErrorBanner('자원 부족 (양 1 + 곡식 1 + 광물 1)')
      setTimeout(() => setErrorBanner(''), 2500)
      return
    }
    if (devDeckRemaining <= 0) {
      setErrorBanner('덱이 모두 소진되었습니다.')
      setTimeout(() => setErrorBanner(''), 2500)
      return
    }
    const mid = game?.matchId ?? matchId ?? 0
    if (!mid) return
    try {
      await catanHub.buyDevCard(mid)
    } catch {
      setErrorBanner('서버 연결 오류')
      setTimeout(() => setErrorBanner(''), 3000)
    }
  }

  /** 카드 사용 모달 열기 */
  function openUseCardModal(card: MyDevCard) {
    if (!isMyTurn) {
      setErrorBanner('본인 턴에만 사용 가능')
      setTimeout(() => setErrorBanner(''), 2500)
      return
    }
    if (card.used) {
      setErrorBanner('이미 사용한 카드입니다.')
      setTimeout(() => setErrorBanner(''), 2500)
      return
    }
    // 같은 턴 구입 카드 사용 금지 (카탄 표준 룰) — 서버 검증이 권위, UX는 안내만
    // game?.currentPhase 검증 (VICTORY_POINT 외 POSTROLL)
    if (card.cardType !== 'VICTORY_POINT' && game?.currentPhase !== 'MAIN_TURN_POSTROLL') {
      setErrorBanner('주사위를 굴린 후에 사용할 수 있습니다.')
      setTimeout(() => setErrorBanner(''), 2500)
      return
    }
    setUseCardModal({ cardId: card.cardId, cardType: card.cardType })
    setKnightStep('pick_hex')
    setKnightHexId(null)
    setRoadBuildEdges([])
    setMonopolyResource('')
    setYopResources([])
  }

  /** 카드 사용 확정 (모달 → invoke) */
  async function confirmUseCard() {
    if (!useCardModal) return
    const mid = game?.matchId ?? matchId ?? 0
    if (!mid) return
    const { cardId, cardType } = useCardModal
    try {
      switch (cardType) {
        case 'KNIGHT':
          if (knightHexId == null) return
          // victim 선택은 별도 버튼에서 처리
          break
        case 'MONOPOLY':
          if (!monopolyResource) {
            setErrorBanner('자원 종류를 선택하세요.')
            setTimeout(() => setErrorBanner(''), 2500)
            return
          }
          await catanHub.useMonopoly(mid, cardId, monopolyResource)
          setUseCardModal(null)
          break
        case 'YEAR_OF_PLENTY':
          if (yopResources.length !== 2) {
            setErrorBanner('자원 2개를 선택하세요.')
            setTimeout(() => setErrorBanner(''), 2500)
            return
          }
          await catanHub.useYearOfPlenty(mid, cardId, yopResources[0], yopResources[1])
          setUseCardModal(null)
          break
        case 'VICTORY_POINT':
          await catanHub.useVictoryPoint(mid, cardId)
          setUseCardModal(null)
          break
        case 'ROAD_BUILDING':
          // edge 클릭으로 처리 (handleEdgeClick에서 invoke)
          break
      }
    } catch {
      setErrorBanner('서버 연결 오류')
      setTimeout(() => setErrorBanner(''), 3000)
    }
  }

  /** KNIGHT 약탈 victim 선택 후 invoke */
  async function confirmUseKnight(victimUc: number | null) {
    if (!useCardModal || useCardModal.cardType !== 'KNIGHT' || knightHexId == null) return
    const mid = game?.matchId ?? matchId ?? 0
    if (!mid) return
    try {
      await catanHub.useKnight(mid, useCardModal.cardId, knightHexId, victimUc)
      setUseCardModal(null)
      setKnightHexId(null)
      setKnightStep('pick_hex')
    } catch {
      setErrorBanner('서버 연결 오류')
      setTimeout(() => setErrorBanner(''), 3000)
    }
  }

  // 발전 카드 종류별 한글 라벨
  const DEV_CARD_LABEL: Record<DevCardType, string> = {
    KNIGHT:         '기사',
    ROAD_BUILDING:  '도로 건설',
    MONOPOLY:       '독점',
    YEAR_OF_PLENTY: '풍년',
    VICTORY_POINT:  '승점',
  }

  // 보유 카드 5종별 카운트 (사용 가능 + 사용 완료 분리)
  const myActiveCardsByType: Record<DevCardType, MyDevCard[]> = {
    KNIGHT: [], ROAD_BUILDING: [], MONOPOLY: [], YEAR_OF_PLENTY: [], VICTORY_POINT: [],
  }
  const myUsedCardsByType: Record<DevCardType, MyDevCard[]> = {
    KNIGHT: [], ROAD_BUILDING: [], MONOPOLY: [], YEAR_OF_PLENTY: [], VICTORY_POINT: [],
  }
  for (const c of myDevCards) {
    if (c.used) myUsedCardsByType[c.cardType].push(c)
    else        myActiveCardsByType[c.cardType].push(c)
  }

  // ── 발전 카드 패널 ───────────────────────────────────────
  // 발전카드 이미지 매핑 (2026-05-06 v8): 5종 모두 이미지 적용 완성
  //   KNIGHT → knight.png / ROAD_BUILDING → road.png / MONOPOLY → monopoly.png /
  //   YEAR_OF_PLENTY → find.png / VICTORY_POINT → win_point_1~5.png cycle (cardId 기반)
  function devCardImg(ct: DevCardType, cardId: number): string {
    if (ct === 'KNIGHT') return '/assets/catan/devcard/knight.png'
    if (ct === 'ROAD_BUILDING') return '/assets/catan/devcard/road.png'
    if (ct === 'MONOPOLY') return '/assets/catan/devcard/monopoly.png'
    if (ct === 'YEAR_OF_PLENTY') return '/assets/catan/devcard/find.png'
    // VICTORY_POINT
    const idx = ((cardId % 5) + 5) % 5 + 1   // 1~5 cycle (음수 cardId 안전망)
    return `/assets/catan/devcard/win_point_${idx}.png`
  }

  // ── 발전카드 패널 (A안: 단일 행 가로 펼침, 자원 카드와 동일 시각 패턴) ──
  // 활성 카드를 type 정렬 후 carousel(marginLeft -26 겹침). 같은 종류 같은 이미지, VP는 cardId cycle.
  // 클릭 = 사용 트리거(VP 제외). 사용된 카드는 우측에 회색 stack.
  const devCardPanel = game?.status === 'MAIN_TURN' ? (() => {
    // 활성 카드 정렬 (type 그룹 + cardId 안정 정렬, 시각 일관)
    const TYPE_ORDER: DevCardType[] = ['KNIGHT', 'ROAD_BUILDING', 'MONOPOLY', 'YEAR_OF_PLENTY', 'VICTORY_POINT']
    const sortedActive = [...myDevCards.filter(c => !c.used)].sort((a, b) => {
      const ai = TYPE_ORDER.indexOf(a.cardType)
      const bi = TYPE_ORDER.indexOf(b.cardType)
      if (ai !== bi) return ai - bi
      return a.cardId - b.cardId
    })
    const sortedUsed = [...myDevCards.filter(c => c.used)].sort((a, b) => {
      const ai = TYPE_ORDER.indexOf(a.cardType)
      const bi = TYPE_ORDER.indexOf(b.cardType)
      if (ai !== bi) return ai - bi
      return a.cardId - b.cardId
    })
    const titleMap: Record<DevCardType, string> = {
      KNIGHT:         '기사 카드 사용 (도적 이동 + 약탈)',
      ROAD_BUILDING:  '도로 건설 카드 사용 (edge 2개 무료)',
      VICTORY_POINT:  '승점 카드 (1점 자동 반영)',
      YEAR_OF_PLENTY: '풍년 카드 사용 (자원 2장 자유 선택)',
      MONOPOLY:       '독점 카드 사용 (자원 1종 약탈)',
    }
    return (
      <div className="play-devcard-panel">
        <h4 className="panel-title play-devcard-title">
          발전 카드 (덱 {devDeckRemaining}장)
        </h4>
        {/* 활성 카드 가로 펼침 (자원 카드와 동일 패턴, marginLeft -26) */}
        <div className="play-devcard-stack">
          {sortedActive.length === 0 && sortedUsed.length === 0 && (
            <span className="play-devcard-empty">보유 카드 없음</span>
          )}
          {sortedActive.map((c, i) => {
            const img = devCardImg(c.cardType, c.cardId)
            const clickable = c.cardType !== 'VICTORY_POINT' && isMyTurn
            return (
              <button
                key={c.cardId}
                type="button"
                className={`play-devcard-card${clickable ? ' clickable' : ' display-only'}`}
                onClick={clickable ? () => openUseCardModal(c) : undefined}
                onMouseEnter={() => setHoveredDevCard({ cardId: c.cardId, cardType: c.cardType })}
                onMouseLeave={() => setHoveredDevCard(null)}
                disabled={!clickable && c.cardType !== 'VICTORY_POINT'}
                title={titleMap[c.cardType]}
                style={{ marginLeft: i === 0 ? 0 : -10 }}
              >
                <img src={img} alt={DEV_CARD_LABEL[c.cardType]} />
              </button>
            )
          })}
          {/* 사용된 카드 stack — active 카드 우측에 회색 톤 */}
          {sortedUsed.map((c, i) => {
            const img = devCardImg(c.cardType, c.cardId)
            return (
              <span
                key={c.cardId}
                className="play-devcard-card used"
                title={`사용된 ${DEV_CARD_LABEL[c.cardType]}`}
                onMouseEnter={() => setHoveredDevCard({ cardId: c.cardId, cardType: c.cardType })}
                onMouseLeave={() => setHoveredDevCard(null)}
                style={{ marginLeft: (i === 0 && sortedActive.length === 0) ? 0 : -10 }}
              >
                <img src={img} alt="used" />
              </span>
            )
          })}
        </div>
        {largestArmyUserCode != null && (
          <div className="play-devcard-largest-army">
            🛡️ 기사단 승점: {players.find(p => p.userCode === largestArmyUserCode)?.nickname ?? `#${largestArmyUserCode}`}
          </div>
        )}
      </div>
    )
  })() : null

  // ── 단계 5 (Phase 7) 승점 패널 ─────────────────────────
  // 2026-05-06 v8: A안 텍스트 색 강화 — 밝은 배경 위 검은/진한 톤 명시 (1번 컬럼 어두운 부모로부터 색 상속 차단)
  const scorePanel = (
    <div className="play-score-panel" style={{
      marginTop: 12, padding: 8, background: '#fff8e1', borderRadius: 6, border: '1px solid #ffd54f',
      color: '#1a1a2e'   // 패널 전체 기본 텍스트 진한 톤
    }}>
      <h4 className="panel-title" style={{ margin: '0 0 6px 0', fontSize: 14, color: '#1a1a2e', fontWeight: 700 }}>
        승점 (목표 10점)
      </h4>
      {/* 본인 총점 (VP 카드 포함) */}
      {myVictoryPoints && (
        <div style={{ fontSize: 13, marginBottom: 6, padding: 6, background: '#fff', borderRadius: 4, border: '1px solid #ffe082' }}>
          <div style={{ fontWeight: 700, color: '#bf360c', fontSize: 14 }}>
            본인: {myVictoryPoints.totalPoints}점
          </div>
          <div style={{ fontSize: 11, color: '#3a3a3a', marginTop: 2, fontWeight: 500 }}>
            마을 {myVictoryPoints.settlementPts} + 도시(성) {myVictoryPoints.cityPts}
            {myVictoryPoints.vpCardPts > 0 && ` + 승점카드 ${myVictoryPoints.vpCardPts}`}
            {myVictoryPoints.longestRoadPts > 0 && ' + 가장긴도로 2'}
            {myVictoryPoints.largestArmyPts > 0 && ' + 기사단 승점 2'}
          </div>
        </div>
      )}
      {/* 모든 플레이어 가시 점수 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, color: '#1a1a2e' }}>
        {allScores.length === 0 && <span style={{ color: '#5a5a5a' }}>점수 데이터 동기화 중...</span>}
        {allScores.map(s => {
          const player = players.find(p => p.userCode === s.userCode)
          if (!player) return null
          const isMe = s.userCode === myUserCode
          const dispPts = isMe && myVictoryPoints ? myVictoryPoints.totalPoints : s.visiblePoints
          return (
            <div key={s.userCode} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontWeight: isMe ? 700 : 600,
              color: '#1a1a2e',
            }}>
              <span style={{
                display: 'inline-block', width: 12, height: 12, borderRadius: '50%',
                background: PLAYER_COLOR[player.color] ?? '#888',
                border: '1px solid #444',
              }}></span>
              <span style={{ flex: 1, color: '#1a1a2e' }}>{player.nickname}{isMe && ' (나)'}</span>
              <span style={{ color: '#1a1a2e', fontWeight: 700 }}>{dispPts}점{!isMe && '(공개)'}</span>
              {s.isLongestRoadHolder && <span title="가장 긴 도로" style={{ color: '#0d47a1', fontWeight: 700 }}>🛣️+2</span>}
              {s.isLargestArmyHolder && <span title="기사단 승점" style={{ color: '#b71c1c', fontWeight: 700 }}>🛡️+2</span>}
            </div>
          )
        })}
      </div>
      {longestRoadUserCode != null && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#0d47a1', fontWeight: 600 }}>
          🛣️ 가장 긴 도로: {players.find(p => p.userCode === longestRoadUserCode)?.nickname ?? `#${longestRoadUserCode}`}
        </div>
      )}
    </div>
  )

  // ── 단계 5: 게임 종료 모달 ─────────────────────────────
  const gameEndedModalEl = gameEnded ? (
    <div className="play-modal-overlay" style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000,
    }}>
      <div className="play-modal" style={{
        background: '#fff', padding: 32, borderRadius: 12, minWidth: 320, maxWidth: '92vw',
        textAlign: 'center', border: '3px solid #ffb300'
      }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>🏆</div>
        <h2 style={{ margin: 0, color: '#e65100' }}>게임 종료</h2>
        <div style={{ fontSize: 18, marginTop: 16, fontWeight: 600 }}>
          우승: {players.find(p => p.userCode === gameEnded.winnerUserCode)?.nickname ?? `#${gameEnded.winnerUserCode}`}
        </div>
        <div style={{ fontSize: 14, marginTop: 6, color: '#666' }}>
          최종 {gameEnded.winnerPoints}점 도달
        </div>
        {/* SEQ=10228 B1 — 게임 종료 후에는 방의 누구나 새 게임 준비 가능 (방장 한정 해제). 관전자는 제외. */}
        <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
          {!isObserver ? (
            <button
              type="button"
              onClick={handleRestartGame}
              disabled={restartPending}
              style={{
                padding: '10px 28px', fontSize: 15, fontWeight: 700,
                background: restartPending ? '#9e9e9e' : '#2e7d32', color: '#fff',
                border: 'none', borderRadius: 4, cursor: restartPending ? 'default' : 'pointer'
              }}
            >
              {restartPending ? '대기실로 이동 중…' : '🔄 같은 방에서 새 게임'}
            </button>
          ) : (
            <div style={{ fontSize: 13, color: '#888' }}>
              플레이어가 새 게임을 준비하면 대기실로 이동합니다.
            </div>
          )}
          <button
            type="button"
            onClick={handleLeave}
            style={{
              padding: '8px 24px', fontSize: 14, fontWeight: 600,
              background: '#1976D2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer'
            }}
          >
            로비로 나가기
          </button>
        </div>
      </div>
    </div>
  ) : null

  // ── Phase 5 거래 제안 모달 (제안자) ─────────────────────────
  const proposeModalEl = proposeOpen ? (
    <div className="play-modal-overlay">
      <div className="play-modal play-trade-modal">
        <h3 className="trade-modal-title">거래 제안</h3>
        <div className="trade-modal-section">
          <div className="trade-modal-section-title">주는 자원 (내 자원)</div>
          {RESOURCE_TYPES.map(rt => {
            const have = myResources[rt] ?? 0
            const cnt  = proposeGiveCounts[rt] ?? 0
            const lbl  = RESOURCE_LABEL[rt]
            return (
              <div key={rt} className="trade-res-row">
                <img src={lbl.img} alt={lbl.label} className="trade-res-icon" />
                <span className="trade-res-label">{lbl.label} ({have})</span>
                <button type="button" className="trade-res-btn" disabled={cnt <= 0}
                  onClick={() => setProposeGiveCounts(p => ({ ...p, [rt]: Math.max(0, (p[rt] ?? 0) - 1) }))}>−</button>
                <span className="trade-res-count">{cnt}</span>
                <button type="button" className="trade-res-btn" disabled={cnt >= have}
                  onClick={() => setProposeGiveCounts(p => ({ ...p, [rt]: Math.min(have, (p[rt] ?? 0) + 1) }))}>+</button>
              </div>
            )
          })}
        </div>
        <div className="trade-modal-section">
          <div className="trade-modal-section-title">받는 자원</div>
          {RESOURCE_TYPES.map(rt => {
            const cnt = proposeGetCounts[rt] ?? 0
            const lbl = RESOURCE_LABEL[rt]
            return (
              <div key={rt} className="trade-res-row">
                <img src={lbl.img} alt={lbl.label} className="trade-res-icon" />
                <span className="trade-res-label">{lbl.label}</span>
                <button type="button" className="trade-res-btn" disabled={cnt <= 0}
                  onClick={() => setProposeGetCounts(p => ({ ...p, [rt]: Math.max(0, (p[rt] ?? 0) - 1) }))}>−</button>
                <span className="trade-res-count">{cnt}</span>
                <button type="button" className="trade-res-btn"
                  onClick={() => setProposeGetCounts(p => ({ ...p, [rt]: (p[rt] ?? 0) + 1 }))}>+</button>
              </div>
            )
          })}
        </div>
        <div className="trade-modal-section">
          <div className="trade-modal-section-title">대상</div>
          <label className="trade-target-label">
            <input type="radio" name="propose_target" checked={proposeTarget === null}
              onChange={() => setProposeTarget(null)} /> 전체
          </label>
          {players.filter(p => p.userCode !== myUserCode).map(p => (
            <label key={p.userCode} className="trade-target-label">
              <input type="radio" name="propose_target" checked={proposeTarget === p.userCode}
                onChange={() => setProposeTarget(p.userCode)} />
              <span className="trade-target-color" style={{ background: PLAYER_COLOR[p.color] ?? '#888' }}></span>
              {p.nickname}
            </label>
          ))}
        </div>
        <div className="trade-modal-actions">
          <button
            type="button"
            className="btn-trade-submit"
            onClick={handleSubmitPropose}
            disabled={
              Object.values(proposeGiveCounts).reduce((s, v) => s + (v ?? 0), 0) < 1 ||
              Object.values(proposeGetCounts).reduce((s, v) => s + (v ?? 0), 0) < 1
            }
            title="주는 자원과 받는 자원 각각 1장 이상 필요"
          >제안하기</button>
          <button type="button" className="btn-trade-close" onClick={() => setProposeOpen(false)}>취소</button>
        </div>
      </div>
    </div>
  ) : null

  // ── Phase 5 거래 응답 모달 (응답자 자동 표시) ────────────────
  const respondTrade = respondTradeId != null ? openTrades.find(t => t.tradeId === respondTradeId) : null
  const counterAlreadyUsed = respondTradeId != null ? catanStore.hasCounterUsed(respondTradeId) : false
  const respondModalEl = respondTrade ? (
    <div className="play-modal-overlay">
      <div className="play-modal play-trade-modal">
        <h3 className="trade-modal-title">
          {(() => {
            const proposerNick = players.find(p => p.userCode === respondTrade.proposerUserCode)?.nickname ?? `#${respondTrade.proposerUserCode}`
            return `${proposerNick}의 거래 제안`
          })()}
        </h3>
        {!counterMode ? (
          <>
            <div className="trade-modal-section">
              <div className="trade-modal-section-title">받는 것 (제안자가 주는)</div>
              <div className="trade-summary">
                {Object.entries(jsonToCounts(respondTrade.giveJson)).map(([k,v]) => {
                  const lbl = RESOURCE_LABEL[k]
                  const have = myResources[k] ?? 0
                  return (
                    <span key={k} className="trade-summary-item">
                      <img src={lbl?.img ?? ''} alt={lbl?.label ?? k} className="trade-res-icon" />
                      {/* 모바일: "양털 보유:3 거래:1" (받는 것은 부족 개념 없음), PC: "양털(1)" (받을 양만) — 2026-05-26 SEQ=10208 */}
                      <span className="trade-summary-name trade-summary-name-mobile">{lbl?.label ?? k} 보유:{have} 거래:{v as number}</span>
                      <span className="trade-summary-name trade-summary-name-pc">{lbl?.label ?? k}({v as number})</span>
                    </span>
                  )
                })}
              </div>
            </div>
            <div className="trade-modal-section">
              <div className="trade-modal-section-title">주는 것 (내가 주는)</div>
              <div className="trade-summary">
                {Object.entries(jsonToCounts(respondTrade.getJson)).map(([k,v]) => {
                  const lbl = RESOURCE_LABEL[k]
                  const have = myResources[k] ?? 0
                  const lack = have < (v as number)
                  return (
                    <span key={k} className={`trade-summary-item${lack ? ' lack' : ''}`}>
                      <img src={lbl?.img ?? ''} alt={lbl?.label ?? k} className="trade-res-icon" />
                      {/* 모바일: "양털 보유:0 거래:1(부족:1)" (부족 시) / "양털 보유:3 거래:1" (충분 시), PC: "양털(1)" (줄 양만) — 2026-05-26 SEQ=10208, (부족:K) 빨강 강조 SEQ=10210 */}
                      <span className="trade-summary-name trade-summary-name-mobile">{lbl?.label ?? k} 보유:{have} 거래:{v as number}{lack && <span className="trade-lack-inline">(부족:{(v as number) - have})</span>}</span>
                      <span className="trade-summary-name trade-summary-name-pc">{lbl?.label ?? k}({v as number})</span>
                      {lack && <span className="lack-tag">부족({(v as number) - have})</span>}
                    </span>
                  )
                })}
              </div>
            </div>
            <div className="trade-modal-actions">
              <button type="button" className="btn-trade-accept" onClick={() => handleRespondAccept(respondTrade.tradeId)}>수락</button>
              <button type="button" className="btn-trade-reject" onClick={() => handleRespondReject(respondTrade.tradeId)}>거절</button>
              <button type="button" className="btn-trade-counter"
                disabled={counterAlreadyUsed}
                title={counterAlreadyUsed ? '이미 카운터 사용 (1회 한정)' : '카운터 제안'}
                onClick={() => {
                  setCounterMode(true)
                  setCounterGiveCounts({})
                  setCounterGetCounts({})
                }}>카운터</button>
            </div>
          </>
        ) : (
          <>
            <div className="trade-modal-section">
              <div className="trade-modal-section-title">카운터: 내가 주는</div>
              {RESOURCE_TYPES.map(rt => {
                const have = myResources[rt] ?? 0
                const cnt  = counterGiveCounts[rt] ?? 0
                const lbl  = RESOURCE_LABEL[rt]
                return (
                  <div key={rt} className="trade-res-row">
                    <img src={lbl.img} alt={lbl.label} className="trade-res-icon" />
                    <span className="trade-res-label">{lbl.label} ({have})</span>
                    <button type="button" className="trade-res-btn" disabled={cnt <= 0}
                      onClick={() => setCounterGiveCounts(p => ({ ...p, [rt]: Math.max(0, (p[rt] ?? 0) - 1) }))}>−</button>
                    <span className="trade-res-count">{cnt}</span>
                    <button type="button" className="trade-res-btn" disabled={cnt >= have}
                      onClick={() => setCounterGiveCounts(p => ({ ...p, [rt]: Math.min(have, (p[rt] ?? 0) + 1) }))}>+</button>
                  </div>
                )
              })}
            </div>
            <div className="trade-modal-section">
              <div className="trade-modal-section-title">카운터: 내가 받는</div>
              {RESOURCE_TYPES.map(rt => {
                const cnt = counterGetCounts[rt] ?? 0
                const lbl = RESOURCE_LABEL[rt]
                return (
                  <div key={rt} className="trade-res-row">
                    <img src={lbl.img} alt={lbl.label} className="trade-res-icon" />
                    <span className="trade-res-label">{lbl.label}</span>
                    <button type="button" className="trade-res-btn" disabled={cnt <= 0}
                      onClick={() => setCounterGetCounts(p => ({ ...p, [rt]: Math.max(0, (p[rt] ?? 0) - 1) }))}>−</button>
                    <span className="trade-res-count">{cnt}</span>
                    <button type="button" className="trade-res-btn"
                      onClick={() => setCounterGetCounts(p => ({ ...p, [rt]: (p[rt] ?? 0) + 1 }))}>+</button>
                  </div>
                )
              })}
            </div>
            <div className="trade-modal-actions">
              <button type="button" className="btn-trade-submit" onClick={() => handleRespondCounter(respondTrade.tradeId)}>카운터 보내기</button>
              <button type="button" className="btn-trade-close" onClick={() => setCounterMode(false)}>취소</button>
            </div>
          </>
        )}
      </div>
    </div>
  ) : null

  // ── Phase 5 은행 거래 모달 ─────────────────────────────────
  const bankModalEl = bankOpen ? (
    <div className="play-modal-overlay">
      <div className="play-modal play-trade-modal">
        <h3 className="trade-modal-title">은행 거래 (4:1)</h3>
        <div className="trade-modal-section">
          <div className="trade-modal-section-title">주는 자원 4장</div>
          {RESOURCE_TYPES.map(rt => {
            const have = myResources[rt] ?? 0
            const lbl  = RESOURCE_LABEL[rt]
            const disabled = have < 4
            return (
              <label key={rt} className={`trade-radio-row${disabled ? ' disabled' : ''}`}>
                <input type="radio" name="bank_give" checked={bankGiveType === rt}
                  disabled={disabled}
                  onChange={() => setBankGiveType(rt)} />
                <img src={lbl.img} alt={lbl.label} className="trade-res-icon" />
                {lbl.label} ({have}{disabled && ' — 4장 미만'})
              </label>
            )
          })}
        </div>
        <div className="trade-modal-section">
          <div className="trade-modal-section-title">받는 자원 1장</div>
          {RESOURCE_TYPES.map(rt => {
            const lbl = RESOURCE_LABEL[rt]
            const disabled = rt === bankGiveType
            return (
              <label key={rt} className={`trade-radio-row${disabled ? ' disabled' : ''}`}>
                <input type="radio" name="bank_get" checked={bankGetType === rt}
                  disabled={disabled}
                  onChange={() => setBankGetType(rt)} />
                <img src={lbl.img} alt={lbl.label} className="trade-res-icon" />
                {lbl.label}{disabled && ' — 같은 자원 불가'}
              </label>
            )
          })}
        </div>
        <div className="trade-modal-actions">
          <button type="button" className="btn-trade-submit" onClick={handleSubmitBank}
            disabled={!bankGiveType || !bankGetType}>거래 실행</button>
          <button type="button" className="btn-trade-close" onClick={() => setBankOpen(false)}>취소</button>
        </div>
      </div>
    </div>
  ) : null

  // ── Phase 5 항구 거래 모달 ─────────────────────────────────
  const portModalEl = portModalPortId != null ? (() => {
    const port = ports.find(p => p.portId === portModalPortId)
    if (!port) return null
    const ratio = port.ratio
    const isGeneric = port.resourceType == null  // 3:1 generic
    return (
      <div className="play-modal-overlay">
        <div className="play-modal play-trade-modal">
          <h3 className="trade-modal-title">항구 거래 ({ratio}:1{!isGeneric && ` ${RESOURCE_LABEL[port.resourceType!]?.label ?? port.resourceType}`})</h3>
          {/* 본인 권한 항구 다수면 변경 가능 */}
          {myPortIds.length > 1 && (
            <div className="trade-modal-section">
              <div className="trade-modal-section-title">항구 선택</div>
              {myPortIds.map(pid => {
                const p = ports.find(x => x.portId === pid)
                if (!p) return null
                const label = p.resourceType == null
                  ? `항구 #${pid} (3:1 generic)`
                  : `항구 #${pid} (${RESOURCE_LABEL[p.resourceType]?.label ?? p.resourceType} 2:1)`
                return (
                  <label key={pid} className="trade-radio-row">
                    <input type="radio" name="port_select" checked={portModalPortId === pid}
                      onChange={() => {
                        setPortModalPortId(pid)
                        setPortGiveType(p.resourceType ?? '')
                        setPortGetType('')
                      }} />
                    {label}
                  </label>
                )
              })}
            </div>
          )}
          <div className="trade-modal-section">
            <div className="trade-modal-section-title">주는 자원 {ratio}장</div>
            {RESOURCE_TYPES.map(rt => {
              const have = myResources[rt] ?? 0
              const lbl  = RESOURCE_LABEL[rt]
              // 2:1 항구는 자원 종류 고정
              const fixedDisabled = !isGeneric && rt !== port.resourceType
              const lackDisabled  = have < ratio
              const disabled = fixedDisabled || lackDisabled
              return (
                <label key={rt} className={`trade-radio-row${disabled ? ' disabled' : ''}`}>
                  <input type="radio" name="port_give" checked={portGiveType === rt}
                    disabled={disabled}
                    onChange={() => setPortGiveType(rt)} />
                  <img src={lbl.img} alt={lbl.label} className="trade-res-icon" />
                  {lbl.label} ({have}{lackDisabled && ` — ${ratio}장 미만`}{fixedDisabled && ` — 2:1 ${RESOURCE_LABEL[port.resourceType!]?.label} 고정`})
                </label>
              )
            })}
          </div>
          <div className="trade-modal-section">
            <div className="trade-modal-section-title">받는 자원 1장</div>
            {RESOURCE_TYPES.map(rt => {
              const lbl = RESOURCE_LABEL[rt]
              const disabled = rt === portGiveType
              return (
                <label key={rt} className={`trade-radio-row${disabled ? ' disabled' : ''}`}>
                  <input type="radio" name="port_get" checked={portGetType === rt}
                    disabled={disabled}
                    onChange={() => setPortGetType(rt)} />
                  <img src={lbl.img} alt={lbl.label} className="trade-res-icon" />
                  {lbl.label}{disabled && ' — 같은 자원 불가'}
                </label>
              )
            })}
          </div>
          <div className="trade-modal-actions">
            <button type="button" className="btn-trade-submit" onClick={handleSubmitPort}
              disabled={!portGiveType || !portGetType}>거래 실행</button>
            <button type="button" className="btn-trade-close" onClick={() => setPortModalPortId(null)}>취소</button>
          </div>
        </div>
      </div>
    )
  })() : null

  // ── 발전 카드 사용 모달 (KNIGHT / ROAD_BUILDING / MONOPOLY / YEAR_OF_PLENTY / VICTORY_POINT) ──
  // 2026-05-06 v6: KNIGHT pick_hex / ROAD_BUILDING / KNIGHT pick_victim(0~1명 자동 처리)는 모달 노출 X.
  //   보드 헥스/edge 클릭이 필요한 단계는 인라인 모드(boardMode 상태) + 액션 컬럼 배너로 안내.
  //   pick_victim 2명+ / MONOPOLY / YEAR_OF_PLENTY 만 모달 표시 (보드 클릭 불요).
  const isInlineBoardMode = useCardModal && (
    (useCardModal.cardType === 'KNIGHT' && knightStep === 'pick_hex') ||
    useCardModal.cardType === 'ROAD_BUILDING'
  )
  const showUseCardModal = useCardModal && !isInlineBoardMode && (() => {
    if (useCardModal.cardType !== 'KNIGHT' || knightStep !== 'pick_victim' || knightHexId == null) return true
    // pick_victim 단계: victim 2명+이면 모달, 0~1명은 useEffect가 자동 처리(모달 미표시)
    const hexVids = new Set<number>(getHexVertices(knightHexId))
    const cands = vertices
      .filter(v => hexVids.has(v.vertexId))
      .filter(v => v.ownerUserCode != null && v.ownerUserCode !== authState.userCode)
      .map(v => v.ownerUserCode!) as number[]
    const victims = Array.from(new Set(cands)).filter(uc => (resCounts[uc] ?? 0) > 0)
    return victims.length >= 2
  })()
  const useCardModalEl = showUseCardModal ? (
    <div className="play-modal-overlay" style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div className="play-modal" style={{ background: '#fff', padding: 24, borderRadius: 8, minWidth: 320, maxWidth: '92vw' }}>
        <h3 style={{ margin: 0, marginBottom: 12 }}>
          {DEV_CARD_LABEL[useCardModal.cardType]} 카드 사용
        </h3>

        {/* KNIGHT */}
        {useCardModal.cardType === 'KNIGHT' && (
          <div>
            {knightStep === 'pick_hex' ? (
              <>
                <p style={{ fontSize: 14, color: '#555' }}>
                  도적을 이동시킬 헥스를 클릭하세요. (현재 위치 외)
                </p>
                {knightHexId != null && (
                  <p style={{ fontSize: 13 }}>선택된 hex: <strong>#{knightHexId}</strong></p>
                )}
              </>
            ) : (
              <>
                <p style={{ fontSize: 14, color: '#555' }}>
                  hex #{knightHexId} 인근 마을/도시(성) 보유자에게서 자원 1장을 약탈합니다.
                </p>
                {(() => {
                  // 도둑 이동 hex의 6 vertex 점유자 중 본인 제외 + 자원 ≥1 보유자만 (2026-05-06 fix)
                  // 카탄 표준 룰: "도둑이 위치한 타일 인접 플레이어"
                  const hexVids = new Set<number>(knightHexId != null ? getHexVertices(knightHexId) : [])
                  const cands = vertices
                    .filter(v => hexVids.has(v.vertexId))
                    .filter(v => v.ownerUserCode != null && v.ownerUserCode !== authState.userCode)
                    .map(v => v.ownerUserCode!) as number[]
                  const victims = Array.from(new Set(cands)).filter(uc => (resCounts[uc] ?? 0) > 0)
                  if (victims.length === 0) {
                    return (
                      <p style={{ fontSize: 13, color: '#888' }}>약탈 대상 없음 — 도적만 이동합니다.</p>
                    )
                  }
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {victims.map(uc => {
                        const p = players.find(pl => pl.userCode === uc)
                        return (
                          <button
                            key={uc}
                            type="button"
                            onClick={() => confirmUseKnight(uc)}
                            style={{ padding: 8, textAlign: 'left' }}
                          >
                            {p?.nickname ?? `#${uc}`} ({resCounts[uc] ?? 0}장)
                          </button>
                        )
                      })}
                      <button
                        type="button"
                        onClick={() => confirmUseKnight(null)}
                        style={{ padding: 8, color: '#888' }}
                      >
                        약탈 안 함 (도적만 이동)
                      </button>
                    </div>
                  )
                })()}
              </>
            )}
          </div>
        )}

        {/* ROAD_BUILDING */}
        {useCardModal.cardType === 'ROAD_BUILDING' && (
          <div>
            <p style={{ fontSize: 14, color: '#555' }}>
              본인 도로/마을에 인접한 edge 2개를 클릭하세요. (자원 차감 없음)
            </p>
            <p style={{ fontSize: 13 }}>
              선택: {roadBuildEdges.length === 0 ? '없음' : roadBuildEdges.map(e => `#${e}`).join(' → ')}
              {' '}({roadBuildEdges.length}/2)
            </p>
          </div>
        )}

        {/* MONOPOLY */}
        {useCardModal.cardType === 'MONOPOLY' && (
          <div>
            <p style={{ fontSize: 14, color: '#555' }}>
              한 자원 종류를 선택하면 다른 모든 플레이어로부터 그 자원을 모두 가져옵니다.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {RESOURCE_TYPES.map(rt => {
                const { label, img } = RESOURCE_LABEL[rt]
                return (
                  <label
                    key={rt}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: 6,
                      background: monopolyResource === rt ? '#e3f2fd' : '#fff',
                      border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      name="monopoly_res"
                      checked={monopolyResource === rt}
                      onChange={() => setMonopolyResource(rt)}
                    />
                    <img src={img} alt={label} style={{ width: 22, height: 22 }} />
                    <span>{label}</span>
                  </label>
                )
              })}
            </div>
          </div>
        )}

        {/* YEAR_OF_PLENTY */}
        {useCardModal.cardType === 'YEAR_OF_PLENTY' && (
          <div>
            <p style={{ fontSize: 14, color: '#555' }}>
              은행에서 자원 2장을 자유 선택합니다. (같은 자원 2장도 가능)
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {RESOURCE_TYPES.map(rt => {
                const { label, img } = RESOURCE_LABEL[rt]
                const cnt = yopResources.filter(r => r === rt).length
                return (
                  <div key={rt} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 100, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <img src={img} alt={label} style={{ width: 22, height: 22 }} />
                      {label}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const idx = yopResources.indexOf(rt)
                        if (idx >= 0) {
                          setYopResources(yopResources.filter((_, i) => i !== idx))
                        }
                      }}
                      disabled={cnt <= 0}
                    >−</button>
                    <span style={{ width: 24, textAlign: 'center' }}>{cnt}</span>
                    <button
                      type="button"
                      onClick={() => {
                        if (yopResources.length < 2) setYopResources([...yopResources, rt])
                      }}
                      disabled={yopResources.length >= 2}
                    >+</button>
                  </div>
                )
              })}
            </div>
            <p style={{ fontSize: 12, marginTop: 6 }}>합계: {yopResources.length}/2</p>
          </div>
        )}

        {/* VICTORY_POINT */}
        {useCardModal.cardType === 'VICTORY_POINT' && (
          <div>
            <p style={{ fontSize: 14, color: '#555' }}>
              승점 카드 즉시 공개합니다. (점수는 구매 시 이미 +1 반영, 본 액션은 다른 플레이어에게 공개)
            </p>
          </div>
        )}

        {/* 공통: 확정/취소 버튼 (KNIGHT는 victim 선택으로 별도 처리) */}
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          {useCardModal.cardType !== 'KNIGHT' && useCardModal.cardType !== 'ROAD_BUILDING' && (
            <button
              type="button"
              onClick={confirmUseCard}
              disabled={
                (useCardModal.cardType === 'MONOPOLY' && !monopolyResource) ||
                (useCardModal.cardType === 'YEAR_OF_PLENTY' && yopResources.length !== 2)
              }
              style={{ padding: '8px 16px', fontWeight: 600 }}
            >
              사용 확정
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setUseCardModal(null)
              setKnightStep('pick_hex')
              setKnightHexId(null)
              setRoadBuildEdges([])
              setMonopolyResource('')
              setYopResources([])
            }}
            style={{ padding: '8px 16px', color: '#666' }}
          >
            취소
          </button>
        </div>
      </div>
    </div>
  ) : null

  // ── 자원 패널 (데스크톱: 채팅 별도 컬럼이라 자원만 / 모바일: 자원 탭) ─────
  // ── 자원 헤더 (총개수 + 폐기 경고) ──────────────────────────
  const resourceHeader = (() => {
    const totalResources = Object.values(myResources).reduce((s, v) => s + (v ?? 0), 0)
    return (
      <h4 className="panel-title">
        내 자원
        <span className="play-resource-total"> (총 {totalResources}장)</span>
        {totalResources >= 8 && (
          <span className="discard-warning"> ⚠️ 7 발생 시 폐기 위험</span>
        )}
      </h4>
    )
  })()

  // ── 자원 3×2 그리드 (데스크톱·모바일 공용) — 2026-05-07
  // 자원이미지 1개 + ×N 숫자만, 카드 반복 이미지 제거
  // 기본판 5종 + 빈 셀 1개 = 3열 2행
  // SEQ=10112 P8-CK-A — EXT_CK ON 시 8종(자원5 + 상품3) 렌더, 3×3 그리드 + 빈 셀 1
  const resourceGrid3x2 = (
    <div className="play-resource-grid3x2">
      {RESOURCE_TYPES.map(rt => {
        const { label, img } = RESOURCE_LABEL[rt]
        const cnt = myResources[rt] ?? 0
        return (
          <div key={rt} className={`play-res-cell${cnt > 0 ? ' has-resource' : ''}`}>
            <img className="play-res-cell-img" src={img} alt={label} />
            <span className="play-res-cell-count">×{cnt}</span>
          </div>
        )
      })}
      {game?.isExtCk
        ? COMMODITY_TYPES.map(rt => {
            const { label, emoji } = RESOURCE_LABEL[rt]
            const cnt = myResources[rt] ?? 0
            return (
              <div key={rt} className={`play-res-cell${cnt > 0 ? ' has-resource' : ''}`} title={label}>
                <span className="play-res-cell-img" style={{ fontSize: '20px' }}>{emoji}</span>
                <span className="play-res-cell-count">×{cnt}</span>
              </div>
            )
          })
        : (<div className="play-res-cell play-res-cell-empty" />)
      }
    </div>
  )

  // 데스크톱 3번 컬럼 / 모바일 resource 탭 모두 동일 컴포넌트 사용
  const resourceRowsDesktop = resourceGrid3x2
  const resourceGridCompact = resourceGrid3x2

  // ── 잠시 나가기 / 게임 포기 ─────────────────────────────────
  // SEQ=10072 STEP B — 관전자는 "관전 종료"(로비로) 단일 버튼만. 플레이어는 기존 2버튼.
  const leaveActions = isObserver ? (
    <div className="play-leave-actions" style={{ marginTop: 'auto', paddingTop: 16 }}>
      <button
        type="button"
        className="btn-leave-forfeit"
        onClick={handleForfeitGame}
        title="관전을 종료하고 로비로 나갑니다"
      >
        관전 종료
      </button>
    </div>
  ) : (
    <div className="play-leave-actions" style={{ marginTop: 'auto', paddingTop: 16 }}>
      <button
        type="button"
        className="btn-leave-temp"
        onClick={handleLeaveTemp}
        title="자리 보존 — 새로고침 시 자동 재진입"
      >
        잠시 나가기
      </button>
      <button
        type="button"
        className="btn-leave-forfeit"
        onClick={handleForfeitGame}
        title="자리 영구 정리 — 게임에서 영구 퇴장 (다른 플레이어는 빈 자리로 계속)"
      >
        게임 포기
      </button>
    </div>
  )

  // ── SEQ=10124 P8-CK-H — 진보카드 9 UX 모달/오버레이 element ──────
  const progressModalEl = (() => {
    if (!progressMode) return null
    const myUc = authState.userCode
    const closeModal = () => { setProgressMode(null); setActionMsg('') }
    const mid = game?.matchId ?? matchId ?? 0
    if (!mid) return null

    if (progressMode.kind === 'POL_BISHOP_PICK_HEX') {
      return (
        <div style={{ position: 'fixed', top: 12, right: 12, zIndex: 950, background: '#fff', border: '1px solid #888', borderRadius: 6, padding: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>👑 주교 (Bishop)</div>
          <div style={{ fontSize: 12 }}>도적을 이동시킬 hex를 클릭하세요.</div>
          <button onClick={closeModal} style={{ marginTop: 6, padding: '4px 10px' }}>취소</button>
        </div>
      )
    }
    if (progressMode.kind === 'POL_BISHOP_CONFIRM') {
      return (
        <BishopVictimModal
          hexId={progressMode.hexId}
          victims={progressMode.victims}
          players={players}
          onConfirm={() => commitProgressInvoke(progressMode.cardId, {
            target_hex_id: progressMode.hexId,
            victims:       progressMode.victims,
          })}
          onCancel={closeModal}
          busy={progressBusy}
        />
      )
    }
    if (progressMode.kind === 'POL_DESERTER') {
      const pk = progressMode.pickedKnightId != null
        ? knights.find(k => k.knightId === progressMode.pickedKnightId)
        : null
      return (
        <DeserterPlacementOverlay
          step={progressMode.step}
          pickedKnight={pk}
          candidateVertexIds={progressMode.candidateVertexIds}
          players={players}
          onCancel={closeModal}
        />
      )
    }
    if (progressMode.kind === 'POL_DIPLOMAT') {
      return (
        <DiplomatRoadOverlay
          candidateEdgeIds={progressMode.candidateEdgeIds}
          onCancel={closeModal}
        />
      )
    }
    if (progressMode.kind === 'POL_INTRIGUE') {
      const pk = progressMode.pickedKnightId != null
        ? knights.find(k => k.knightId === progressMode.pickedKnightId)
        : null
      return (
        <IntrigueMoveOverlay
          step={progressMode.step}
          pickedKnight={pk}
          candidateVertexIds={progressMode.candidateVertexIds}
          players={players}
          onCancel={closeModal}
        />
      )
    }
    if (progressMode.kind === 'POL_SPY') {
      const candidates = players.filter(p => p.userCode !== myUc)
      return (
        <SpyHandModal
          step={progressMode.step}
          candidatePlayers={candidates}
          targetUserCode={progressMode.targetUserCode}
          targetHand={progressMode.targetHand}
          onPickPlayer={(uc) => {
            // SEQ=10125 P8-CK-H2 (c): SpyGetHand second-stage RPC 호출
            // 상대 핸드 결과는 SpyHandRevealed 이벤트로 caller 전용 수신 →
            // 핸들러가 progressMode.step=pick_card 로 전이 + targetHand 주입.
            const mid = game?.matchId ?? matchId ?? 0
            if (!mid) return
            setProgressMode({ ...progressMode, targetUserCode: uc, step: 'pick_player' })
            void (async () => {
              try {
                await catanHub.spyGetHand(mid, uc)
                // 결과는 onSpyHandRevealed 핸들러가 step=pick_card 로 전이
              } catch {
                setErrorBanner('상대 핸드 조회 오류 (Spy)')
                setTimeout(() => setErrorBanner(''), 3000)
              }
            })()
          }}
          onPickCard={(cardId) => {
            void commitProgressInvoke(progressMode.cardId, {
              target: progressMode.targetUserCode, target_card_id: cardId,
            })
          }}
          onCancel={closeModal}
          busy={progressBusy}
        />
      )
    }
    if (progressMode.kind === 'SCI_INVENTOR') {
      const hexA = progressMode.hexA != null ? hexes.find(h => h.hexId === progressMode.hexA) : null
      const hexB = progressMode.hexB != null ? hexes.find(h => h.hexId === progressMode.hexB) : null
      return (
        <InventorSwapModal
          hexes={hexes}
          step={progressMode.step}
          pickedHexA={hexA}
          pickedHexB={hexB}
          onPickHex={() => { /* 보드 클릭으로 처리 */ }}
          onConfirm={() => {
            if (progressMode.hexA != null && progressMode.hexB != null) {
              void commitProgressInvoke(progressMode.cardId, {
                hex_a: progressMode.hexA, hex_b: progressMode.hexB,
              })
            }
          }}
          onCancel={closeModal}
          busy={progressBusy}
        />
      )
    }
    if (progressMode.kind === 'SCI_PIONEERING') {
      return (
        <PioneeringRoadOverlay
          candidateEdgeIds={progressMode.candidateEdgeIds}
          onCancel={closeModal}
        />
      )
    }
    if (progressMode.kind === 'TRD_COMMERCIAL_HARBOR') {
      const targets = players.filter(p => p.userCode !== myUc)
      return (
        <CommercialHarborTradeModal
          targets={targets}
          myCommodity={progressMode.myCommodity}
          theirResource={progressMode.theirResource}
          onChangeMyCommodity={(r) => setProgressMode({ ...progressMode, myCommodity: r })}
          onChangeTheirResource={(r) => setProgressMode({ ...progressMode, theirResource: r })}
          onConfirm={() => {
            void commitProgressInvoke(progressMode.cardId, {
              my_commodity:    progressMode.myCommodity,
              their_resource:  progressMode.theirResource,
            })
          }}
          onCancel={closeModal}
          busy={progressBusy}
        />
      )
    }
    if (progressMode.kind === 'TRD_MERCHANT') {
      return (
        <MerchantPlacementOverlay
          candidateHexIds={progressMode.candidateHexIds}
          onCancel={closeModal}
        />
      )
    }
    return null
  })()

  // ── 도적 위치 배지 (자원 패널 안 — 내 자원과 발전카드 사이, 2026-05-07 위치 이동) ──
  const robberBadge = (() => {
    const robberHex = hexes.find(h => h.hasRobber)
    if (!robberHex) return null
    const isDesert = robberHex.numberToken == null || robberHex.resourceType === 'DESERT'
    const text = isDesert
      ? '🦹 도적: 사막'
      : `🦹 도적: ${robberHex.numberToken} (${RESOURCE_LABEL[robberHex.resourceType ?? '']?.label ?? robberHex.resourceType ?? '?'})`
    return (
      <span className={`play-header-robber${robberPulse ? ' pulse' : ''}${isDesert ? ' desert' : ''}`}>
        {text}
      </span>
    )
  })()

  // ── 모바일 통합 패널 (resource 탭 = 자원 + 발전카드 + 액션 + 승점 + 나가기) ──
  const resourcePanel = (
    <div className="play-resource-panel">
      {resourceHeader}
      {dicePanel}
      {resourceGridCompact}
      {actionMsg && (
        <div className="play-action-msg">{actionMsg}</div>
      )}
      {robberBadge}
      {devCardPanel}
      {scorePanel}
      {leaveActions}
    </div>
  )

  // ── 데스크톱 3번 컬럼: 자원(5행 가로 펼침) + 발전카드 ────────
  const resourceColumn = (
    <div className="play-resource-panel play-resource-column">
      {resourceHeader}
      {resourceRowsDesktop}
      {robberBadge}
      {devCardPanel}
    </div>
  )

  // ── 발전카드 인라인 배너 (KNIGHT pick_hex / ROAD_BUILDING) — 보드 클릭 모드 안내
  const cardInlineBanner = isInlineBoardMode ? (
    <div className="play-card-inline-banner">
      {useCardModal?.cardType === 'KNIGHT' && (
        <>
          <strong>🦹 기사 카드 사용 중</strong>
          <span>도적을 이동시킬 헥스를 보드에서 클릭하세요</span>
        </>
      )}
      {useCardModal?.cardType === 'ROAD_BUILDING' && (
        <>
          <strong>🛣️ 도로 건설 카드 사용 중</strong>
          <span>본인 도로/마을에 인접한 edge 2개를 클릭하세요 ({roadBuildEdges.length}/2)</span>
        </>
      )}
    </div>
  ) : null

  // ── 데스크톱 4번 컬럼: 주사위 + 액션 메시지 + 나가기 (승점은 1번 컬럼으로 이동, v7) ──
  const actionColumn = (
    <div className="play-resource-panel play-action-column">
      {dicePanel}
      {cardInlineBanner}
      {actionMsg && (
        <div className="play-action-msg">{actionMsg}</div>
      )}
      {leaveActions}
    </div>
  )

  // ── 채팅 패널 ─────────────────────────────────────────────
  const chatPanelEl = (
    <ChatPanel
      chatLog={chatLog}
      roomCode={roomCode}
      onSend={handleSendChat}
      onClear={() => roomStore.clearChatLog()}
      tabActive={isMobile ? activeTab === 'chat' : true}
    />
  )

  // ── 현재 플레이어 color 조회 ──────────────────────────
  const myPlayer = players.find(p => p.userCode === myUserCode)
  const myColor  = myPlayer?.color ?? ''

  // ── 보드 영역 ──────────────────────────────────────────
  const boardArea = (
    <div className="play-board-area">
      {loading && (
        <div className="play-board-loading">
          <span className="spinner" /> 보드 로딩 중…
        </div>
      )}
      {errMsg && (
        <div className="room-err-banner">{errMsg}</div>
      )}
      {!loading && hexes.length > 0 && (
        <CatanBoard
          hexes={hexes}
          ports={ports}
          vertices={vertices}
          edges={edges}
          players={players}
          knights={knights}
          wallVertexIds={cityWalls.map(w => w.vertexId)}
          stageW={stageSize.w}
          stageH={stageSize.h}
          hexSize={stageSize.hexSize}
          myUserCode={myUserCode}
          myColor={myColor}
          currentPhase={game?.currentPhase ?? null}
          isMyTurn={isMyTurn}
          lastSettlementVertexId={lastSettlementVertexId}
          buildMode={buildMode}
          devCardMode={
            useCardModal?.cardType === 'KNIGHT' && knightStep === 'pick_hex'
              ? 'KNIGHT_PICK_HEX'
              : useCardModal?.cardType === 'ROAD_BUILDING'
                ? 'ROAD_BUILDING_PICK_EDGE'
                : null
          }
          roadBuildSelectedEdges={
            useCardModal?.cardType === 'ROAD_BUILDING' ? roadBuildEdges : undefined
          }
          onVertexClick={handleVertexClick}
          onEdgeClick={handleEdgeClick}
          onHexClick={handleHexClick}
          onPortClick={openPortModal}
          // SEQ=10125 P8-CK-H2 (a) — 진보카드 9 UX 기사 클릭 통합
          knightClickMode={
            progressMode?.kind === 'POL_DESERTER' && progressMode.step === 'pick_knight' ? 'DESERTER' :
            progressMode?.kind === 'POL_INTRIGUE' && progressMode.step === 'pick_knight' ? 'INTRIGUE' :
            null
          }
          onKnightClick={handleKnightClick}
        />
      )}
      {!loading && hexes.length === 0 && !errMsg && (
        <div className="play-board-empty">보드 데이터 없음</div>
      )}
    </div>
  )

  // ── 렌더 ───────────────────────────────────────────────
  return (
    <div className="app-shell">
      {discardModal}
      {proposeModalEl}
      {respondModalEl}
      {bankModalEl}
      {portModalEl}
      {victimModal}
      {useCardModalEl}
      {gameEndedModalEl}
      {progressModalEl}
      {/* Track O 2026-05-07 — 발전카드 hover 확대 overlay (pointer-events: none, hover 비차단) */}
      {hoveredDevCard && (
        <div className="play-devcard-zoom-overlay">
          <div className="play-devcard-zoom-card">
            <img src={devCardImg(hoveredDevCard.cardType, hoveredDevCard.cardId)} alt={DEV_CARD_LABEL[hoveredDevCard.cardType]} />
            <div className="play-devcard-zoom-label">{DEV_CARD_LABEL[hoveredDevCard.cardType]}</div>
          </div>
        </div>
      )}
      <NavBar
        auth={authState}
        onLogout={handleLogout}
        onOpenSettings={() => setShowSettings(true)}
        onOpenMyInfo={undefined}
        inGame
      />

      {/* ActionError 배너 */}
      {errorBanner && (
        <div className="play-error-banner">{errorBanner}</div>
      )}

      {/* Track C-1 본인/상대 턴 진입 슬라이드 배너 (D-1 헤더 인접 슬라이드 다운) */}
      {showTurnBanner && (
        <div className={`play-turn-banner play-turn-banner-${turnBannerVariant ?? 'my-turn'}`} role="status" aria-live="polite">
          {turnBannerText}
        </div>
      )}

      {/* 게임 정보 헤더 + 턴 안내 */}
      <div className="play-header">
        <span className="play-header-room">룸: <strong>{roomCode}</strong></span>
        {/* SEQ=10072 STEP B — 관전 중 배지 */}
        {isObserver && (
          <span className="play-header-observer">👁 관전 중</span>
        )}
        {game && (
          <span className="play-header-status">
            {statusLabel(game.status)}
          </span>
        )}
        {game && (
          <span className={`play-turn-guide${isMyTurn ? ' my-turn' : ''}`}>
            {isObserver ? '관전자는 게임에 참여하지 않습니다' : turnGuideText()}
          </span>
        )}
        {/* 도적 위치 배지: 자원 패널 안으로 이동 (2026-05-07) */}
        {game?.matchId && (
          <span className="play-header-match muted">#{game.matchId}</span>
        )}
      </div>

      {/* 모바일 탭바 (4탭: 보드 / 플레이어 / 자원·액션 / 채팅) */}
      {isMobile && (
        <div className="room-tab-bar">
          <button
            className={`room-tab-btn${activeTab === 'board' ? ' active' : ''}`}
            onClick={() => setActiveTab('board')}
          >보드</button>
          <button
            className={`room-tab-btn${activeTab === 'players' ? ' active' : ''}`}
            onClick={() => setActiveTab('players')}
          >플레이어</button>
          <button
            className={`room-tab-btn${activeTab === 'resource' ? ' active' : ''}`}
            onClick={() => setActiveTab('resource')}
          >자원·액션</button>
          <button
            className={`room-tab-btn${activeTab === 'chat' ? ' active' : ''}`}
            onClick={() => setActiveTab('chat')}
          >
            채팅
            {unreadChat > 0 && (
              <span className="chat-unread-badge">{unreadChat > 99 ? '99+' : unreadChat}</span>
            )}
          </button>
        </div>
      )}

      {/* 레이아웃 */}
      {isMobile ? (
        <div className="play-layout-mobile">
          {activeTab === 'board'    && <div className="play-tab-pane">{boardArea}</div>}
          {activeTab === 'players'  && <div className="play-tab-pane">{playerPanel}</div>}
          {activeTab === 'resource' && <div className="play-tab-pane">{resourcePanel}</div>}
          {activeTab === 'chat'     && <div className="play-tab-pane">{chatPanelEl}</div>}
        </div>
      ) : (
        <div className="play-layout-desktop">
          <div className="play-side-column">
            {playerPanel}
            {/* 2026-05-06 v7: 승점을 4번 컬럼 → 1번 컬럼 (플레이어 ▽ 승점 ▽ 채팅) */}
            <div className="play-side-score">{scorePanel}</div>
            <div className="play-side-chat">{chatPanelEl}</div>
          </div>
          {boardArea}
          {resourceColumn}
          {actionColumn}
        </div>
      )}

      {/* SEQ=10110 STEP=4 — 게임 중에는 [⚙ 설정]만 노출. [👤 내정보] 비활성. */}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
    </div>
  )
}

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    SETUP_FORWARD:  '초기 배치 중',
    SETUP_BACKWARD: '초기 배치 중 (역순)',
    MAIN_TURN:      '게임 진행 중',
    GAME_END:       '게임 종료',
  }
  return map[status] ?? status
}

function diceFace(n: number): string {
  const faces = ['', '⚀', '⚁', '⚂', '⚃', '⚄', '⚅']
  return faces[n] ?? String(n)
}
